// src/lib/generators.js
// ─── SECTION 1: Helpers + normalizeText + shared constants ───────────────────

// ─── UTILITY ─────────────────────────────────────────────────────────────────

export function safeList(value) { return Array.isArray(value) ? value : []; }

// Canonical Total Signals formula — sums per-source pill counts.
// Single source of truth. If you add a source pill to the Source Breakdown,
// add the same field here. Used at all "Total Signals" render surfaces.
export function computeTotalSignals(cov) {
  if (!cov) return 0;
  return (
    (cov.website_signals_extracted || 0) +
    (cov.amazon_count || 0) +
    (cov.youtube_count || cov.social_comments_analyzed || 0) +
    (cov.trustpilot_count || 0) +
    (cov.reddit_count || 0) +
    (cov.walmart_count || 0) +
    (cov.google_queries_analyzed || 0)
  );
}

// ─── ACTIVITY PHRASE FILTER ───────────────────────────────────────────────────

export const ACTIVITY_PHRASES = ["known activity override", "ctv", "long-form", "drtv", "youtube", "podcast"];

// ─── SPLIT WORD REPAIR DATA ───────────────────────────────────────────────────

export const MERGE_WORD_STARTERS = [
  "great","good","bad","well","now","here","there","work","works","working",
  "stop","start","started","came","gone","went","gave","made","said","told",
  "very","real","really","just","from","back","been","that","this","with",
  "when","then","than","them","they","have","need","like","know","love",
  "hate","feel","feels","felt","take","took","give","gets","even","over",
  "after","before","never","always","still","only","every","again","today",
  "yesterday","tomorrow","perfect","better","worse","finally","honestly"
];

export const MERGE_SPLIT_RE = new RegExp(
  `([a-z])(${MERGE_WORD_STARTERS.join("|")})(?=[a-z])`,
  "g"
);

export const SPLIT_WORD_REPAIRS = [
  // ── Priority 1 — observed in latest runs ──────────────────────────────────
  [/\bot her\b/gi, "other"],
  [/\bot hers\b/gi, "others"],
  [/\bot herwise\b/gi, "otherwise"],
  [/\bs old\b/gi, "sold"],
  [/\bm outh\b/gi, "mouth"],
  [/\bAl though\b/g, "Although"],
  [/\bal though\b/gi, "although"],
  [/\be verything\b/gi, "everything"],
  [/\be veryone\b/gi, "everyone"],
  [/\bric her\b/gi, "richer"],
  [/\bric hest\b/gi, "richest"],
  [/\bpurc hasing\b/gi, "purchasing"],
  [/\bpurc hased\b/gi, "purchased"],
  [/\bpurc hase\b/gi, "purchase"],
  [/\bpurc hases\b/gi, "purchases"],
  [/\bw here's\b/gi, "where's"],
  [/\bw here\b/gi, "where"],
  [/\bw hether\b/gi, "whether"],
  [/\by our\b/g, "your"],
  [/\bY our\b/g, "Your"],
  [/\bau thenticity\b/gi, "authenticity"],
  [/\bad justabilit(y|ies)\b/gi, (_, s) => `adjustabilit${s}`],
  [/\bad justab/gi, "adjustab"],
  [/\benc ouraging\b/gi, "encouraging"],
  [/\brein forcement\b/gi, "reinforcement"],
  [/\bconf uses\b/gi, "confuses"],
  [/\bstreng thening\b/gi, "strengthening"],
  [/\bstreng then(s|ed|ing)?\b/gi, (_, s = "") => `strengthen${s}`],
  [/\bunderemp hasiz(es|ing|ed|e)\b/gi, (_, s) => `underemphasiz${s}`],
  [/\bunderemp hasis\b/gi, "underemphasis"],
  // ── April 2 run ──────────────────────────────────────────────────────────
  [/\boveremphasiz(es|ing|ed|e)\b/gi, (_, s) => `overemphasiz${s}`],
  [/\boveremp hasiz(es|ing|ed|e)\b/gi, (_, s) => `overemphasiz${s}`],
  [/\bac knowledged\b/gi, "acknowledged"],
  [/\bac knowledges\b/gi, "acknowledges"],
  [/\bac knowledging\b/gi, "acknowledging"],
  [/\bac knowledgment(s)?\b/gi, (_, s = "") => `acknowledgment${s}`],
  [/\bk nowing\b/gi, "knowing"],
  [/\bk nown\b/gi, "known"],
  [/\bk nowledge\b/gi, "knowledge"],
  [/\bk now\b/gi, "know"],
  [/\bk nows\b/gi, "knows"],
  [/\bunderst anding\b/gi, "understanding"],
  [/\bunderst and\b/gi, "understand"],
  [/\bunderst ands\b/gi, "understands"],
  [/\bca using\b/gi, "causing"],
  [/\bca used\b/gi, "caused"],
  [/\bpro longed\b/gi, "prolonged"],
  [/\bpro long(s|ed|ing)?\b/gi, (_, s = "") => `prolong${s}`],
  [/\bgr andchildren\b/gi, "grandchildren"],
  [/\bgr andchild\b/gi, "grandchild"],
  [/\bgr andparent(s)?\b/gi, (_, s = "") => `grandparent${s}`],
  [/\bs ources\b/gi, "sources"],
  [/\bs ource\b/gi, "source"],
  [/\bre newed\b/gi, "renewed"],
  [/\bre new(s|ing|al|als)?\b/gi, (_, s = "") => `renew${s}`],
  [/\bst andards\b/gi, "standards"],
  [/\bst andard\b/gi, "standard"],
  [/\bst anding\b/gi, "standing"],
  // ── April 1 run ──────────────────────────────────────────────────────────
  [/\bef fort less(ly|ness)?\b/gi, (_, s = "") => `effortless${s}`],
  [/\bef fort\b/gi, "effort"],
  [/\bfo under\b/gi, "founder"],
  [/\bfo unders\b/gi, "founders"],
  [/\btrans formative\b/gi, "transformative"],
  [/\btrans form\b/gi, "transform"],
  [/\bc overs\b/gi, "covers"],
  [/\bc overed\b/gi, "covered"],
  [/\bc overing\b/gi, "covering"],
  [/\bh ours\b/gi, "hours"],
  [/\bh our\b/gi, "hour"],
  [/\bWorksgreat\b/gi, "Works great"],
  [/\brein forc(es|ing|ed|e)\b/gi, (_, s) => `reinforc${s}`],
  [/\brein force\b/gi, "reinforce"],
  [/\bRein forc(es|ing|ed|e)\b/g, (_, s) => `Reinforc${s}`],
  [/\bRein force\b/g, "Reinforce"],
  [/\bDimini sh(es|ing|ed)?\b/g, (_, s = "") => `Diminish${s}`],
  [/\bdimini sh(es|ing|ed)?\b/gi, (_, s = "") => `diminish${s}`],
  [/\bconf using\b/gi, "confusing"],
  [/\bconf used\b/gi, "confused"],
  [/\bconf use\b/gi, "confuse"],
  [/\bt here's\b/gi, "there's"],
  [/\bt here\b/gi, "there"],
  [/\bbeca use\b/gi, "because"],
  [/\bbeca me\b/gi, "became"],
  [/\bh ol low(er|est|ness|ing|ed)?\b/gi, (_, s = "") => `hollow${s}`],
  [/\bh ol low\b/gi, "hollow"],
  // ── April 1 second run ───────────────────────────────────────────────────
  [/\bdeli ver(y|ies)\b/gi, (_, s) => `deliver${s}`],
  [/\bdeli ver(s|ed|ing)?\b/gi, (_, s = "") => `deliver${s}`],
  [/\bDeli ver(y|ies|s|ed|ing)?\b/g, (_, s = "") => `Deliver${s}`],
  [/\benc ourage(s|d|ment)?\b/gi, (_, s = "") => `encourage${s}`],
  [/\bEnc ourage(s|d|ment)?\b/g, (_, s = "") => `Encourage${s}`],
  [/\bdem and(s|ed|ing)?\b/gi, (_, s = "") => `demand${s}`],
  [/\bDem and(s|ed|ing)?\b/g, (_, s = "") => `Demand${s}`],
  [/\bre putation\b/gi, "reputation"],
  [/\bRe putation\b/g, "Reputation"],
  [/\bh andling\b/gi, "handling"],
  [/\bh andled\b/gi, "handled"],
  [/\bh andle(s|d|r)?\b/gi, (_, s = "") => `handle${s}`],
  [/\bH andle(s|d|r)?\b/g, (_, s = "") => `Handle${s}`],
  [/\bre ad just(s|ed|ing|ment|ments)?\b/gi, (_, s = "") => `readjust${s}`],
  // ── Final pass additions ─────────────────────────────────────────────────
  [/\bem its\b/gi, "emits"],
  [/\bem it\b/gi, "emit"],
  [/\bem itted\b/gi, "emitted"],
  [/\bem itting\b/gi, "emitting"],
  [/\basgood\b/gi, "as good"],
  [/\btet her\b/gi, "tether"],
  [/\btet hered\b/gi, "tethered"],
  [/\btet hering\b/gi, "tethering"],
  [/\bbenef itts\b/gi, "benefits"],
  [/\bbenef itt\b/gi, "benefit"],
  [/\bbenefitts\b/gi, "benefits"],
  [/\bdiscom fort/gi, "discomfort"],
  [/\bwhet her\b/gi, "whether"],
  [/\bun foreseen\b/gi, "unforeseen"],
  [/\bresearc hers\b/gi, "researchers"],
  [/\bresearc her\b/gi, "researcher"],
  [/\bresearc hing\b/gi, "researching"],
  [/\bresearc h\b/gi, "research"],
  [/\bunder performing\b/gi, "underperforming"],
  [/\bunder performance\b/gi, "underperformance"],
  // ── Persistent survivors ─────────────────────────────────────────────────
  [/\bpur chase\b/gi, "purchase"],
  [/\bunder per form/gi, "underperform"],
  [/\bUnderper form/gi, "Underperform"],
  [/\bh old\b/gi, "hold"],
  [/\bh olds\b/gi, "holds"],
  [/\bh olding\b/gi, "holding"],
  [/\bst and\b/gi, "stand"],
  [/\bst ands\b/gi, "stands"],
  [/\bl andscape\b/gi, "landscape"],
  [/\bl anding\b/gi, "landing"],
  [/\bl anded\b/gi, "landed"],
  [/\bfol lowed\b/gi, "followed"],
  [/\bfol lowing\b/gi, "following"],
  [/\bfurt her\b/gi, "further"],
  [/\bfurt hest\b/gi, "furthest"],
  [/\bdistri bution\b/gi, "distribution"],
  [/\bdistri bute(s|d|ing)?\b/gi, (_, s = "") => `distribute${s}`],
  [/\brat her\b/gi, "rather"],
  [/\bdiminish/gi, "diminish"],
  [/\bword-of-m outh\b/gi, "word-of-mouth"],
  [/\bword of m outh\b/gi, "word of mouth"],
  [/\bAd justed\b/g, "Adjusted"],
  [/\bAd justment(s)?\b/g, (_, s = "") => `Adjustment${s}`],
  [/\bAd justable\b/g, "Adjustable"],
  [/\bad justed\b/gi, "adjusted"],
  [/\bad justment(s)?\b/gi, (_, s = "") => `adjustment${s}`],
  [/\bad justable\b/gi, "adjustable"],
  [/\bad justability\b/gi, "adjustability"],
  [/\bad justing\b/gi, "adjusting"],
  [/\bad just\b/gi, "adjust"],
  // ── Earlier observed patterns ─────────────────────────────────────────────
  [/\bemp hasiz(es|ing|ed|e)\b/gi, (_, s) => `emphasiz${s}`],
  [/\bemp hasis\b/gi, "emphasis"],
  [/\baf fordabilit(y|ies)\b/gi, (_, s) => `affordabilit${s}`],
  [/\baf fordable\b/gi, "affordable"],
  [/\baf ford\b/gi, "afford"],
  [/\bw histle\b/gi, "whistle"],
  [/\bw histling\b/gi, "whistling"],
  [/\bw histled\b/gi, "whistled"],
  [/\bca uses\b/gi, "causes"],
  [/\bca used\b/gi, "caused"],
  [/\bca use\b/gi, "cause"],
  [/\bseam less/gi, "seamless"],
  [/\bbe fore\b/gi, "before"],
  [/\bpus hes\b/gi, "pushes"],
  [/\bpu shes\b/gi, "pushes"],
  [/\bovers hadow/gi, "overshadow"],
  [/\bdisc ourage/gi, "discourage"],
  [/\bdisc over/gi, "discover"],
  [/\bin form\b/gi, "inform"],
  [/\bin forming\b/gi, "informing"],
  [/\bin formed\b/gi, "informed"],
  [/\bab out\b/gi, "about"],
  [/\bbr and\b/gi, "brand"],
  [/\bbr ands\b/gi, "brands"],
  [/\bch urn\b/gi, "churn"],
  [/\bt his\b/gi, "this"],
  [/\bj ourney\b/gi, "journey"],
  [/\buncom fortable\b/gi, "uncomfortable"],
  [/\bUncom fortable\b/g, "Uncomfortable"],
  [/\bal most\b/gi, "almost"],
  [/\bfoc us(es|ing|ed)?\b/gi, (m) => "focus" + (m.slice(6) || "")],
  [/\bbenef it(s|ted|ting)?\b/gi, (m) => "benefit" + (m.slice(7) || "")],
  [/\bwire less\b/gi, "wireless"],
  [/\blim it(s|ed|ing|ation|ations)?\b/gi, (m) => "limit" + (m.slice(5) || "")],
  [/\bper forms\b/gi, "performs"],
  [/\bper form\b/gi, "perform"],
  [/\bper forming\b/gi, "performing"],
  [/\bper formed\b/gi, "performed"],
  [/\bf its\b/gi, "fits"],
  [/\bover emp hasis/gi, "overemphasis"],
  [/\bover emp hasiz/gi, "overemphasiz"],
  [/\bne glect/gi, "neglect"],
  [/\bskep ticism\b/gi, "skepticism"],
  [/\bskep tical\b/gi, "skeptical"],
  [/\btranspar ent/gi, "transparent"],
  [/\btranspar ency\b/gi, "transparency"],
  [/\bval idat/gi, "validat"],
  [/\bsignif icant/gi, "significant"],
  [/\bun realistic\b/gi, "unrealistic"],
  [/\bfirs thand\b/gi, "firsthand"],
  [/\bthous ands\b/gi, "thousands"],
  [/\bau thentic/gi, "authentic"],
  [/\bac knowledg/gi, "acknowledg"],
  [/\bcom fortable\b/gi, "comfortable"],
  [/\bre chargeable\b/gi, "rechargeable"],
  [/\bre liable\b/gi, "reliable"],
  [/\bun reliable\b/gi, "unreliable"],
  [/\bun noticed\b/gi, "unnoticed"],
  [/\bon set\b/gi, "onset"],
  [/\bal lowing\b/gi, "allowing"],
  [/\bal lowed\b/gi, "allowed"],
  [/\bal lows\b/gi, "allows"],
  [/\bUn noticed\b/g, "Unnoticed"],
  [/\bim pressive\b/gi, "impressive"],
  [/\bef fective\b/gi, "effective"],
  [/\bin visible\b/gi, "invisible"],
  [/\bac cessible\b/gi, "accessible"],
  [/\bcon nectivity\b/gi, "connectivity"],
  [/\bsat isfaction\b/gi, "satisfaction"],
  [/\bper formance\b/gi, "performance"],
  [/\bex perience\b/gi, "experience"],
  [/\bfeed back\b/gi, "feedback"],
  [/\be very day\b/gi, "everyday"],
  [/\be veryday\b/gi, "everyday"],
  [/\bpr event/gi, "prevent"],
  [/\btar get/gi, "target"],
  [/\bdraw back/gi, "drawback"],
  [/\bin stall/gi, "install"],
  [/\bover all\b/gi, "overall"],
  [/\bin stead\b/gi, "instead"],
  [/\bal ready\b/gi, "already"],
  [/\ban other\b/gi, "another"],
  [/\bany thing\b/gi, "anything"],
  [/\bany where\b/gi, "anywhere"],
  [/\bany one\b/gi, "anyone"],
  [/\bany way\b/gi, "anyway"],
  [/\bsome thing\b/gi, "something"],
  [/\bsome where\b/gi, "somewhere"],
  [/\bsome one\b/gi, "someone"],
  [/\bsome times\b/gi, "sometimes"],
  [/\bever ything\b/gi, "everything"],
  [/\bever yone\b/gi, "everyone"],
  [/\bwith out\b/gi, "without"],
  [/\bwith in\b/gi, "within"],
  [/\bhow ever\b/gi, "however"],
  [/\bwhat ever\b/gi, "whatever"],
  [/\bnone the less\b/gi, "nonetheless"],
  [/\bnever the less\b/gi, "nevertheless"],
  [/\bunder stand/gi, "understand"],
  [/\bover whelm/gi, "overwhelm"],
  [/\bdis appoint/gi, "disappoint"],
  [/\bex cellent\b/gi, "excellent"],
  [/\bex ceptional\b/gi, "exceptional"],
  [/\bex tremely\b/gi, "extremely"],
  [/\bcon venient\b/gi, "convenient"],
  [/\bcon sistent/gi, "consistent"],
  [/\bcom pletely\b/gi, "completely"],
  [/\bcom fort/gi, "comfort"],
  [/\bin credible/gi, "incredible"],
  [/\bper fect/gi, "perfect"],
  [/\bpow erful/gi, "powerful"],
  [/\buse ful/gi, "useful"],
  [/\bhelp ful/gi, "helpful"],
  [/\bworth while\b/gi, "worthwhile"],
  // ── Capitalized mid-sentence artifacts ───────────────────────────────────
  [/(?<![.!?\n] )\bUnderperform(s|ed|ing|ance)?\b/g, (m) => m.toLowerCase()],
];

// ─── NORMALIZE TEXT ───────────────────────────────────────────────────────────

export function normalizeText(str) {
  if (!str || typeof str !== "string") return str;
  let result = str
    .replace(/([.!?])([A-Z])/g, "$1 $2")
    .replace(/([,;])([^\s])/g, "$1 $2")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/(\d)([a-zA-Z])/g, "$1 $2")
    .replace(/([a-zA-Z])(\d)/g, "$1 $2")
    .replace(MERGE_SPLIT_RE, "$1 $2")
    .replace(/  +/g, " ")
    .trim();
  for (const [pattern, replacement] of SPLIT_WORD_REPAIRS) {
    result = result.replace(pattern, replacement);
  }
  return result;
}

// ─── NORMALISERS ─────────────────────────────────────────────────────────────

export function normaliseCustomerLanguage(raw) {
  if (!raw) return {};
  if (Array.isArray(raw)) return { trust_and_skepticism: raw };
  const result = {};
  for (const [key, value] of Object.entries(raw)) {
    if (Array.isArray(value) && value.length > 0) result[key] = value;
  }
  return result;
}

export function formatThemeLabel(key) {
  if (!key) return "Customer Signals";
  const isPlaceholder = /^(theme[_\s]?key[_\s]?\d+|category[_\s]?\d+|theme[_\s]?\d+|group[_\s]?\d+)$/i.test(key.trim());
  if (isPlaceholder) return "Customer Signals";
  return key.replace(/_/g, " ").toUpperCase();
}

export function normaliseAdAngle(angle) {
  if (typeof angle === "string") return {
    angle, hook: angle, why: null, tied_to: null, tied_to_stage: null,
    what_it_addresses: null, suggested_usage: null, proof_direction: null, format: null,
    signals_summary: null, show_direction: null,
  };
  return {
    hook: angle.hook || angle.angle || angle.angle_name || "",
    angle: angle.hook || angle.angle || angle.angle_name || "",
    why: angle.why || angle.what_it_addresses || null,
    what_it_addresses: angle.what_it_addresses || angle.why || null,
    suggested_usage: angle.suggested_usage || null,
    proof_direction: angle.proof_direction || null,
    tied_to: angle.tied_to_stage || angle.tied_to || null,
    tied_to_stage: angle.tied_to_stage || angle.tied_to || null,
    format: angle.format || null,
    signals_summary: angle.signals_summary || null,
    show_direction: Array.isArray(angle.show_direction) ? angle.show_direction : null,
  };
}

// ─── HTML DOWNLOAD TRIGGER ────────────────────────────────────────────────────

export function downloadHTML(html, filename) {
  const blob = new Blob([html], { type: "text/html;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ─── SECTION 2: generateNavBar + generateAdAngleReportHTML + generateMessageGapReportHTML ───

// ─── SHARED NAV BAR ───────────────────────────────────────────────────────────

export function generateWhiteLabelStyle(report) {
  const wl = report?.white_label;
  if (!wl) return "";
  const accent = wl.accent_color || "#2563eb";
  const accentBright = wl.accent_color || "#3b82f6";
  const accentLight = wl.accent_color || "#60a5fa";
  return `<style>:root{--blue:${accent};--blue-bright:${accentBright};--blue-light:${accentLight};}</style>`;
}

export function generateNavBar(brandName, currentPage, uuid, whiteLabel) {
  const pages = [
    { id: "portal",      label: "Portal",           file: "index.html",                          slug: "portal" },
    { id: "trends",      label: "Market Trends",    file: "market-signal-trends.html",           slug: "market-signal-trends" },
    { id: "signals",     label: "Customer Signals", file: "customer-signals-report.html",        slug: "customer-signals" },
    { id: "prepurchase", label: "Pre-Purchase",     file: "pre-purchase-intelligence-report.html", slug: "pre-purchase" },
    { id: "messagegap",  label: "Message Gap",      file: "message-gap-report.html",             slug: "message-gap" },
    { id: "adangles",    label: "Ad Angles",        file: "ad-angle-report.html",                slug: "ad-angles" },
    { id: "strategy",    label: "Overview",         file: "strategy-report.html",                slug: "strategy" },
    { id: "exec",        label: "Exec Summary",     file: "executive-summary.html",              slug: "executive" },
  ];
  const b = String(brandName || "").replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;");
  const agencyName = whiteLabel?.agency_name ? String(whiteLabel.agency_name).replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;") : "Ntentify";
  const accentColor = whiteLabel?.accent_color || "#2563eb";
  const base = uuid ? `/r/${uuid}` : "";
  return `
<nav style="position:sticky;top:0;z-index:100;background:#eef2fa;border-bottom:2px solid ${accentColor};padding:0 32px;display:flex;align-items:center;justify-content:space-between;gap:24px;min-height:56px;font-family:'DM Mono',monospace;">
  <div style="font-family:'Bebas Neue',sans-serif;font-size:18px;letter-spacing:.1em;color:${accentColor};white-space:nowrap;">${agencyName} · ${b}</div>
  <div style="display:flex;gap:6px;flex-wrap:wrap;align-items:center;">
    ${pages.map(p => `
    <a href="${base ? `${base}/${p.slug}` : p.file}" style="font-size:11px;letter-spacing:.12em;text-transform:uppercase;padding:8px 14px;text-decoration:none;border-radius:999px;white-space:nowrap;font-weight:500;transition:all .15s;
      ${currentPage === p.id
        ? "background:#2563eb;color:#ffffff;border:1px solid #2563eb;"
        : "color:#4a6a99;background:#ffffff;border:1px solid #d4e0f5;"}
      " ${currentPage === p.id ? "" : `onmouseover="this.style.color='#0d1526';this.style.borderColor='#2563eb';" onmouseout="this.style.color='#4a6a99';this.style.borderColor='#d4e0f5';"`}>${p.label}</a>`).join("")}
  </div>
</nav>`;
}

// ─── AD ANGLE REPORT ─────────────────────────────────────────────────────────

export function generateAdAngleReportHTML(report, uuid) {
  const s = (v) => String(v || "").replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;").replace(/"/g,"&quot;");
  const ns = (v) => s(normalizeText(String(v || "")));
  const sl = (v) => Array.isArray(v) ? v : [];
  const generated = report.generated_at ? new Date(report.generated_at).toLocaleString() : new Date().toLocaleString();

  const stages = ["Problem Awareness", "Frustration", "Solution Exploration", "Decision"];
  const stageDescriptions = {
    "Problem Awareness": "Reaching people before they know a solution exists.",
    "Frustration": "Reaching people who have tried and been let down.",
    "Solution Exploration": "Reaching people actively comparing options.",
    "Decision": "Reaching people who are close but need a final push.",
  };

  const allAngles = sl(report.recommended_ad_angles).map(normaliseAdAngle);
  const totalAngles = allAngles.length;

  const byStage = {};
  for (const stage of stages) {
    byStage[stage] = allAngles.filter(a =>
      (a.tied_to_stage || a.tied_to || "").toLowerCase().includes(stage.toLowerCase())
    );
  }
  const ungrouped = allAngles.filter(a =>
    !stages.some(st => (a.tied_to_stage || a.tied_to || "").toLowerCase().includes(st.toLowerCase()))
  );

  const stagesHTML = stages.map((stage, stageIdx) => {
    const angles = byStage[stage] || [];
    if (!angles.length) return "";
    return `
<div class="stage-block">
  <div class="stage-header">
    <div class="stage-num">0${stageIdx + 1}</div>
    <div class="stage-header-body">
      <div class="stage-name">${s(stage)}</div>
      <div class="stage-desc">${s(stageDescriptions[stage])}</div>
    </div>
    <div class="stage-count">${angles.length} angle${angles.length !== 1 ? "s" : ""}</div>
  </div>
  ${angles.map((a, idx) => {
    const cardId = `card-${stage.replace(/\s+/g,"-").toLowerCase()}-${idx}`;
    const hasBrief = !!(a.signals_summary || (a.show_direction && a.show_direction.length));
    return `
  <div class="angle-card" id="${cardId}">
    <div class="angle-num">${idx + 1}</div>
    <div class="angle-body">
      ${a.format ? `<div class="angle-format">${s(a.format)}</div>` : ""}
      <div class="angle-hook">${ns(a.hook)}</div>
      ${hasBrief ? `
      <div class="angle-view-toggle">
        <button class="angle-view-btn active" onclick="(function(btn){var card=document.getElementById('${cardId}');card.classList.remove('show-brief');card.querySelectorAll('.angle-view-btn').forEach(function(b){b.classList.remove('active')});btn.classList.add('active');})(this)">Media Buyer</button>
        <button class="angle-view-btn" onclick="(function(btn){var card=document.getElementById('${cardId}');card.classList.add('show-brief');card.querySelectorAll('.angle-view-btn').forEach(function(b){b.classList.remove('active')});btn.classList.add('active');})(this)">Production Brief</button>
      </div>` : ""}
      <div class="angle-meta-view">
        <div class="angle-meta-grid">
          ${a.what_it_addresses ? `<div class="angle-meta-item"><div class="angle-meta-label">ADDRESSES</div><div class="angle-meta-value">${ns(a.what_it_addresses)}</div></div>` : ""}
          ${a.suggested_usage ? `<div class="angle-meta-item"><div class="angle-meta-label">SUGGESTED USAGE</div><div class="angle-meta-value">${s(a.suggested_usage)}</div></div>` : ""}
          ${a.proof_direction ? `<div class="angle-meta-item"><div class="angle-meta-label">PROOF DIRECTION</div><div class="angle-meta-value">${s(a.proof_direction)}</div></div>` : ""}
        </div>
      </div>
      ${hasBrief ? `
      <div class="angle-brief-view">
        ${a.signals_summary ? `
        <div class="angle-signals-summary">
          <div class="angle-signals-label">Signal Intelligence</div>
          ${ns(a.signals_summary)}
        </div>` : ""}
        ${a.show_direction && a.show_direction.length ? `
        <div class="angle-direction-list">
          ${a.show_direction.map(d => `
          <div class="angle-direction-item">
            <div class="angle-direction-format">${s(d.format)}</div>
            <div class="angle-direction-text">${ns(d.direction)}</div>
          </div>`).join("")}
        </div>` : ""}
      </div>` : ""}
    </div>
  </div>`;
  }).join("")}
</div>`;
  }).join("");

  const ungroupedHTML = ungrouped.length ? `
<div class="stage-block">
  <div class="stage-header">
    <div class="stage-num">—</div>
    <div class="stage-header-body">
      <div class="stage-name">Additional Angles</div>
      <div class="stage-desc">Angles without a specific stage assignment.</div>
    </div>
    <div class="stage-count">${ungrouped.length} angle${ungrouped.length !== 1 ? "s" : ""}</div>
  </div>
  ${ungrouped.map((a, idx) => {
    const cardId = `card-ungrouped-${idx}`;
    const hasBrief = !!(a.signals_summary || (a.show_direction && a.show_direction.length));
    return `
  <div class="angle-card" id="${cardId}">
    <div class="angle-num">${idx + 1}</div>
    <div class="angle-body">
      <div class="angle-hook">${ns(a.hook)}</div>
      ${hasBrief ? `
      <div class="angle-view-toggle">
        <button class="angle-view-btn active" onclick="(function(btn){var card=document.getElementById('${cardId}');card.classList.remove('show-brief');card.querySelectorAll('.angle-view-btn').forEach(function(b){b.classList.remove('active')});btn.classList.add('active');})(this)">Media Buyer</button>
        <button class="angle-view-btn" onclick="(function(btn){var card=document.getElementById('${cardId}');card.classList.add('show-brief');card.querySelectorAll('.angle-view-btn').forEach(function(b){b.classList.remove('active')});btn.classList.add('active');})(this)">Production Brief</button>
      </div>` : ""}
      <div class="angle-meta-view">
        <div class="angle-meta-grid">
          ${a.what_it_addresses ? `<div class="angle-meta-item"><div class="angle-meta-label">ADDRESSES</div><div class="angle-meta-value">${ns(a.what_it_addresses)}</div></div>` : ""}
          ${a.suggested_usage ? `<div class="angle-meta-item"><div class="angle-meta-label">SUGGESTED USAGE</div><div class="angle-meta-value">${s(a.suggested_usage)}</div></div>` : ""}
          ${a.proof_direction ? `<div class="angle-meta-item"><div class="angle-meta-label">PROOF DIRECTION</div><div class="angle-meta-value">${s(a.proof_direction)}</div></div>` : ""}
        </div>
      </div>
      ${hasBrief ? `
      <div class="angle-brief-view">
        ${a.signals_summary ? `
        <div class="angle-signals-summary">
          <div class="angle-signals-label">Signal Intelligence</div>
          ${ns(a.signals_summary)}
        </div>` : ""}
        ${a.show_direction && a.show_direction.length ? `
        <div class="angle-direction-list">
          ${a.show_direction.map(d => `
          <div class="angle-direction-item">
            <div class="angle-direction-format">${s(d.format)}</div>
            <div class="angle-direction-text">${ns(d.direction)}</div>
          </div>`).join("")}
        </div>` : ""}
      </div>` : ""}
    </div>
  </div>`;
  }).join("")}
</div>` : "";

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>${s(report.brand_name)} — Ad Angle Report</title>
<link href="https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Mono:wght@300;400;500&family=Inter:wght@300;400;500;600;700&display=swap" rel="stylesheet">
<style>
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
:root {
  --navy-deep:    #080f1c;
  --navy-base:    #0d1526;
  --navy-raised:  #111d33;
  --navy-surface: #162040;
  --navy-edge:    #1e2f55;
  --navy-mid:     #243568;
  --blue:         #2563eb;
  --blue-bright:  #3b82f6;
  --blue-light:   #60a5fa;
  --blue-dim:     rgba(37,99,235,0.1);
  --text-primary:   #f0f4ff;
  --text-secondary: #a8c0e8;
  --text-muted:     #7a9cc8;
  --text-faint:     #4a6a99;
  --light-bg:     #eef2fa;
  --light-surface:#f6f8fd;
  --light-border: #d0daf0;
  --light-text:   #0d1526;
  --light-muted:  #4a5f8a;
  --light-subtle: #7a8fb0;
  --font-disp: 'Bebas Neue', sans-serif;
  --font-body: 'Inter', 'Helvetica Neue', Arial, sans-serif;
  --font-mono: 'DM Mono', monospace;
  --pad: 48px;
}
html, body { background: var(--light-bg); color: var(--light-text); font-family: var(--font-body); font-size: 14px; line-height: 1.6; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.wrap { max-width: 900px; margin: 0 auto; padding-bottom: 80px; }
.cover { background: var(--navy-deep); color: var(--text-primary); padding: 52px var(--pad) 44px; position: relative; overflow: hidden; }
.cover::after { content: ''; position: absolute; top: 0; right: 0; width: 400px; height: 100%; background: radial-gradient(ellipse at 80% 50%, rgba(37,99,235,0.12) 0%, transparent 70%); pointer-events: none; }
.cover-eyebrow { font-family: var(--font-mono); font-size: 10px; letter-spacing: .2em; text-transform: uppercase; color: var(--text-muted); margin-bottom: 14px; position: relative; }
.cover-title { font-family: var(--font-disp); font-size: 64px; line-height: 1; letter-spacing: -.01em; color: var(--text-primary); position: relative; }
.cover-brand { font-family: var(--font-disp); font-size: 36px; letter-spacing: .02em; color: var(--blue-light); margin: 8px 0 32px; position: relative; }
.cover-meta { font-family: var(--font-mono); font-size: 10px; color: var(--text-muted); border-top: 1px solid var(--navy-edge); padding-top: 20px; display: flex; gap: 36px; flex-wrap: wrap; position: relative; }
.cover-stat strong { display: block; font-family: var(--font-disp); font-size: 34px; color: var(--text-primary); line-height: 1; margin-bottom: 3px; }
.intent-strip { background: var(--blue); padding: 14px var(--pad); }
.intent-strip-text { font-family: var(--font-mono); font-size: 10px; letter-spacing: .16em; text-transform: uppercase; color: rgba(255,255,255,0.9); }
.stage-block { border-bottom: 2px solid var(--navy-base); }
.stage-block:last-child { border-bottom: none; }
.stage-header { display: grid; grid-template-columns: 56px 1fr auto; align-items: center; gap: 16px; padding: 24px var(--pad) 20px; background: var(--navy-raised); border-bottom: 1px solid var(--navy-edge); }
.stage-num { font-family: var(--font-disp); font-size: 52px; color: var(--navy-mid); line-height: 1; }
.stage-name { font-family: var(--font-disp); font-size: 30px; letter-spacing: .02em; color: var(--text-primary); line-height: 1; margin-bottom: 4px; }
.stage-desc { font-family: var(--font-mono); font-size: 10px; color: var(--text-muted); letter-spacing: .1em; }
.stage-count { font-family: var(--font-mono); font-size: 10px; letter-spacing: .12em; color: var(--blue-light); white-space: nowrap; background: var(--navy-surface); padding: 6px 12px; border: 1px solid var(--navy-edge); }
.angle-card { display: grid; grid-template-columns: 44px 1fr; border-bottom: 1px solid var(--light-border); background: var(--light-surface); }
.angle-card:last-child { border-bottom: none; }
.angle-num { font-family: var(--font-disp); font-size: 28px; color: var(--light-border); padding: 22px 0 22px 20px; line-height: 1; }
.angle-body { padding: 22px 28px 22px 16px; }
.angle-format { font-family: var(--font-mono); font-size: 11px; letter-spacing: .16em; text-transform: uppercase; color: #f0f4ff; background: var(--navy-raised); border: 1px solid var(--navy-edge); padding: 5px 14px; display: inline-block; margin-bottom: 12px; }
.angle-hook { font-size: 18px; font-weight: 700; color: var(--light-text); line-height: 1.35; margin-bottom: 16px; }
.angle-meta-grid { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 10px; }
.angle-meta-item { background: var(--light-bg); border: 1px solid var(--light-border); padding: 10px 12px; }
.angle-meta-label { font-family: var(--font-mono); font-size: 8px; letter-spacing: .18em; text-transform: uppercase; color: var(--blue); margin-bottom: 5px; }
.angle-meta-value { font-size: 12px; color: var(--light-muted); line-height: 1.5; }
.footer { padding: 28px var(--pad); background: var(--navy-base); border-top: 2px solid var(--navy-edge); }
.footer-text { font-family: var(--font-mono); font-size: 10px; color: var(--text-faint); letter-spacing: .1em; line-height: 1.6; }
.angle-view-toggle { display: flex; gap: 0; margin-bottom: 14px; border: 1px solid var(--light-border); width: fit-content; }
.angle-view-btn { font-family: var(--font-mono); font-size: 10px; letter-spacing: .14em; text-transform: uppercase; padding: 6px 14px; cursor: pointer; border: none; background: var(--light-bg); color: var(--light-muted); transition: background 0.12s, color 0.12s; }
.angle-view-btn.active { background: var(--navy-raised); color: var(--text-primary); }
.angle-view-btn:first-child { border-right: 1px solid var(--light-border); }
.angle-brief-view { display: none; }
.angle-card.show-brief .angle-meta-view { display: none; }
.angle-card.show-brief .angle-brief-view { display: block; }
.angle-signals-summary { font-size: 13px; color: var(--light-text); line-height: 1.75; margin-bottom: 16px; padding: 14px 16px; background: var(--light-bg); border-left: 3px solid var(--blue); }
.angle-signals-label { font-family: var(--font-mono); font-size: 9px; letter-spacing: .18em; text-transform: uppercase; color: var(--blue); margin-bottom: 8px; }
.angle-direction-list { display: grid; gap: 10px; }
.angle-direction-item { padding: 14px 16px; background: var(--light-bg); border: 1px solid var(--light-border); }
.angle-direction-format { font-family: var(--font-mono); font-size: 9px; letter-spacing: .16em; text-transform: uppercase; color: var(--blue); margin-bottom: 8px; }
.angle-direction-text { font-size: 13px; color: var(--light-text); line-height: 1.75; }
@media print {
  @page { margin: 0; size: A4; }
  .stage-header { page-break-after: avoid; break-after: avoid; }
  .angle-card { page-break-inside: avoid; break-inside: avoid; }
  .stage-block { page-break-inside: avoid; break-inside: avoid; }
  .wrap { padding-bottom: 0; }
  .angle-view-toggle { display: none; }
  .angle-brief-view { display: block !important; }
}
</style>
</head>
<body>
${generateWhiteLabelStyle(report)}
${generateNavBar(report.brand_name, "adangles", uuid, report.white_label)}
<div class="wrap">
<div class="cover">
  <div class="cover-eyebrow">Ntentify · Semantic Intent Engine · Ad Angle Report</div>
  <div class="cover-title">AD ANGLE REPORT</div>
  <div class="cover-brand">${s(report.brand_name)}</div>
  <div class="cover-meta">
    <div class="cover-stat"><strong>${totalAngles}</strong>Total Angles</div>
    <div class="cover-stat"><strong>4</strong>Funnel Stages</div>
    <div class="cover-stat"><strong>${s(report.website)}</strong>Website</div>
    <div class="cover-stat"><strong>${s(generated)}</strong>Generated</div>
  </div>
</div>
<div class="intent-strip">
  <div class="intent-strip-text">Each angle: Media Buyer view · Production Brief view · toggle per card</div>
</div>
${(report.exec_summary_verdict?.verdict || report.strategic_verdict) ? `
<div style="background:var(--navy-raised);border-left:4px solid var(--blue);padding:28px var(--pad);border-bottom:2px solid var(--navy-base);">
  <div style="font-family:var(--font-mono);font-size:10px;letter-spacing:.22em;text-transform:uppercase;color:var(--blue-light);margin-bottom:12px;">Highest Signal Opportunity</div>
  <div style="font-size:15px;line-height:1.75;color:var(--text-primary);max-width:720px;">${ns((report.exec_summary_verdict?.verdict || report.strategic_verdict || "").replace(/^STRATEGIC VERDICT:\s*/i, ""))}</div>
</div>` : ""}
${stagesHTML}
${ungroupedHTML}
<div class="footer">
  <div class="footer-text">
    Generated by Semantic Intent Engine · ${s(report.brand_name)} · ${s(generated)}<br>
    All angles are derived from real customer signal data. Hooks are paste-ready for immediate testing.
  </div>
</div>
</div>
</body>
</html>`;
}

// ─── MESSAGE GAP REPORT ───────────────────────────────────────────────────────

export function generateMessageGapReportHTML(report, signals, uuid) {
  const s = (v) => String(v || "").replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;").replace(/"/g,"&quot;");
  const ns = (v) => s(normalizeText(String(v || "")));
  const sl = (v) => Array.isArray(v) ? v : [];
  const generated = report.generated_at ? new Date(report.generated_at).toLocaleString() : new Date().toLocaleString();

  const mg = report.message_gap || null;
  const mis = report.core_strategic_misalignment || {};
  const ss = report.strategic_summary || {};

  const decodeEntities = (str) => (str || "")
    .replace(/&ndash;/g, "–").replace(/&mdash;/g, "—")
    .replace(/&amp;/g, "&").replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'").replace(/&lt;/g, "<").replace(/&gt;/g, ">");

  const coreGap = mg?.core_gap || mis.closing_statement || "";

  const customerNeeds = mg?.what_customers_want?.length
    ? sl(mg.what_customers_want).filter(Boolean)
    : [mis.what_customers_are_trying_to_fix].filter(Boolean);

  const marketingClaims = mg?.what_marketing_is_saying?.length
    ? sl(mg.what_marketing_is_saying)
    : [
        decodeEntities(signals?.title),
        ...(signals?.h1s || []).slice(0, 3).map(decodeEntities),
        ...(signals?.h2s || []).slice(0, 3).map(decodeEntities),
      ].filter(Boolean).slice(0, 6);

  const gapCustomerSide = mg?.the_gap_customer_side || mis.what_customers_are_trying_to_fix || "";
  const gapMarketingSide = mg?.the_gap_marketing_side || mis.what_marketing_is_selling || "";
  const whyThisBreaks = mg?.why_this_breaks || mis.why_this_matters || mis.why_this_creates_failure || "";
  const evidenceQuotes = mg?.evidence_quotes?.length
    ? sl(mg.evidence_quotes)
    : sl(mis.customer_evidence).slice(0, 4);

  const impactPoints = mg?.impact_on_performance?.length
    ? sl(mg.impact_on_performance)
    : sl(ss.top_reasons_marketing_is_failing).slice(0, 3);

  const messagingShifts = mg?.what_to_change?.length
    ? sl(mg.what_to_change)
    : sl(ss.top_messaging_shifts_needed).slice(0, 5);

  const beforeAfterPairs = mg?.before_after?.length
    ? sl(mg.before_after).filter((p) => p?.before && p?.after)
    : sl(ss.top_messaging_shifts_needed).map((shift) => {
        const parts = shift.split(/\.\s*Start\s+/i);
        if (parts.length === 2) {
          return { before: parts[0].replace(/^Stop\s+/i, "").trim(), after: parts[1].trim() };
        }
        return null;
      }).filter(Boolean);

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>${s(report.brand_name)} — Message Gap Report</title>
<link href="https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Mono:wght@300;400;500&family=Inter:wght@300;400;500;600;700&display=swap" rel="stylesheet">
<style>
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
:root {
  --navy-deep:    #080f1c;
  --navy-base:    #0d1526;
  --navy-raised:  #111d33;
  --navy-surface: #162040;
  --navy-edge:    #1e2f55;
  --navy-mid:     #243568;
  --blue:         #2563eb;
  --blue-bright:  #3b82f6;
  --blue-light:   #60a5fa;
  --blue-dim:     rgba(37,99,235,0.1);
  --text-primary:   #f0f4ff;
  --text-secondary: #a8c0e8;
  --text-muted:     #7a9cc8;
  --text-faint:     #4a6a99;
  --light-bg:     #eef2fa;
  --light-surface:#f6f8fd;
  --light-border: #d0daf0;
  --light-text:   #0d1526;
  --light-muted:  #4a5f8a;
  --font-disp: 'Bebas Neue', sans-serif;
  --font-body: 'Inter', 'Helvetica Neue', Arial, sans-serif;
  --font-mono: 'DM Mono', monospace;
  --pad: 48px;
}
html, body { background: var(--light-bg); color: var(--light-text); font-family: var(--font-body); font-size: 14px; line-height: 1.6; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.wrap { max-width: 860px; margin: 0 auto; padding-bottom: 80px; }
.cover { background: var(--navy-deep); color: var(--text-primary); padding: 52px var(--pad) 44px; position: relative; overflow: hidden; }
.cover::after { content: ''; position: absolute; top: 0; right: 0; width: 400px; height: 100%; background: radial-gradient(ellipse at 80% 50%, rgba(37,99,235,0.12) 0%, transparent 70%); pointer-events: none; }
.cover-eyebrow { font-family: var(--font-mono); font-size: 12px; letter-spacing: .2em; text-transform: uppercase; color: var(--text-muted); margin-bottom: 14px; position: relative; }
.cover-title { font-family: var(--font-disp); font-size: 64px; line-height: 1; letter-spacing: -.01em; color: var(--text-primary); position: relative; }
.cover-brand { font-family: var(--font-disp); font-size: 36px; letter-spacing: .02em; color: var(--blue-light); margin: 8px 0 32px; position: relative; }
.cover-meta { font-family: var(--font-mono); font-size: 12px; color: var(--text-muted); border-top: 1px solid var(--navy-edge); padding-top: 20px; display: flex; gap: 36px; flex-wrap: wrap; position: relative; }
.gap-statement { background: var(--blue); padding: 32px var(--pad); }
.gap-statement-label { font-family: var(--font-mono); font-size: 12px; letter-spacing: .2em; text-transform: uppercase; color: rgba(255,255,255,.7); margin-bottom: 10px; }
.gap-statement-text { font-size: 20px; font-weight: 600; color: #fff; line-height: 1.45; max-width: 680px; }
.section { padding: 40px var(--pad); border-bottom: 1px solid var(--light-border); background: var(--light-surface); }
.section:last-child { border-bottom: none; }
.section--gray { background: var(--light-bg); }
.section--blue { background: rgba(37,99,235,0.06); }
.section-label { font-family: var(--font-mono); font-size: 12px; letter-spacing: .2em; text-transform: uppercase; color: var(--blue); margin-bottom: 8px; display: flex; align-items: center; gap: 12px; }
.section-label::after { content: ''; flex: 1; height: 1px; background: var(--light-border); }
.section-title { font-family: var(--font-disp); font-size: 32px; letter-spacing: -.01em; line-height: 1.1; color: var(--light-text); margin-bottom: 20px; }
.need-list { display: grid; gap: 10px; }
.need-item { display: grid; grid-template-columns: 28px 1fr; gap: 12px; align-items: start; padding: 14px 16px; background: var(--light-surface); border: 1px solid var(--light-border); }
.need-num { font-family: var(--font-disp); font-size: 22px; color: var(--blue); line-height: 1; margin-top: 2px; }
.need-text { font-size: 15px; color: var(--light-text); line-height: 1.5; font-weight: 500; }
.claims-list { display: grid; gap: 8px; }
.claim-item { padding: 12px 16px; background: var(--light-bg); border-left: 3px solid var(--light-border); font-size: 14px; color: var(--light-muted); line-height: 1.5; font-style: italic; }
.gap-grid { display: grid; grid-template-columns: 1fr 48px 1fr; margin-top: 4px; }
.gap-col { padding: 20px; border: 1px solid var(--light-border); background: var(--light-surface); }
.gap-col--right { border-left: none; }
.gap-col-label { font-family: var(--font-mono); font-size: 10px; letter-spacing: .16em; text-transform: uppercase; color: var(--light-muted); margin-bottom: 8px; }
.gap-col-text { font-size: 14px; font-weight: 600; line-height: 1.5; color: var(--light-text); }
.gap-divider { background: var(--blue); display: flex; align-items: center; justify-content: center; font-family: var(--font-disp); font-size: 24px; color: #fff; }
.gap-why { margin-top: 16px; padding: 16px 20px; background: var(--blue-dim); border-left: 3px solid var(--blue); font-size: 14px; line-height: 1.7; color: var(--light-text); }
.gap-why-label { font-family: var(--font-mono); font-size: 10px; letter-spacing: .16em; text-transform: uppercase; color: var(--blue); margin-bottom: 6px; }
.quote-list { display: grid; gap: 8px; margin-top: 16px; }
.quote-item { font-family: var(--font-mono); font-size: 13px; font-style: italic; color: var(--light-muted); padding: 10px 14px; background: var(--light-bg); border-left: 3px solid var(--light-border); line-height: 1.5; }
.impact-list { display: grid; gap: 10px; }
.impact-item { padding: 14px 16px; background: var(--light-surface); border: 1px solid var(--light-border); border-left: 3px solid var(--blue); font-size: 14px; line-height: 1.6; color: var(--light-text); }
.change-list { display: grid; gap: 12px; }
.change-item { display: grid; grid-template-columns: 28px 1fr; gap: 12px; align-items: start; padding: 16px; background: var(--light-surface); border: 1px solid var(--light-border); }
.change-num { font-family: var(--font-disp); font-size: 22px; color: var(--blue); line-height: 1; margin-top: 2px; }
.change-text { font-size: 14px; color: var(--light-text); line-height: 1.6; }
.ba-list { display: grid; gap: 16px; }
.ba-item { display: grid; grid-template-columns: 1fr 40px 1fr; }
.ba-col { padding: 16px 18px; }
.ba-col--before { background: var(--light-bg); border: 1px solid var(--light-border); }
.ba-col--after { background: rgba(37,99,235,0.06); border: 1px solid rgba(37,99,235,0.2); border-left: none; }
.ba-arrow { background: var(--light-border); display: flex; align-items: center; justify-content: center; font-size: 18px; color: var(--light-muted); }
.ba-label { font-family: var(--font-mono); font-size: 10px; letter-spacing: .16em; text-transform: uppercase; color: var(--light-muted); margin-bottom: 6px; }
.ba-label--after { color: var(--blue); }
.ba-text { font-size: 13px; line-height: 1.6; color: var(--light-text); }
.visual-evidence { margin-top: 20px; padding: 16px 20px; background: var(--light-bg); border: 1px solid var(--light-border); border-top: 2px solid var(--navy-raised); }
.visual-evidence-label { font-family: var(--font-mono); font-size: 10px; letter-spacing: .16em; text-transform: uppercase; color: var(--light-muted); margin-bottom: 12px; }
.visual-evidence-issue { font-family: var(--font-disp); font-size: 18px; letter-spacing: .02em; color: var(--light-text); margin-bottom: 10px; line-height: 1; }
.visual-evidence-list { display: grid; gap: 8px; }
.visual-evidence-item { display: grid; grid-template-columns: 1fr auto; gap: 12px; align-items: start; padding: 10px 14px; background: var(--light-surface); border: 1px solid var(--light-border); }
.visual-evidence-context { font-size: 13px; color: var(--light-text); line-height: 1.5; }
.visual-evidence-link { font-family: var(--font-mono); font-size: 11px; color: var(--blue); text-decoration: none; white-space: nowrap; padding: 4px 10px; border: 1px solid rgba(37,99,235,0.3); background: var(--blue-dim); }
.visual-evidence-link:hover { background: var(--blue); color: #fff; }
.footer { padding: 28px var(--pad); background: var(--navy-base); border-top: 2px solid var(--navy-edge); }
.footer-text { font-family: var(--font-mono); font-size: 12px; color: var(--text-faint); letter-spacing: .1em; line-height: 1.6; }
@media print {
  @page { margin: 0; size: A4; }
  .section { page-break-inside: avoid; break-inside: avoid; }
  .ba-item { page-break-inside: avoid; break-inside: avoid; }
  .wrap { padding-bottom: 0; }
}
</style>
</head>
<body>
${generateWhiteLabelStyle(report)}
${generateNavBar(report.brand_name, "messagegap", uuid, report.white_label)}
<div class="wrap">

<div class="cover">
  <div class="cover-eyebrow">Ntentify · Semantic Intent Engine · Message Gap Report</div>
  <div class="cover-title">MESSAGE GAP REPORT</div>
  <div class="cover-brand">${s(report.brand_name)}</div>
  <div class="cover-meta">
    <div>${s(report.website)}</div>
    <div>${s(generated)}</div>
  </div>
</div>

${coreGap ? `
<div class="gap-statement">
  <div class="gap-statement-label">Core Gap</div>
  <div class="gap-statement-text">${ns(coreGap)}</div>
</div>` : ""}

${customerNeeds.length ? `
<div class="section">
  <div class="section-label">Section 01</div>
  <div class="section-title">What Customers Actually Want</div>
  <div class="need-list">
    ${customerNeeds.map((need, i) => `
    <div class="need-item">
      <div class="need-num">${i + 1}</div>
      <div class="need-text">${ns(need)}</div>
    </div>`).join("")}
  </div>
</div>` : ""}

${marketingClaims.length ? `
<div class="section section--gray">
  <div class="section-label">Section 02</div>
  <div class="section-title">What Marketing Is Saying</div>
  <div class="claims-list">
    ${marketingClaims.map((claim) => `<div class="claim-item">"${ns(claim)}"</div>`).join("")}
  </div>
</div>` : ""}

${(gapCustomerSide && gapMarketingSide) ? `
<div class="section">
  <div class="section-label">Section 03</div>
  <div class="section-title">The Gap</div>
  <div class="gap-grid">
    <div class="gap-col">
      <div class="gap-col-label">What Customers Want</div>
      <div class="gap-col-text">${ns(gapCustomerSide)}</div>
    </div>
    <div class="gap-divider">≠</div>
    <div class="gap-col gap-col--right">
      <div class="gap-col-label">What Marketing Delivers</div>
      <div class="gap-col-text">${ns(gapMarketingSide)}</div>
    </div>
  </div>
  ${whyThisBreaks ? `
  <div class="gap-why">
    <div class="gap-why-label">Why This Breaks Performance</div>
    ${ns(whyThisBreaks)}
  </div>` : ""}
  ${evidenceQuotes.length ? `
  <div class="quote-list">
    ${evidenceQuotes.map((q) => `<div class="quote-item">"${ns(q)}"</div>`).join("")}
  </div>` : ""}
  ${sl(mg?.visual_evidence).length ? `
  <div class="visual-evidence">
    <div class="visual-evidence-label">What This Looks Like In The Real World</div>
    ${sl(mg.visual_evidence).map(ev => `
    <div style="margin-bottom: 16px;">
      <div class="visual-evidence-issue">${s(ev.issue)}</div>
      <div class="visual-evidence-list">
        ${sl(ev.videos).map(v => `
        <div class="visual-evidence-item">
          <div class="visual-evidence-context">${ns(v.context)}</div>
          <a class="visual-evidence-link" href="${s(v.url)}" target="_blank" rel="noreferrer">Watch →</a>
        </div>`).join("")}
      </div>
    </div>`).join("")}
  </div>` : ""}
</div>` : ""}

${impactPoints.length ? `
<div class="section section--gray">
  <div class="section-label">Section 04</div>
  <div class="section-title">Impact on Performance</div>
  <div class="impact-list">
    ${impactPoints.map((point) => `<div class="impact-item">${ns(point)}</div>`).join("")}
  </div>
</div>` : ""}

${messagingShifts.length ? `
<div class="section section--blue">
  <div class="section-label">Section 05</div>
  <div class="section-title">What To Change</div>
  <div class="change-list">
    ${messagingShifts.map((shift, i) => `
    <div class="change-item">
      <div class="change-num">${i + 1}</div>
      <div class="change-text">${ns(shift)}</div>
    </div>`).join("")}
  </div>
</div>` : ""}

${beforeAfterPairs.length ? `
<div class="section">
  <div class="section-label">Section 06</div>
  <div class="section-title">Before → After</div>
  <div class="ba-list">
    ${beforeAfterPairs.map((pair) => `
    <div class="ba-item">
      <div class="ba-col ba-col--before">
        <div class="ba-label">Current Messaging</div>
        <div class="ba-text">${ns(pair.before)}</div>
      </div>
      <div class="ba-arrow">→</div>
      <div class="ba-col ba-col--after">
        <div class="ba-label ba-label--after">Improved Messaging</div>
        <div class="ba-text">${ns(pair.after)}</div>
      </div>
    </div>`).join("")}
  </div>
</div>` : ""}

<div class="footer">
  <div class="footer-text">
    Generated by Semantic Intent Engine · ${s(report.brand_name)} · ${s(generated)}<br>
    Derived from real customer signal data. For full analysis see the Strategy Report.
  </div>
</div>

</div>
</body>
</html>`;
}

// ─── SECTION 3: generateCustomerSignalsReportHTML + generatePortalHTML ────────

export function generateCustomerSignalsReportHTML(report, uuid) {
  const s = (v) => String(v || "").replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;").replace(/"/g,"&quot;");
  const ns = (v) => s(normalizeText(String(v || ""))).replace(/'/g, "&#39;");
  const sl = (v) => Array.isArray(v) ? v : [];
  const generated = report.generated_at ? new Date(report.generated_at).toLocaleString() : new Date().toLocaleString();

  const cs = report.customer_signals || {};
  const voc = cs.voice_of_customer || {};
  const watching = cs.what_customers_are_watching || {};
  const patterns = sl(cs.content_patterns);
  const gaps = sl(cs.content_gaps);

  const STAGES = ["Problem Awareness", "Frustration", "Solution Exploration", "Decision"];
  const STAGE_NUMS = { "Problem Awareness": "01", "Frustration": "02", "Solution Exploration": "03", "Decision": "04" };
  const STAGE_MINI = { "Problem Awareness": "PROB AWARE", "Frustration": "FRUSTRATION", "Solution Exploration": "SOL EXP", "Decision": "DECISION" };
  const STAGE_DESC = {
    "Problem Awareness": "What people say before they know a solution exists.",
    "Frustration": "What people say after being let down.",
    "Solution Exploration": "What people say while comparing options.",
    "Decision": "What people say when they're close to buying.",
  };

  const vocHTML = STAGES.map((stage) => {
    const stageData = voc[stage] || {};
    const quotes = sl(stageData.quotes);
    const summary = stageData.summary || "";
    const cluster = sl(report.intent_clusters).find(c => c.intent_stage === stage) || {};
    const userQueries = sl(cluster.user_queries);
    if (!quotes.length && !summary && !userQueries.length) return "";
    return `
<div class="stage-block">
  <div class="stage-header">
    <div class="stage-num">${STAGE_NUMS[stage]}</div>
    <div class="stage-header-body">
      <div class="stage-name">${s(stage)}</div>
      <div class="stage-desc">${s(STAGE_DESC[stage])}</div>
    </div>
  </div>
  ${summary ? `<div class="stage-summary">${ns(summary)}</div>` : ""}
  <div class="quote-grid">
    ${quotes.map(q => `<div class="quote-item">"${ns(q)}"</div>`).join("")}
  </div>
  ${userQueries.length ? `
  <div class="queries-block">
    <div class="queries-label">What they're Googling</div>
    ${userQueries.map(q => `<div class="query-item">${ns(q)}</div>`).join("")}
  </div>` : ""}
</div>`;
  }).join("");

  // ─── WHAT CUSTOMERS ARE WATCHING — Theater Layout (Session 27) ──────────────
  const allVideos = [];
  STAGES.forEach((stage) => {
    sl(watching[stage]).forEach((v) => {
      allVideos.push({
        title: v.title || v.relevance || "",
        url: v.url || "",
        relevance: v.relevance || "",
        stage: stage,
        stageMini: STAGE_MINI[stage],
      });
    });
  });
  const totalVideos = allVideos.length;
  const stageCounts = {};
  STAGES.forEach(st => { stageCounts[st] = sl(watching[st]).length; });

  let watchingHTML = "";
  if (totalVideos > 0) {
    const totalComments = report.signal_coverage?.social_comments_analyzed || report.signal_breakdown?.social_comments || 0;
    const patternCount = patterns.length;
    const stagesWithVideos = STAGES.filter(st => stageCounts[st] > 0).length;

    const statsStrip = `
<div class="watching-stats-strip">
  <div class="watching-stat"><div class="watching-stat-num">${totalComments}</div><div class="watching-stat-label">Video Comments</div></div>
  <div class="watching-stat"><div class="watching-stat-num">${totalVideos}</div><div class="watching-stat-label">Videos Mapped</div></div>
  <div class="watching-stat"><div class="watching-stat-num">${patternCount}</div><div class="watching-stat-label">Hook Patterns</div></div>
  <div class="watching-stat"><div class="watching-stat-num">${stagesWithVideos}</div><div class="watching-stat-label">Funnel Stages</div></div>
</div>`;

    const stageFilterStrip = `
<div class="watching-stage-filter">
  <div class="watching-stage-cell active" data-stage-filter="all">
    <div class="watching-stage-num">ALL</div>
    <div class="watching-stage-name">Every Stage</div>
    <div class="watching-stage-count">${totalVideos} videos</div>
  </div>
  ${STAGES.filter(st => stageCounts[st] > 0).map(st => `
  <div class="watching-stage-cell" data-stage-filter="${s(st)}">
    <div class="watching-stage-num">${STAGE_NUMS[st]}</div>
    <div class="watching-stage-name">${s(st)}</div>
    <div class="watching-stage-count">${stageCounts[st]} video${stageCounts[st] !== 1 ? "s" : ""}</div>
  </div>`).join("")}
</div>`;

    function ytId(url) { var m = String(url || '').match(/(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/); return m ? m[1] : ''; }
    const feature = allVideos[0];
    const featureVideoId = ytId(feature.url);
    const featureHTML = `
<div class="watching-feature" id="watchingFeature" data-video-id="${s(featureVideoId)}">
  <div class="watching-feature-thumb-wrap" id="watchingFeatureThumbLink">
    <div class="watching-feature-thumb" id="watchingFeatureThumb">
      <div class="watching-feature-source-tag">VIDEO</div>
      <div class="watching-feature-now-playing">FEATURED</div>
      <div class="watching-feature-play-icon"><svg viewBox="0 0 24 24" width="28" height="28" fill="white"><path d="M8 5v14l11-7z"/></svg></div>
    </div>
    <div class="watching-feature-iframe-wrap" id="watchingFeatureIframeWrap" style="display:none;"></div>
  </div>
  <div class="watching-feature-meta">
    <div class="watching-feature-meta-top">
      <span class="watching-feature-stage-badge" id="watchingFeatureStage">${s(feature.stage)}</span>
      <a href="${s(feature.url)}" target="_blank" rel="noopener" class="watching-youtube-link" id="watchingFeatureYoutubeLink">WATCH VIDEO ↗</a>
    </div>
    <h3 id="watchingFeatureTitle">${ns(feature.title)}</h3>
    <div class="watching-relevance-block">
      <div class="watching-relevance-label">Why Buyers Watch This</div>
      <div class="watching-relevance-text" id="watchingFeatureRelevance">${ns(feature.relevance)}</div>
    </div>
  </div>
</div>`;

    const railCards = allVideos.map((v, i) => `
<div class="watching-rail-card${i === 0 ? ' is-playing' : ''}" data-video-index="${i}" data-stage="${s(v.stage)}">
  <div class="watching-rail-card-top">
    <span class="watching-rail-num">${String(i + 1).padStart(2, '0')}</span>
    <span class="watching-stage-mini">${v.stageMini}</span>
  </div>
  <h4>${ns(v.title)}</h4>
  ${v.relevance ? `<div class="watching-rail-relevance">${ns(v.relevance)}</div>` : ''}
</div>`).join("");

    const railHTML = `
<div class="watching-rail" id="watchingRail">
  <div class="watching-rail-header">
    <div class="watching-rail-header-title">Full Inventory</div>
    <div class="watching-rail-header-count" id="watchingRailCount">${totalVideos} VIDEOS</div>
  </div>
  ${railCards}
</div>`;

    const patternsBlockHTML = patterns.length ? `
<div class="watching-patterns-section">
  <div class="watching-patterns-header">
    <h3>Hook Patterns Buyers Click On</h3>
    <div class="watching-patterns-meta">${patterns.length} PATTERN${patterns.length !== 1 ? 'S' : ''} / ${totalComments} COMMENTS / ${totalVideos} VIDEOS</div>
  </div>
  <div class="watching-patterns-grid">
    ${patterns.map(p => `
    <div class="watching-pattern-card">
      <div class="watching-pattern-frequency">${s((p.frequency || 'occasional').toUpperCase())}</div>
      <div class="watching-pattern-hook">${ns(p.hook)}</div>
      ${sl(p.examples).length ? `
      <div class="watching-pattern-examples-label">Example Titles</div>
      <div class="watching-pattern-examples">
        ${sl(p.examples).map(e => {
          const title = typeof e === 'string' ? e : (e?.title || '');
          const url = typeof e === 'string' ? '' : (e?.url || '');
          if (url) return `<div class="watching-pattern-example"><a href="${s(url)}" target="_blank" rel="noreferrer">${ns(title)}</a></div>`;
          return `<div class="watching-pattern-example">${ns(title)}</div>`;
        }).join('')}
      </div>` : ''}
    </div>`).join('')}
  </div>
</div>` : '';

    const videosJson = JSON.stringify(allVideos.map(v => ({ title: v.title, url: v.url, relevance: v.relevance, stage: v.stage }))).replace(/</g, '\\u003c');

    watchingHTML = `
<div class="watching-body">
  ${statsStrip}
  ${stageFilterStrip}
  <div class="watching-theater-grid">
    ${featureHTML}
    ${railHTML}
  </div>
  ${patternsBlockHTML}
</div>
<script>
(function() {
  var videos = ${videosJson};
  var activeIndex = 0;
  var feature = document.getElementById('watchingFeature');
  if (!feature) return;
  var rail = document.getElementById('watchingRail');
  var railCount = document.getElementById('watchingRailCount');
  var titleEl = document.getElementById('watchingFeatureTitle');
  var stageEl = document.getElementById('watchingFeatureStage');
  var relevanceEl = document.getElementById('watchingFeatureRelevance');
  var ytLink = document.getElementById('watchingFeatureYoutubeLink');
  var thumb = document.getElementById('watchingFeatureThumb');
  var iframeWrap = document.getElementById('watchingFeatureIframeWrap');
  var thumbWrap = document.getElementById('watchingFeatureThumbLink');
  function ytIdFromUrl(url) { var m = String(url || '').match(/(?:youtube\\.com\\/(?:watch\\?(?:.*&)?v=|embed\\/|shorts\\/)|youtu\\.be\\/)([\\w-]{11})/); return m ? m[1] : ''; }
  function loadIframe(videoId, autoplay) {
    if (!videoId) return false;
    var src = 'https://www.youtube.com/embed/' + videoId + '?rel=0&modestbranding=1' + (autoplay ? '&autoplay=1' : '');
    iframeWrap.innerHTML = '<iframe src="' + src + '" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>';
    thumb.style.display = 'none';
    iframeWrap.style.display = 'block';
    return true;
  }
  function unloadIframe() {
    iframeWrap.innerHTML = '';
    iframeWrap.style.display = 'none';
    thumb.style.display = 'flex';
  }
  function swapFeature(index) {
    if (index === activeIndex) return;
    var v = videos[index]; if (!v) return;
    feature.classList.add('swapping');
    setTimeout(function() {
      titleEl.textContent = v.title;
      stageEl.textContent = v.stage;
      relevanceEl.textContent = v.relevance;
      if (v.url) ytLink.href = v.url;
      var newId = ytIdFromUrl(v.url);
      feature.setAttribute('data-video-id', newId);
      if (iframeWrap.style.display === 'block') {
        loadIframe(newId, true);
      } else {
        unloadIframe();
      }
      rail.querySelectorAll('.watching-rail-card').forEach(function(card, i) { card.classList.toggle('is-playing', i === index); });
      activeIndex = index;
      feature.classList.remove('swapping');
    }, 200);
  }
  thumbWrap.addEventListener('click', function(e) {
    if (iframeWrap.style.display === 'block') return;
    e.preventDefault();
    var id = feature.getAttribute('data-video-id');
    if (id) loadIframe(id, true);
  });
  function applyStageFilter(stage) {
    document.querySelectorAll('.watching-stage-cell').forEach(function(cell) {
      cell.classList.toggle('active', cell.getAttribute('data-stage-filter') === stage);
    });
    var visibleCount = 0;
    rail.querySelectorAll('.watching-rail-card').forEach(function(card) {
      var visible = (stage === 'all' || card.getAttribute('data-stage') === stage);
      card.style.display = visible ? '' : 'none';
      if (visible) visibleCount++;
    });
    if (railCount) railCount.textContent = visibleCount + ' VIDEO' + (visibleCount !== 1 ? 'S' : '');
  }
  rail.querySelectorAll('.watching-rail-card').forEach(function(card) {
    card.addEventListener('click', function() { swapFeature(parseInt(card.getAttribute('data-video-index'), 10)); });
  });
  document.querySelectorAll('.watching-stage-cell').forEach(function(cell) {
    cell.addEventListener('click', function() { applyStageFilter(cell.getAttribute('data-stage-filter')); });
  });
})();
</script>`;
  }

  // patternsHTML removed — hook patterns now folded into watchingHTML (Session 27)

  const gapsHTML = gaps.map((g, i) => `
<div class="gap-item">
  <div class="gap-num">${i + 1}</div>
  <div class="gap-body">
    <div class="gap-label">${ns(g.gap)}</div>
    ${g.why_it_matters ? `<div class="gap-why">${ns(g.why_it_matters)}</div>` : ""}
  </div>
</div>`).join("");

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>${s(report.brand_name)} — Customer Signals Report</title>
<link href="https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Mono:wght@300;400;500&family=Inter:wght@300;400;500;600;700&display=swap" rel="stylesheet">
<style>
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
:root {
  --navy-deep: #080f1c; --navy-base: #0d1526; --navy-raised: #111d33;
  --navy-surface: #162040; --navy-edge: #1e2f55; --navy-mid: #243568;
  --blue: #2563eb; --blue-bright: #3b82f6; --blue-light: #60a5fa;
  --blue-dim: rgba(37,99,235,0.1);
  --text-primary: #f0f4ff; --text-secondary: #a8c0e8;
  --text-muted: #7a9cc8; --text-faint: #4a6a99;
  --light-bg: #eef2fa; --light-surface: #f6f8fd;
  --light-border: #d0daf0; --light-text: #0d1526; --light-muted: #4a5f8a;
  --font-disp: 'Bebas Neue', sans-serif;
  --font-body: 'Inter', 'Helvetica Neue', Arial, sans-serif;
  --font-mono: 'DM Mono', monospace;
  --pad: 48px;
}
html, body { background: var(--light-bg); color: var(--light-text); font-family: var(--font-body); font-size: 14px; line-height: 1.6; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.wrap { max-width: 860px; margin: 0 auto; padding-bottom: 80px; }
.cover { background: var(--navy-deep); color: var(--text-primary); padding: 52px var(--pad) 44px; position: relative; overflow: hidden; }
.cover::after { content: ''; position: absolute; top: 0; right: 0; width: 400px; height: 100%; background: radial-gradient(ellipse at 80% 50%, rgba(37,99,235,0.12) 0%, transparent 70%); pointer-events: none; }
.cover-eyebrow { font-family: var(--font-mono); font-size: 12px; letter-spacing: .2em; text-transform: uppercase; color: var(--text-muted); margin-bottom: 14px; position: relative; }
.cover-title { font-family: var(--font-disp); font-size: 56px; line-height: 1; letter-spacing: -.01em; color: var(--text-primary); position: relative; }
.cover-subtitle { font-family: var(--font-disp); font-size: 28px; letter-spacing: .02em; color: var(--blue-light); margin: 4px 0 32px; position: relative; }
.cover-brand { font-family: var(--font-disp); font-size: 36px; letter-spacing: .02em; color: var(--blue-light); margin: 0 0 32px; position: relative; }
.cover-meta { font-family: var(--font-mono); font-size: 12px; color: var(--text-muted); border-top: 1px solid var(--navy-edge); padding-top: 20px; display: flex; gap: 36px; flex-wrap: wrap; position: relative; }
.report-section { border-bottom: 2px solid var(--navy-base); }
.report-section:last-child { border-bottom: none; }
.section-header { background: var(--navy-raised); padding: 20px var(--pad); border-bottom: 1px solid var(--navy-edge); }
.section-eyebrow { font-family: var(--font-mono); font-size: 12px; letter-spacing: .2em; text-transform: uppercase; color: var(--text-muted); margin-bottom: 6px; }
.section-title { font-family: var(--font-disp); font-size: 36px; letter-spacing: -.01em; color: var(--text-primary); line-height: 1; }
.section-desc { font-family: var(--font-mono); font-size: 12px; color: var(--text-muted); margin-top: 6px; letter-spacing: .06em; }
.stage-block { border-bottom: 1px solid var(--light-border); }
.stage-block:last-child { border-bottom: none; }
.stage-header { display: grid; grid-template-columns: 48px 1fr; gap: 16px; align-items: center; padding: 20px var(--pad) 16px; background: var(--light-bg); border-bottom: 1px solid var(--light-border); }
.stage-num { font-family: var(--font-disp); font-size: 40px; color: var(--light-border); line-height: 1; }
.stage-name { font-family: var(--font-disp); font-size: 24px; letter-spacing: .02em; color: var(--light-text); line-height: 1; margin-bottom: 3px; }
.stage-desc { font-family: var(--font-mono); font-size: 12px; color: var(--light-muted); letter-spacing: .08em; }
.stage-summary { padding: 14px var(--pad); font-size: 13px; color: var(--light-muted); line-height: 1.7; font-style: italic; border-bottom: 1px solid var(--light-border); background: var(--light-surface); }
.quote-grid { display: grid; gap: 0; padding: 0 var(--pad); background: var(--light-surface); }
.quote-item { font-family: var(--font-mono); font-size: 13px; font-style: italic; color: var(--light-text); padding: 12px 0; border-bottom: 1px solid var(--light-border); line-height: 1.55; }
.queries-block { padding: 12px var(--pad) 16px; background: var(--light-bg); border-top: 1px solid var(--light-border); }
.queries-label { font-family: var(--font-mono); font-size: 10px; letter-spacing: .16em; text-transform: uppercase; color: var(--blue); margin-bottom: 10px; }
.query-item { font-family: var(--font-mono); font-size: 12px; color: var(--light-muted); padding: 7px 10px; background: var(--light-surface); border: 1px solid var(--light-border); margin-bottom: 4px; line-height: 1.4; }
.query-item:last-child { margin-bottom: 0; }
.video-list { display: grid; gap: 0; padding: 0 var(--pad); background: var(--light-surface); }
.video-item { padding: 14px 0; border-bottom: 1px solid var(--light-border); display: grid; grid-template-columns: 1fr auto; gap: 12px; align-items: center; }
.video-item:last-child { border-bottom: none; }
.video-relevance { font-family: var(--font-mono); font-size: 12px; color: var(--light-text); line-height: 1.5; }
.video-watch { font-family: var(--font-mono); font-size: 11px; color: var(--blue); text-decoration: none; white-space: nowrap; padding: 4px 10px; border: 1px solid rgba(37,99,235,0.3); background: var(--blue-dim); }
.video-watch:hover { background: var(--blue); color: #fff; }
.patterns-list { display: grid; gap: 0; background: var(--light-surface); }
.pattern-item { padding: 20px var(--pad); border-bottom: 1px solid var(--light-border); }
.pattern-item:last-child { border-bottom: none; }
.pattern-header { display: flex; align-items: center; gap: 14px; margin-bottom: 10px; }
.pattern-hook { font-family: var(--font-disp); font-size: 22px; letter-spacing: .02em; color: var(--light-text); line-height: 1; }
.pattern-freq { font-family: var(--font-mono); font-size: 10px; letter-spacing: .14em; text-transform: uppercase; padding: 4px 10px; border-radius: 2px; white-space: nowrap; }
.freq-common { background: var(--blue-dim); color: var(--blue); }
.freq-occasional { background: var(--light-border); color: var(--light-muted); }
.freq-rare { background: rgba(37,99,235,0.05); color: var(--blue-bright); }
.pattern-examples { display: grid; gap: 6px; }
.pattern-example { font-family: var(--font-mono); font-size: 12px; color: var(--light-muted); padding: 6px 10px; background: var(--light-bg); border: 1px solid var(--light-border); line-height: 1.4; }
.pattern-example-link { color: var(--blue); text-decoration: none; }
.pattern-example-link:hover { text-decoration: underline; }
.gaps-list { display: grid; gap: 0; background: var(--light-surface); }
.gap-item { display: grid; grid-template-columns: 40px 1fr; gap: 16px; align-items: start; padding: 20px var(--pad); border-bottom: 1px solid var(--light-border); }
.gap-item:last-child { border-bottom: none; }
.gap-num { font-family: var(--font-disp); font-size: 32px; color: var(--blue); line-height: 1; margin-top: 2px; }
.gap-label { font-size: 15px; font-weight: 700; color: var(--light-text); line-height: 1.4; margin-bottom: 6px; }
.gap-why { font-size: 13px; color: var(--light-muted); line-height: 1.65; }
.footer { padding: 28px var(--pad); background: var(--navy-base); border-top: 2px solid var(--navy-edge); }
.footer-text { font-family: var(--font-mono); font-size: 12px; color: var(--text-faint); letter-spacing: .1em; line-height: 1.6; }
/* WHAT CUSTOMERS ARE WATCHING — Theater Layout (Session 27) */
.watching-body { background: var(--navy-deep); color: var(--text-primary); padding: 32px var(--pad); }
.watching-stats-strip { display: grid; grid-template-columns: repeat(4, 1fr); gap: 0; background: var(--navy-base); border: 1px solid var(--navy-edge); border-radius: 4px; margin-bottom: 24px; position: relative; overflow: hidden; }
.watching-stats-strip::before { content: ''; position: absolute; top: 0; left: 0; width: 100%; height: 1px; background: linear-gradient(90deg, transparent, var(--blue-bright), transparent); }
.watching-stat { padding: 24px 20px; border-right: 1px solid var(--navy-edge); }
.watching-stat:last-child { border-right: none; }
.watching-stat-num { font-family: var(--font-disp); font-size: 48px; line-height: 1; color: var(--blue-light); margin-bottom: 6px; letter-spacing: .02em; }
.watching-stat-label { font-family: var(--font-mono); font-size: 10px; color: var(--text-faint); letter-spacing: .18em; text-transform: uppercase; }
.watching-stage-filter { display: grid; grid-template-columns: repeat(auto-fit, minmax(140px, 1fr)); gap: 0; border: 1px solid var(--navy-edge); border-radius: 4px; overflow: hidden; margin-bottom: 24px; background: var(--navy-base); }
.watching-stage-cell { padding: 18px 16px; border-right: 1px solid var(--navy-edge); position: relative; cursor: pointer; transition: background 0.15s ease; }
.watching-stage-cell:last-child { border-right: none; }
.watching-stage-cell:hover { background: var(--navy-raised); }
.watching-stage-cell.active { background: var(--navy-raised); }
.watching-stage-cell.active::after { content: ''; position: absolute; bottom: 0; left: 0; right: 0; height: 2px; background: var(--blue-bright); }
.watching-stage-num { font-family: var(--font-mono); font-size: 10px; color: var(--text-faint); letter-spacing: .18em; margin-bottom: 6px; }
.watching-stage-name { font-family: var(--font-disp); font-size: 18px; letter-spacing: .04em; line-height: 1; color: var(--text-primary); margin-bottom: 4px; }
.watching-stage-count { font-family: var(--font-mono); font-size: 11px; color: var(--blue-light); }
.watching-theater-grid { display: grid; grid-template-columns: 1.8fr 1fr; gap: 20px; margin-bottom: 28px; }
.watching-feature { background: var(--navy-base); border: 1px solid var(--navy-edge); border-radius: 4px; overflow: hidden; align-self: start; transition: opacity 0.25s ease; }
.watching-feature.swapping { opacity: 0.4; }
.watching-feature-thumb-link { display: block; text-decoration: none; }
.watching-feature-thumb-wrap { position: relative; }
.watching-feature-thumb { aspect-ratio: 16 / 9; background: linear-gradient(135deg, var(--navy-raised) 0%, var(--navy-mid) 100%); position: relative; display: flex; align-items: center; justify-content: center; cursor: pointer; }
.watching-feature-iframe-wrap { aspect-ratio: 16 / 9; background: #000; position: relative; }
.watching-feature-iframe-wrap iframe { width: 100%; height: 100%; display: block; border: 0; }
.watching-feature-thumb::before { content: ''; position: absolute; inset: 0; background: radial-gradient(circle at 30% 40%, rgba(59,130,246,0.15), transparent 50%), radial-gradient(circle at 70% 60%, rgba(96,165,250,0.1), transparent 50%); }
.watching-feature-source-tag { position: absolute; top: 14px; left: 14px; background: rgba(8,15,28,0.85); color: var(--text-primary); padding: 5px 10px; border-radius: 2px; font-family: var(--font-mono); font-size: 10px; letter-spacing: .15em; display: flex; align-items: center; gap: 8px; z-index: 2; }
.watching-feature-source-tag::before { content: ''; width: 7px; height: 7px; border-radius: 50%; background: #ef4444; }
.watching-feature-now-playing { position: absolute; top: 14px; right: 14px; background: var(--blue-bright); color: white; padding: 5px 10px; border-radius: 2px; font-family: var(--font-mono); font-size: 10px; letter-spacing: .15em; z-index: 2; }
.watching-feature-play-icon { width: 64px; height: 64px; border-radius: 50%; background: rgba(0,0,0,0.6); border: 2px solid rgba(255,255,255,0.3); display: flex; align-items: center; justify-content: center; transition: transform 0.2s ease; z-index: 2; }
.watching-feature-thumb:hover .watching-feature-play-icon { transform: scale(1.08); }
.watching-feature-play-icon svg { margin-left: 4px; }
.watching-feature-meta { padding: 22px; }
.watching-feature-meta-top { display: flex; justify-content: space-between; align-items: center; margin-bottom: 14px; gap: 12px; flex-wrap: wrap; }
.watching-feature-stage-badge { display: inline-block; padding: 4px 10px; background: rgba(59,130,246,0.12); border: 1px solid rgba(96,165,250,0.3); color: var(--blue-light); font-family: var(--font-mono); font-size: 10px; letter-spacing: .15em; text-transform: uppercase; border-radius: 2px; }
.watching-youtube-link { font-family: var(--font-mono); font-size: 10px; color: var(--text-muted); letter-spacing: .15em; text-decoration: none; padding: 4px 10px; border: 1px solid var(--navy-edge); border-radius: 2px; transition: all 0.15s ease; }
.watching-youtube-link:hover { border-color: #ef4444; color: var(--text-primary); }
.watching-feature h3 { font-family: var(--font-body); font-size: 17px; font-weight: 700; line-height: 1.35; margin-bottom: 14px; color: var(--text-primary); }
.watching-relevance-block { border-left: 2px solid var(--blue-bright); padding-left: 14px; margin-top: 14px; }
.watching-relevance-label { font-family: var(--font-mono); font-size: 10px; color: var(--text-faint); letter-spacing: .18em; text-transform: uppercase; margin-bottom: 8px; }
.watching-relevance-text { color: var(--text-secondary); font-size: 13px; line-height: 1.65; }
.watching-rail { background: var(--navy-base); border: 1px solid var(--navy-edge); border-radius: 4px; max-height: 580px; overflow-y: auto; }
.watching-rail::-webkit-scrollbar { width: 6px; }
.watching-rail::-webkit-scrollbar-track { background: var(--navy-base); }
.watching-rail::-webkit-scrollbar-thumb { background: var(--navy-edge); border-radius: 3px; }
.watching-rail::-webkit-scrollbar-thumb:hover { background: var(--blue-bright); }
.watching-rail-header { position: sticky; top: 0; background: var(--navy-base); padding: 14px 18px; border-bottom: 1px solid var(--navy-edge); z-index: 10; display: flex; justify-content: space-between; align-items: center; }
.watching-rail-header-title { font-family: var(--font-mono); font-size: 10px; color: var(--text-faint); letter-spacing: .2em; text-transform: uppercase; }
.watching-rail-header-count { font-family: var(--font-mono); font-size: 11px; color: var(--blue-light); }
.watching-rail-card { padding: 14px 18px; border-bottom: 1px solid var(--navy-edge); cursor: pointer; transition: background 0.15s ease; position: relative; }
.watching-rail-card:last-child { border-bottom: none; }
.watching-rail-card:hover { background: var(--navy-raised); }
.watching-rail-card.is-playing { background: var(--navy-raised); border-left: 2px solid var(--blue-bright); padding-left: 16px; }
.watching-rail-card-top { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; }
.watching-rail-num { font-family: var(--font-mono); font-size: 10px; color: var(--text-faint); letter-spacing: .15em; }
.watching-stage-mini { font-family: var(--font-mono); font-size: 9px; color: var(--blue-light); letter-spacing: .15em; background: rgba(59,130,246,0.08); padding: 3px 8px; border-radius: 2px; }
.watching-rail-card h4 { font-family: var(--font-body); font-size: 13px; font-weight: 600; line-height: 1.4; margin-bottom: 6px; color: var(--text-primary); }
.watching-rail-relevance { font-size: 11px; color: var(--text-muted); line-height: 1.5; }
.watching-patterns-section { margin-top: 32px; padding-top: 32px; border-top: 1px solid var(--navy-edge); }
.watching-patterns-header { display: flex; justify-content: space-between; align-items: flex-end; margin-bottom: 24px; gap: 16px; flex-wrap: wrap; }
.watching-patterns-header h3 { font-family: var(--font-disp); font-size: 28px; letter-spacing: .02em; color: var(--text-primary); }
.watching-patterns-meta { font-family: var(--font-mono); font-size: 10px; color: var(--text-faint); letter-spacing: .15em; }
.watching-patterns-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 14px; }
.watching-pattern-card { background: linear-gradient(180deg, var(--navy-base) 0%, var(--navy-raised) 100%); border: 1px solid var(--navy-edge); border-radius: 4px; padding: 22px; position: relative; overflow: hidden; }
.watching-pattern-card::before { content: ''; position: absolute; top: 0; left: 0; width: 3px; height: 100%; background: linear-gradient(180deg, var(--blue-bright), transparent); }
.watching-pattern-frequency { display: inline-block; font-family: var(--font-mono); font-size: 9px; color: var(--blue-light); letter-spacing: .2em; margin-bottom: 12px; }
.watching-pattern-hook { font-family: var(--font-disp); font-size: 22px; line-height: 1.1; letter-spacing: .01em; color: var(--text-primary); margin-bottom: 14px; }
.watching-pattern-examples-label { font-family: var(--font-mono); font-size: 9px; color: var(--text-faint); letter-spacing: .2em; text-transform: uppercase; margin-bottom: 8px; }
.watching-pattern-examples { display: grid; gap: 6px; }
.watching-pattern-example { font-family: var(--font-mono); font-size: 11px; color: var(--text-secondary); line-height: 1.55; padding: 6px 10px; background: rgba(8,15,28,0.4); border: 1px solid var(--navy-edge); border-radius: 2px; }
.watching-pattern-example a { color: var(--blue-light); text-decoration: none; }
.watching-pattern-example a:hover { color: var(--text-primary); }
@media (max-width: 768px) {
  .watching-theater-grid { grid-template-columns: 1fr; }
  .watching-rail { max-height: 420px; }
  .watching-stats-strip { grid-template-columns: 1fr 1fr; }
  .watching-stat:nth-child(2) { border-right: none; }
  .watching-stage-filter { grid-template-columns: 1fr 1fr; }
}
@media print {
  @page { margin: 0; size: A4; }
  .section-header { page-break-after: avoid; break-after: avoid; }
  .stage-header { page-break-after: avoid; break-after: avoid; }
  .stage-block { page-break-inside: avoid; break-inside: avoid; }
  .gap-item { page-break-inside: avoid; break-inside: avoid; }
  .watching-feature, .watching-rail, .watching-pattern-card { page-break-inside: avoid; break-inside: avoid; }
  .wrap { padding-bottom: 0; }
}
</style>
</head>
<body>
${generateWhiteLabelStyle(report)}
${generateNavBar(report.brand_name, "signals", uuid, report.white_label)}
<div class="wrap">

<div class="cover">
  <div class="cover-eyebrow">Ntentify · Semantic Intent Engine · Customer Signals Report</div>
  <div class="cover-title">CUSTOMER SIGNALS</div>
  <div class="cover-subtitle">What Customers Are Watching</div>
  <div class="cover-brand">${s(report.brand_name)}</div>
  <div class="cover-meta">
    <div>${s(report.website)}</div>
    <div>${s(generated)}</div>
  </div>
</div>

${vocHTML ? `
<div class="report-section">
  <div class="section-header">
    <div class="section-eyebrow">Section 01</div>
    <div class="section-title">VOICE OF CUSTOMER</div>
    <div class="section-desc">Real customer language — grouped by where they are in the decision journey</div>
  </div>
  ${vocHTML}
</div>` : ""}

${watchingHTML ? `
<div class="report-section">
  <div class="section-header">
    <div class="section-eyebrow">Section 02</div>
    <div class="section-title">WHAT CUSTOMERS ARE WATCHING</div>
    <div class="section-desc">Video content customers consume before and after buying — mapped across the funnel</div>
  </div>
  ${watchingHTML}
</div>` : ""}

${gapsHTML ? `
<div class="report-section">
  <div class="section-header">
    <div class="section-eyebrow">Section 03</div>
    <div class="section-title">CONTENT GAPS</div>
    <div class="section-desc">Where existing content is weak — and where you can win</div>
  </div>
  <div class="gaps-list">${gapsHTML}</div>
</div>` : ""}

<div class="footer">
  <div class="footer-text">
    Generated by Semantic Intent Engine · ${s(report.brand_name)} · ${s(generated)}<br>
    Derived from real customer signal data and video comments and viewing trends.
  </div>
</div>

</div>
</body>
</html>`;
}

export function generatePrePurchaseIntelligenceReportHTML(report, uuid) {
  const s = (v) => String(v || "").replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;").replace(/"/g,"&quot;");
  const ns = (v) => s(normalizeText(String(v || "")));
  const sl = (v) => Array.isArray(v) ? v : [];
  const generated = report.generated_at ? new Date(report.generated_at).toLocaleString() : new Date().toLocaleString();

  const ppi = report.pre_purchase_intel || {};
  const ss = report.search_signals || {};

  const sec1 = ppi.section_1_question_map || {};
  const sec2 = ppi.section_2_doubt_density || {};
  const sec3 = ppi.section_3_brand_vs_category || {};
  const sec4 = ppi.section_4_unanswered_questions || {};
  const sec5 = ppi.section_5_competitor_surface || {};
  const sec6 = ppi.section_6_strategic_verdict || {};

  // ─── SECTION 1 — Question Map ─────────────────────────────────────────────
  const intentBreakdown = sl(sec1.intent_breakdown);
  const intentBreakdownHTML = intentBreakdown.map((item, i) => `
<div class="intent-item">
  <div class="intent-num">${String(i + 1).padStart(2, "0")}</div>
  <div class="intent-body">
    <div class="intent-header">
      <div class="intent-category">${ns(item.intent_category)}</div>
      <div class="intent-stage">${ns(item.stage)}</div>
    </div>
    <div class="intent-count">${item.question_count || 0} ${item.question_count === 1 ? "question" : "questions"}</div>
    <div class="intent-reveals">${ns(item.what_it_reveals)}</div>
  </div>
</div>`).join("");

  const section1HTML = sec1.headline || sec1.narrative ? `
<div class="report-section">
  <div class="section-header">
    <div class="section-eyebrow">Section 01</div>
    <div class="section-title">PRE-PURCHASE QUESTION MAP</div>
    <div class="section-desc">What prospects are asking before they encounter the brand</div>
  </div>
  ${sec1.headline ? `<div class="section-headline">${ns(sec1.headline)}</div>` : ""}
  ${sec1.narrative ? `<div class="section-narrative">${ns(sec1.narrative).split("\n\n").map(p => `<p>${p}</p>`).join("")}</div>` : ""}
  ${intentBreakdown.length ? `<div class="intent-list">${intentBreakdownHTML}</div>` : ""}
  ${sec1.dominant_concern ? `
  <div class="dominant-concern">
    <div class="dominant-label">Dominant pre-purchase concern</div>
    <div class="dominant-text">${ns(sec1.dominant_concern)}</div>
  </div>` : ""}
</div>` : "";

  // ─── SECTION 2 — Doubt Density ────────────────────────────────────────────
  const doubtScore = sec2.score ?? 0;
  const doubtLabel = sec2.label || "low";
  const sampleDoubtQuestions = sl(sec2.sample_doubt_questions);
  const doubtSampleHTML = sampleDoubtQuestions.map(q => `
<div class="doubt-quote">"${ns(q)}"</div>`).join("");

  const section2HTML = `
<div class="report-section">
  <div class="section-header">
    <div class="section-eyebrow">Section 02</div>
    <div class="section-title">DOUBT DENSITY</div>
    <div class="section-desc">How much of pre-purchase search carries skepticism</div>
  </div>
  <div class="doubt-score-block">
    <div class="doubt-score-num">${doubtScore}<span class="doubt-pct">%</span></div>
    <div class="doubt-score-label doubt-${doubtLabel}">${s(doubtLabel.toUpperCase())}</div>
  </div>
  ${sec2.headline ? `<div class="section-headline">${ns(sec2.headline)}</div>` : ""}
  ${sec2.interpretation ? `<div class="section-narrative"><p>${ns(sec2.interpretation)}</p></div>` : ""}
  ${sampleDoubtQuestions.length ? `
  <div class="doubt-samples">
    <div class="doubt-samples-label">Doubt-shaped questions in this brand's search surface</div>
    ${doubtSampleHTML}
  </div>` : ""}
</div>`;

  // ─── SECTION 3 — Brand vs Category ────────────────────────────────────────
  const brandCount = sec3.brand_count ?? 0;
  const categoryCount = sec3.category_count ?? 0;
  const totalRatio = brandCount + categoryCount;
  const brandPct = totalRatio > 0 ? Math.round((brandCount / totalRatio) * 100) : 0;
  const categoryPct = totalRatio > 0 ? 100 - brandPct : 0;

  const section3HTML = `
<div class="report-section">
  <div class="section-header">
    <div class="section-eyebrow">Section 03</div>
    <div class="section-title">BRAND VS CATEGORY SEARCH</div>
    <div class="section-desc">Whether prospects search the brand by name or the category problem</div>
  </div>
  <div class="ratio-block">
    <div class="ratio-bar">
      <div class="ratio-fill ratio-brand" style="width: ${brandPct}%">
        <div class="ratio-fill-label">${brandPct}% Brand</div>
      </div>
      <div class="ratio-fill ratio-category" style="width: ${categoryPct}%">
        <div class="ratio-fill-label">${categoryPct}% Category</div>
      </div>
    </div>
    <div class="ratio-counts">
      <div class="ratio-count"><span class="ratio-count-num">${brandCount}</span> brand-named questions</div>
      <div class="ratio-count"><span class="ratio-count-num">${categoryCount}</span> category-only questions</div>
    </div>
  </div>
  ${sec3.headline ? `<div class="section-headline">${ns(sec3.headline)}</div>` : ""}
  ${sec3.interpretation ? `<div class="section-narrative"><p>${ns(sec3.interpretation)}</p></div>` : ""}
  ${sec3.strategic_implication ? `
  <div class="strategic-implication">
    <div class="implication-label">Strategic implication</div>
    <div class="implication-text">${ns(sec3.strategic_implication)}</div>
  </div>` : ""}
</div>`;

  // ─── SECTION 4 — Unanswered Questions ─────────────────────────────────────
  const unansweredQs = sl(sec4.questions);
  const unansweredHTML = unansweredQs.map((q, i) => `
<div class="unanswered-item">
  <div class="unanswered-num">${String(i + 1).padStart(2, "0")}</div>
  <div class="unanswered-body">
    <div class="unanswered-question">"${ns(q.question)}"</div>
    <div class="unanswered-meta">
      <span class="unanswered-intent">${ns(q.intent_category)}</span>
    </div>
    ${q.why_it_matters ? `<div class="unanswered-why">${ns(q.why_it_matters)}</div>` : ""}
    ${q.suggested_content_type ? `
    <div class="unanswered-suggestion">
      <span class="suggestion-label">Suggested content:</span> ${ns(q.suggested_content_type)}
    </div>` : ""}
  </div>
</div>`).join("");

  const section4HTML = `
<div class="report-section">
  <div class="section-header">
    <div class="section-eyebrow">Section 04</div>
    <div class="section-title">UNANSWERED QUESTIONS</div>
    <div class="section-desc">Pre-purchase questions the brand's website does not visibly address</div>
  </div>
  ${sec4.headline ? `<div class="section-headline">${ns(sec4.headline)}</div>` : ""}
  ${sec4.narrative ? `<div class="section-narrative"><p>${ns(sec4.narrative)}</p></div>` : ""}
  ${unansweredQs.length ? `<div class="unanswered-list">${unansweredHTML}</div>` : `
  <div class="empty-state">No unanswered question gaps detected — brand website coverage is broad relative to pre-purchase search.</div>`}
</div>`;

  // ─── SECTION 5 — Competitor Surface ───────────────────────────────────────
  const competitorQs = sl(sec5.competitor_questions);
  const competitorHTML = competitorQs.map((q, i) => `
<div class="competitor-item">
  <div class="competitor-num">${String(i + 1).padStart(2, "0")}</div>
  <div class="competitor-body">
    <div class="competitor-question">"${ns(q.question)}"</div>
    ${q.competitor ? `<div class="competitor-name">vs. ${ns(q.competitor)}</div>` : ""}
    ${q.what_it_reveals ? `<div class="competitor-reveals">${ns(q.what_it_reveals)}</div>` : ""}
  </div>
</div>`).join("");

  const section5HTML = `
<div class="report-section">
  <div class="section-header">
    <div class="section-eyebrow">Section 05</div>
    <div class="section-title">COMPETITIVE COMPARISON SURFACE</div>
    <div class="section-desc">Where prospects compare this brand against alternatives in pre-purchase search</div>
  </div>
  ${sec5.headline ? `<div class="section-headline">${ns(sec5.headline)}</div>` : ""}
  ${sec5.narrative ? `<div class="section-narrative"><p>${ns(sec5.narrative)}</p></div>` : ""}
  ${competitorQs.length ? `<div class="competitor-list">${competitorHTML}</div>` : `
  <div class="empty-state">No direct competitor references detected in pre-purchase search. Buyers are not yet comparing this brand against named rivals at the search stage.</div>`}
</div>`;

  // ─── SECTION 6 — Strategic Verdict ────────────────────────────────────────
  const section6HTML = `
<div class="report-section">
  <div class="section-header">
    <div class="section-eyebrow">Section 06</div>
    <div class="section-title">STRATEGIC VERDICT</div>
    <div class="section-desc">What pre-purchase intent reveals about the brand's funnel position</div>
  </div>
  ${sec6.headline ? `<div class="section-headline">${ns(sec6.headline)}</div>` : ""}
  ${sec6.verdict ? `<div class="section-narrative verdict-narrative"><p>${ns(sec6.verdict)}</p></div>` : ""}
  ${sec6.tied_to_other_reports ? `
  <div class="tied-reports">
    <div class="tied-label">How this connects to the rest of the engagement</div>
    <div class="tied-text">${ns(sec6.tied_to_other_reports)}</div>
  </div>` : ""}
</div>`;

  // ─── Empty-state guard ────────────────────────────────────────────────────
  const hasContent = sec1.headline || sec1.narrative || sec2.headline || sec3.headline || sec4.headline || sec5.headline || sec6.headline;

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>${s(report.brand_name)} — Pre-Purchase Intel Report</title>
<link href="https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Mono:wght@300;400;500&family=Inter:wght@300;400;500;600;700&display=swap" rel="stylesheet">
<style>
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
:root {
  --navy-deep: #080f1c; --navy-base: #0d1526; --navy-raised: #111d33;
  --navy-surface: #162040; --navy-edge: #1e2f55; --navy-mid: #243568;
  --blue: #2563eb; --blue-bright: #3b82f6; --blue-light: #60a5fa;
  --blue-dim: rgba(37,99,235,0.1);
  --text-primary: #f0f4ff; --text-secondary: #a8c0e8;
  --text-muted: #7a9cc8; --text-faint: #4a6a99;
  --light-bg: #eef2fa; --light-surface: #f6f8fd;
  --light-border: #d0daf0; --light-text: #0d1526; --light-muted: #4a5f8a;
  --font-disp: 'Bebas Neue', sans-serif;
  --font-body: 'Inter', 'Helvetica Neue', Arial, sans-serif;
  --font-mono: 'DM Mono', monospace;
  --pad: 48px;
}
html, body { background: var(--light-bg); color: var(--light-text); font-family: var(--font-body); font-size: 14px; line-height: 1.6; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.wrap { max-width: 860px; margin: 0 auto; padding-bottom: 80px; }

.cover { background: var(--navy-deep); color: var(--text-primary); padding: 52px var(--pad) 44px; position: relative; overflow: hidden; }
.cover::after { content: ''; position: absolute; top: 0; right: 0; width: 400px; height: 100%; background: radial-gradient(ellipse at 80% 50%, rgba(37,99,235,0.12) 0%, transparent 70%); pointer-events: none; }
.cover-eyebrow { font-family: var(--font-mono); font-size: 12px; letter-spacing: .2em; text-transform: uppercase; color: var(--text-muted); margin-bottom: 14px; position: relative; }
.cover-title { font-family: var(--font-disp); font-size: 56px; line-height: 1; letter-spacing: -.01em; color: var(--text-primary); position: relative; }
.cover-subtitle { font-family: var(--font-disp); font-size: 28px; letter-spacing: .02em; color: var(--blue-light); margin: 4px 0 32px; position: relative; }
.cover-brand { font-family: var(--font-disp); font-size: 36px; letter-spacing: .02em; color: var(--blue-light); margin: 0 0 32px; position: relative; }
.cover-meta { font-family: var(--font-mono); font-size: 12px; color: var(--text-muted); border-top: 1px solid var(--navy-edge); padding-top: 20px; display: flex; gap: 36px; flex-wrap: wrap; position: relative; }

.report-section { border-bottom: 2px solid var(--navy-base); }
.report-section:last-child { border-bottom: none; }
.section-header { background: var(--navy-raised); padding: 20px var(--pad); border-bottom: 1px solid var(--navy-edge); }
.section-eyebrow { font-family: var(--font-mono); font-size: 12px; letter-spacing: .2em; text-transform: uppercase; color: var(--text-muted); margin-bottom: 6px; }
.section-title { font-family: var(--font-disp); font-size: 36px; letter-spacing: -.01em; color: var(--text-primary); line-height: 1; }
.section-desc { font-family: var(--font-mono); font-size: 12px; color: var(--text-muted); margin-top: 6px; letter-spacing: .06em; }

.section-headline { font-family: var(--font-disp); font-size: 22px; letter-spacing: .015em; color: var(--light-text); padding: 24px var(--pad) 8px; line-height: 1.25; background: var(--light-surface); }
.section-narrative { padding: 8px var(--pad) 24px; background: var(--light-surface); }
.section-narrative p { font-size: 14px; color: var(--light-text); line-height: 1.7; margin-bottom: 14px; }
.section-narrative p:last-child { margin-bottom: 0; }
.verdict-narrative p { font-size: 15px; }

/* SECTION 1 — Intent Breakdown */
.intent-list { background: var(--light-bg); border-top: 1px solid var(--light-border); }
.intent-item { display: grid; grid-template-columns: 48px 1fr; gap: 16px; align-items: start; padding: 18px var(--pad); border-bottom: 1px solid var(--light-border); }
.intent-item:last-child { border-bottom: none; }
.intent-num { font-family: var(--font-disp); font-size: 32px; color: var(--light-border); line-height: 1; }
.intent-header { display: flex; gap: 14px; align-items: center; margin-bottom: 4px; flex-wrap: wrap; }
.intent-category { font-family: var(--font-disp); font-size: 22px; letter-spacing: .015em; color: var(--light-text); line-height: 1; }
.intent-stage { font-family: var(--font-mono); font-size: 10px; letter-spacing: .14em; text-transform: uppercase; padding: 4px 10px; background: var(--blue-dim); color: var(--blue); border-radius: 2px; white-space: nowrap; }
.intent-count { font-family: var(--font-mono); font-size: 11px; letter-spacing: .1em; text-transform: uppercase; color: var(--light-muted); margin-bottom: 6px; }
.intent-reveals { font-size: 13px; color: var(--light-muted); line-height: 1.65; }

.dominant-concern { padding: 16px var(--pad); background: var(--light-bg); border-top: 1px solid var(--light-border); }
.dominant-label { font-family: var(--font-mono); font-size: 10px; letter-spacing: .16em; text-transform: uppercase; color: var(--blue); margin-bottom: 6px; }
.dominant-text { font-size: 13px; color: var(--light-text); line-height: 1.6; }

/* SECTION 2 — Doubt Density */
.doubt-score-block { display: flex; align-items: baseline; gap: 18px; padding: 28px var(--pad) 20px; background: var(--light-surface); border-bottom: 1px solid var(--light-border); }
.doubt-score-num { font-family: var(--font-disp); font-size: 96px; line-height: 1; letter-spacing: -.02em; color: var(--blue); }
.doubt-pct { font-size: 48px; color: var(--blue-light); margin-left: 4px; }
.doubt-score-label { font-family: var(--font-mono); font-size: 13px; letter-spacing: .2em; padding: 6px 14px; border-radius: 2px; }
.doubt-low { background: var(--blue-dim); color: var(--blue); }
.doubt-moderate { background: rgba(245,158,11,0.12); color: #f59e0b; }
.doubt-high { background: rgba(239,68,68,0.12); color: #ef4444; }

.doubt-samples { padding: 20px var(--pad); background: var(--light-bg); border-top: 1px solid var(--light-border); }
.doubt-samples-label { font-family: var(--font-mono); font-size: 10px; letter-spacing: .16em; text-transform: uppercase; color: var(--blue); margin-bottom: 12px; }
.doubt-quote { font-family: var(--font-mono); font-size: 13px; font-style: italic; color: var(--light-text); padding: 10px 14px; background: var(--light-surface); border: 1px solid var(--light-border); margin-bottom: 6px; line-height: 1.5; }
.doubt-quote:last-child { margin-bottom: 0; }

/* SECTION 3 — Brand vs Category */
.ratio-block { padding: 28px var(--pad) 20px; background: var(--light-surface); border-bottom: 1px solid var(--light-border); }
.ratio-bar { display: flex; height: 56px; border: 1px solid var(--light-border); margin-bottom: 14px; overflow: hidden; }
.ratio-fill { display: flex; align-items: center; justify-content: center; font-family: var(--font-mono); font-size: 12px; letter-spacing: .12em; text-transform: uppercase; transition: width 0.3s ease; }
.ratio-brand { background: var(--blue); color: #fff; }
.ratio-category { background: var(--blue-dim); color: var(--blue); }
.ratio-fill-label { padding: 0 8px; white-space: nowrap; }
.ratio-counts { display: flex; justify-content: space-between; gap: 24px; font-family: var(--font-mono); font-size: 12px; color: var(--light-muted); }
.ratio-count-num { font-family: var(--font-disp); font-size: 24px; color: var(--light-text); margin-right: 6px; }

.strategic-implication { padding: 16px var(--pad); background: var(--light-bg); border-top: 1px solid var(--light-border); }
.implication-label { font-family: var(--font-mono); font-size: 10px; letter-spacing: .16em; text-transform: uppercase; color: var(--blue); margin-bottom: 6px; }
.implication-text { font-size: 13px; color: var(--light-text); line-height: 1.65; }

/* SECTION 4 — Unanswered Questions */
.unanswered-list { background: var(--light-surface); border-top: 1px solid var(--light-border); }
.unanswered-item { display: grid; grid-template-columns: 40px 1fr; gap: 16px; align-items: start; padding: 20px var(--pad); border-bottom: 1px solid var(--light-border); }
.unanswered-item:last-child { border-bottom: none; }
.unanswered-num { font-family: var(--font-disp); font-size: 28px; color: var(--blue); line-height: 1; margin-top: 2px; }
.unanswered-question { font-family: var(--font-mono); font-size: 14px; font-style: italic; color: var(--light-text); line-height: 1.5; margin-bottom: 8px; }
.unanswered-meta { margin-bottom: 8px; }
.unanswered-intent { font-family: var(--font-mono); font-size: 10px; letter-spacing: .14em; text-transform: uppercase; padding: 4px 10px; background: var(--blue-dim); color: var(--blue); border-radius: 2px; }
.unanswered-why { font-size: 13px; color: var(--light-muted); line-height: 1.65; margin-bottom: 8px; }
.unanswered-suggestion { font-size: 12px; color: var(--light-text); line-height: 1.5; padding: 8px 12px; background: var(--light-bg); border-left: 3px solid var(--blue); }
.suggestion-label { font-family: var(--font-mono); font-size: 10px; letter-spacing: .14em; text-transform: uppercase; color: var(--blue); margin-right: 6px; }

.empty-state { padding: 24px var(--pad); background: var(--light-surface); font-family: var(--font-mono); font-size: 12px; color: var(--light-muted); font-style: italic; line-height: 1.6; }

/* SECTION 5 — Competitor Surface */
.competitor-list { background: var(--light-surface); border-top: 1px solid var(--light-border); }
.competitor-item { display: grid; grid-template-columns: 40px 1fr; gap: 16px; align-items: start; padding: 20px var(--pad); border-bottom: 1px solid var(--light-border); }
.competitor-item:last-child { border-bottom: none; }
.competitor-num { font-family: var(--font-disp); font-size: 28px; color: var(--blue); line-height: 1; margin-top: 2px; }
.competitor-question { font-family: var(--font-mono); font-size: 14px; font-style: italic; color: var(--light-text); line-height: 1.5; margin-bottom: 6px; }
.competitor-name { font-family: var(--font-disp); font-size: 18px; letter-spacing: .02em; color: var(--blue); margin-bottom: 8px; }
.competitor-reveals { font-size: 13px; color: var(--light-muted); line-height: 1.65; }

/* SECTION 6 — Strategic Verdict */
.tied-reports { padding: 20px var(--pad); background: var(--blue-dim); border-top: 1px solid var(--light-border); }
.tied-label { font-family: var(--font-mono); font-size: 10px; letter-spacing: .16em; text-transform: uppercase; color: var(--blue); margin-bottom: 8px; }
.tied-text { font-size: 13px; color: var(--light-text); line-height: 1.65; }

.footer { padding: 28px var(--pad); background: var(--navy-base); border-top: 2px solid var(--navy-edge); }
.footer-text { font-family: var(--font-mono); font-size: 12px; color: var(--text-faint); letter-spacing: .1em; line-height: 1.6; }

@media print {
  @page { margin: 0; size: A4; }
  .section-header { page-break-after: avoid; break-after: avoid; }
  .intent-item { page-break-inside: avoid; break-inside: avoid; }
  .unanswered-item { page-break-inside: avoid; break-inside: avoid; }
  .competitor-item { page-break-inside: avoid; break-inside: avoid; }
  .doubt-score-block { page-break-inside: avoid; break-inside: avoid; }
  .ratio-block { page-break-inside: avoid; break-inside: avoid; }
  .wrap { padding-bottom: 0; }
}
</style>
</head>
<body>
${generateWhiteLabelStyle(report)}
${generateNavBar(report.brand_name, "pre-purchase", uuid, report.white_label)}
<div class="wrap">

<div class="cover">
  <div class="cover-eyebrow">Ntentify · Semantic Intent Engine · Pre-Purchase Intel Report</div>
  <div class="cover-title">PRE-PURCHASE INTEL</div>
  <div class="cover-brand">${s(report.brand_name)}</div>
  <div class="cover-meta">
    <div>${s(report.website)}</div>
    <div>${s(generated)}</div>
    ${ss.total_questions ? `<div>${ss.total_questions} pre-purchase questions analyzed</div>` : ""}
  </div>
</div>

${hasContent ? `
${section1HTML}
${section2HTML}
${section3HTML}
${section4HTML}
${section5HTML}
${section6HTML}
` : `
<div class="report-section">
  <div class="section-header">
    <div class="section-eyebrow">Pre-Purchase Intel</div>
    <div class="section-title">DATA UNAVAILABLE</div>
    <div class="section-desc">Insufficient pre-purchase search activity for this brand</div>
  </div>
  <div class="empty-state">Pre-purchase search demand was not measurable in this run. Refer to Customer Signals and Market Signal Trends for the post-experience customer voice analysis driving the rest of this engagement.</div>
</div>
`}

<div class="footer">
  <div class="footer-text">
    Generated by Semantic Intent Engine · ${s(report.brand_name)} · ${s(generated)}<br>
    Derived from the questions people search and pre-purchase search intent analysis.
  </div>
</div>

</div>
</body>
</html>`;
}

// ─── PORTAL HTML ──────────────────────────────────────────────────────────────

export function generatePortalHTML(report, uuid) {
  const s = (v) => String(v || "").replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;").replace(/"/g,"&quot;");
  const ns = (v) => s(normalizeText(String(v || "")));
  const sl = (v) => Array.isArray(v) ? v : [];
  const generated = report.generated_at ? new Date(report.generated_at).toLocaleString() : new Date().toLocaleString();
  const coverage = report.signal_coverage || {};

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>${s(report.brand_name)} — Intelligence Report</title>
<link href="https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Mono:wght@300;400;500&family=Inter:wght@300;400;500;600;700&display=swap" rel="stylesheet">
<style>
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
:root {
  --navy-deep: #080f1c; --navy-base: #0d1526; --navy-raised: #111d33;
  --navy-surface: #162040; --navy-edge: #1e2f55; --navy-mid: #243568;
  --blue: #2563eb; --blue-bright: #3b82f6; --blue-light: #60a5fa;
  --blue-dim: rgba(37,99,235,0.1);
  --text-primary: #f0f4ff; --text-secondary: #a8c0e8;
  --text-muted: #7a9cc8; --text-faint: #4a6a99;
  --light-bg: #eef2fa; --light-surface: #f6f8fd;
  --light-border: #d0daf0; --light-text: #0d1526; --light-muted: #4a5f8a;
  --font-disp: 'Bebas Neue', sans-serif;
  --font-body: 'Inter', 'Helvetica Neue', Arial, sans-serif;
  --font-mono: 'DM Mono', monospace;
  --pad: 48px;
}
html, body { background: var(--light-bg); color: var(--light-text); font-family: var(--font-body); font-size: 15px; line-height: 1.6; }
.wrap { max-width: 780px; margin: 0 auto; padding-bottom: 80px; }
.cover { background: var(--navy-deep); color: var(--text-primary); padding: 56px var(--pad) 48px; position: relative; overflow: hidden; }
.cover::after { content: ''; position: absolute; top: 0; right: 0; width: 400px; height: 100%; background: radial-gradient(ellipse at 80% 50%, rgba(37,99,235,0.15) 0%, transparent 70%); pointer-events: none; }
.cover-eyebrow { font-family: var(--font-mono); font-size: 11px; letter-spacing: .2em; text-transform: uppercase; color: var(--text-muted); margin-bottom: 16px; position: relative; }
.cover-brand { font-family: var(--font-disp); font-size: 56px; line-height: 1; letter-spacing: -.01em; color: var(--text-primary); position: relative; margin-bottom: 6px; }
.cover-label { font-family: var(--font-disp); font-size: 24px; letter-spacing: .04em; color: var(--blue-light); position: relative; margin-bottom: 36px; }
.cover-verdict { position: relative; border-top: 1px solid var(--navy-edge); padding-top: 28px; }
.cover-verdict-label { font-family: var(--font-mono); font-size: 11px; letter-spacing: .2em; text-transform: uppercase; color: var(--blue-light); margin-bottom: 12px; }
.cover-verdict-text { font-size: 22px; line-height: 1.35; color: var(--text-primary); font-weight: 400; max-width: 600px; }
.cover-meta { position: relative; margin-top: 36px; font-family: var(--font-mono); font-size: 11px; color: var(--text-faint); }
.stats-strip { background: var(--navy-raised); border-bottom: 1px solid var(--navy-edge); padding: 20px var(--pad); display: flex; gap: 40px; flex-wrap: wrap; }
.stat-item { font-family: var(--font-mono); font-size: 11px; color: var(--text-muted); letter-spacing: .1em; display: flex; flex-direction: column; }
.stat-item .stat-label { min-height: 28px; display: flex; align-items: flex-end; margin-bottom: 4px; }
.stat-item strong { display: block; font-family: var(--font-disp); font-size: 32px; color: var(--text-primary); line-height: 1; margin-bottom: 2px; letter-spacing: 0; }
.reports-section { padding: 40px var(--pad); background: var(--navy-base); border-bottom: 2px solid var(--navy-edge); }
.reports-label { font-family: var(--font-mono); font-size: 11px; letter-spacing: .2em; text-transform: uppercase; color: var(--text-muted); margin-bottom: 8px; }
.reports-title { font-family: var(--font-disp); font-size: 28px; letter-spacing: .02em; color: var(--text-primary); margin-bottom: 24px; line-height: 1; }
.report-cards { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.report-card { background: var(--navy-raised); border: 1px solid var(--navy-edge); padding: 20px 22px; text-decoration: none; display: block; transition: background 0.15s, border-color 0.15s; }
.report-card:hover { background: var(--navy-surface); border-color: var(--blue); }
.report-card-eyebrow { font-family: var(--font-mono); font-size: 10px; letter-spacing: .16em; text-transform: uppercase; color: var(--blue-light); margin-bottom: 8px; }
.report-card-title { font-family: var(--font-disp); font-size: 20px; letter-spacing: .02em; color: var(--text-primary); line-height: 1; margin-bottom: 8px; }
.report-card-desc { font-size: 13px; color: var(--text-muted); line-height: 1.5; }
.report-card-link { font-family: var(--font-mono); font-size: 10px; letter-spacing: .12em; text-transform: uppercase; color: var(--blue-light); margin-top: 14px; display: block; }
.how-section { padding: 40px var(--pad); background: var(--light-surface); border-bottom: 1px solid var(--light-border); }
.how-label { font-family: var(--font-mono); font-size: 11px; letter-spacing: .2em; text-transform: uppercase; color: var(--blue); margin-bottom: 8px; }
.how-title { font-family: var(--font-disp); font-size: 28px; letter-spacing: .02em; color: var(--light-text); margin-bottom: 8px; line-height: 1; }
.how-intro { font-size: 14px; line-height: 1.7; color: var(--light-muted); margin-bottom: 24px; max-width: 580px; }
.how-steps { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.how-step { background: var(--light-bg); border: 1px solid var(--light-border); padding: 16px 18px; }
.how-step-num { font-family: var(--font-disp); font-size: 32px; color: var(--blue); line-height: 1; margin-bottom: 6px; opacity: 0.4; }
.how-step-title { font-family: var(--font-mono); font-size: 11px; letter-spacing: .12em; text-transform: uppercase; color: var(--light-text); margin-bottom: 6px; font-weight: 500; }
.how-step-desc { font-size: 13px; color: var(--light-muted); line-height: 1.55; }
.section { padding: 40px var(--pad); border-bottom: 1px solid var(--light-border); background: var(--light-surface); }
.section-label { font-family: var(--font-mono); font-size: 11px; letter-spacing: .2em; text-transform: uppercase; color: var(--blue); margin-bottom: 16px; }
.section-title { font-family: var(--font-disp); font-size: 28px; letter-spacing: .02em; color: var(--light-text); margin-bottom: 16px; line-height: 1; }
.summary-text { font-size: 16px; line-height: 1.75; color: var(--light-text); max-width: 640px; }
.means-list { display: grid; gap: 12px; margin-top: 4px; }
.means-item { padding: 16px 20px; background: var(--light-bg); border: 1px solid var(--light-border); border-left: 3px solid var(--blue); font-size: 14px; line-height: 1.65; color: var(--light-text); }
.vc-section { padding: 32px var(--pad); border-bottom: 1px solid var(--light-border); background: var(--light-bg); }
.vc-header { display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 4px; gap: 16px; flex-wrap: wrap; }
.vc-label { font-family: var(--font-mono); font-size: 11px; letter-spacing: .2em; text-transform: uppercase; color: var(--blue); }
.vc-context-right { font-family: var(--font-mono); font-size: 11px; color: var(--light-muted); letter-spacing: .04em; }
.vc-context { font-family: var(--font-mono); font-size: 12px; color: var(--light-muted); margin-bottom: 16px; }
.vc-bar { display: flex; height: 14px; border-radius: 3px; overflow: hidden; background: var(--light-border); margin-bottom: 14px; }
.vc-bar-pos { background: #22a06b; transition: width 1.2s cubic-bezier(.4,0,.2,1); }
.vc-bar-mix { background: #94a3b8; transition: width 1.2s cubic-bezier(.4,0,.2,1); }
.vc-bar-neg { background: #d04848; transition: width 1.2s cubic-bezier(.4,0,.2,1); }
.vc-legend { display: flex; gap: 24px; flex-wrap: wrap; font-family: var(--font-mono); font-size: 11px; color: var(--light-text); letter-spacing: .04em; }
.vc-legend-item { display: flex; align-items: center; gap: 8px; }
.vc-dot { width: 10px; height: 10px; border-radius: 50%; display: inline-block; }
.vc-dot-pos { background: #22a06b; }
.vc-dot-mix { background: #94a3b8; }
.vc-dot-neg { background: #d04848; }
.actions-list { display: grid; gap: 12px; margin-top: 4px; }
.action-item { display: grid; grid-template-columns: 32px 1fr; gap: 14px; align-items: start; padding: 16px 20px; background: var(--light-bg); border: 1px solid var(--light-border); }
.action-num { font-family: var(--font-disp); font-size: 26px; color: var(--blue); line-height: 1; margin-top: 2px; }
.action-hook { font-size: 15px; font-weight: 700; color: var(--light-text); line-height: 1.4; margin-bottom: 4px; }
.action-insight { font-size: 13px; color: var(--light-text); line-height: 1.65; margin-top: 6px; }
.action-mechanism { font-size: 13px; color: var(--light-muted); line-height: 1.65; margin-top: 6px; }
.action-consequence { font-size: 13px; color: var(--light-muted); line-height: 1.65; margin-top: 6px; font-style: italic; }
.action-suggestion { margin-top: 10px; padding: 10px 14px; background: var(--blue-dim); border-left: 3px solid var(--blue); font-size: 13px; color: var(--light-text); line-height: 1.55; }
.action-suggestion-label { font-family: var(--font-mono); font-size: 10px; letter-spacing: .14em; text-transform: uppercase; color: var(--blue); display: block; margin-bottom: 4px; }
.action-evidence { margin-top: 10px; padding: 10px 14px; background: var(--light-bg); border: 1px solid var(--light-border); border-top: 2px solid var(--navy-raised); }
.action-evidence-label { font-family: var(--font-mono); font-size: 10px; letter-spacing: .14em; text-transform: uppercase; color: var(--light-muted); display: block; margin-bottom: 8px; }
.action-evidence-list { display: grid; gap: 6px; }
.action-evidence-item { display: grid; grid-template-columns: 1fr auto; gap: 10px; align-items: center; padding: 8px 10px; background: var(--light-surface); border: 1px solid var(--light-border); }
.action-evidence-context { font-size: 12px; color: var(--light-text); line-height: 1.45; }
.action-evidence-link { font-family: var(--font-mono); font-size: 11px; color: var(--blue); text-decoration: none; white-space: nowrap; padding: 4px 8px; border: 1px solid rgba(37,99,235,0.3); background: var(--blue-dim); }
.action-evidence-link:hover { background: var(--blue); color: #fff; }
.footer { padding: 28px var(--pad); background: var(--navy-base); border-top: 2px solid var(--navy-edge); }
.footer-text { font-family: var(--font-mono); font-size: 11px; color: var(--text-faint); letter-spacing: .1em; line-height: 1.6; }
@media (max-width: 600px) {
  .report-cards { grid-template-columns: 1fr; }
  .how-steps { grid-template-columns: 1fr; }
  :root { --pad: 24px; }
}
</style>
</head>
<body>
${generateWhiteLabelStyle(report)}
${generateNavBar(report.brand_name, "portal", uuid, report.white_label)}
<div class="wrap">

<div class="cover">
  <div class="cover-eyebrow">Ntentify · Semantic Intent Engine · Intelligence Report</div>
  <div class="cover-brand">${s(report.brand_name)}</div>
  <div class="cover-label">Customer Signal Summary</div>
  <div class="cover-verdict">
    <div class="cover-verdict-label">Core Finding</div>
    <div class="cover-verdict-text">${ns((report.strategic_verdict || report.signals_summary || "").replace(/^STRATEGIC VERDICT:\s*/i, ""))}</div>
  </div>
  <div class="cover-meta">${s(report.website)} &nbsp;·&nbsp; ${s(generated)}</div>
</div>

${coverage.total_signals_processed ? `
<div class="stats-strip">
  <div class="stat-item"><div class="stat-label">Total Signals</div><strong>${computeTotalSignals(coverage)}</strong></div>
  ${coverage.reviews_analyzed > 0 ? `<div class="stat-item"><div class="stat-label">Reviews Analyzed</div><strong>${coverage.reviews_analyzed}</strong></div>` : ""}
  ${coverage.social_comments_analyzed > 0 ? `<div class="stat-item"><div class="stat-label">Video Comments</div><strong>${coverage.social_comments_analyzed}</strong></div>` : ""}
  ${coverage.google_queries_analyzed > 0 ? `<div class="stat-item"><div class="stat-label">Search Queries</div><strong>${coverage.google_queries_analyzed}</strong></div>` : ""}
</div>` : ""}

<div class="reports-section">
  <div class="reports-label">Your Intelligence Package</div>
  <div class="reports-title">YOUR REPORTS</div>
  <div class="report-cards">
    <a class="report-card" href="customer-signals-report.html" target="_blank" rel="noopener noreferrer">
      <div class="report-card-eyebrow">Report 01</div>
      <div class="report-card-title">CUSTOMER SIGNALS</div>
      <div class="report-card-desc">Real customer language, video comments and viewing trends, and content gaps — organized by stage of the buying journey.</div>
      <div class="report-card-link">Open Report →</div>
    </a>
    <a class="report-card" href="message-gap-report.html" target="_blank" rel="noopener noreferrer">
      <div class="report-card-eyebrow">Report 02</div>
      <div class="report-card-title">MESSAGE GAP REPORT</div>
      <div class="report-card-desc">What customers actually want vs. what marketing is saying — and exactly what to change to close the gap.</div>
      <div class="report-card-link">Open Report →</div>
    </a>
    <a class="report-card" href="ad-angle-report.html" target="_blank" rel="noopener noreferrer">
      <div class="report-card-eyebrow">Report 03</div>
      <div class="report-card-title">AD ANGLE REPORT</div>
      <div class="report-card-desc">Paste-ready hooks grouped by funnel stage. Each angle includes what it addresses, suggested usage, and proof direction.</div>
      <div class="report-card-link">Open Report →</div>
    </a>
    <a class="report-card" href="strategy-report.html" target="_blank" rel="noopener noreferrer">
      <div class="report-card-eyebrow">Report 04</div>
      <div class="report-card-title">FULL STRATEGY REPORT</div>
      <div class="report-card-desc">The complete intelligence report. All signal clusters, brand positioning analysis, campaign diagnosis, and strategic recommendations.</div>
      <div class="report-card-link">Open Report →</div>
    </a>
    <a class="report-card" href="executive-summary.html" target="_blank" rel="noopener noreferrer" style="grid-column: 1 / -1;">
      <div class="report-card-eyebrow">Report 05</div>
      <div class="report-card-title">EXECUTIVE SUMMARY</div>
      <div class="report-card-desc">Signal diagnostic dashboard. Total signals, stage distribution, sentiment meters, source breakdown, and severity heat — with a Claude-generated system verdict.</div>
      <div class="report-card-link">Open Dashboard →</div>
    </a>
  </div>
</div>

<div class="how-section">
  <div class="how-label">How This Works</div>
  <div class="how-title">THE SIGNAL IS THE STRATEGY</div>
  <div class="how-intro">This report was built by the Semantic Intent Engine — a system that analyzes real customer signals and converts them into direct response strategy. No surveys. No assumptions. Real data from people who searched, reviewed, and watched content in your category.</div>
  <div class="how-steps">
    <div class="how-step"><div class="how-step-num">01</div><div class="how-step-title">Signal Collection</div><div class="how-step-desc">We pull the questions people search, retail and review-site reviews, video comments, and public evidence from your category — hundreds of signals per run.</div></div>
    <div class="how-step"><div class="how-step-num">02</div><div class="how-step-title">Intent Mapping</div><div class="how-step-desc">Signals are mapped across four stages of the customer journey: Problem Awareness, Frustration, Solution Exploration, and Decision.</div></div>
    <div class="how-step"><div class="how-step-num">03</div><div class="how-step-title">Gap Analysis</div><div class="how-step-desc">We identify where your marketing is misaligned with what customers actually need — surfacing the exact messaging gaps hurting performance.</div></div>
    <div class="how-step"><div class="how-step-num">04</div><div class="how-step-title">Strategy Output</div><div class="how-step-desc">Everything converts into paste-ready ad angles, messaging shifts, and strategic recommendations your team can act on immediately.</div></div>
  </div>
</div>

${report.signals_summary ? `
<div class="section">
  <div class="section-label">Summary</div>
  <div class="section-title">What The Signals Show</div>
  <div class="summary-text">${ns(report.signals_summary)}</div>
</div>` : ""}

${(() => {
  const pb = report.signal_polarity_breakdown;
  if (!pb || typeof pb !== "object") return "";
  const pos = pb.positive || 0;
  const mix = pb.mixed || 0;
  const neg = pb.negative || 0;
  const total = pos + mix + neg;
  if (total === 0) return "";
  const posPct = Math.round((pos / total) * 100);
  const negPct = Math.round((neg / total) * 100);
  const mixPct = 100 - posPct - negPct;
  const cov = report.signal_coverage || {};
  const reviewsCount = cov.reviews_analyzed || 0;
  const queriesCount = cov.google_queries_analyzed || 0;
  const contextRight = (reviewsCount > 0 || queriesCount > 0) ? `${reviewsCount > 0 ? `${reviewsCount} reviews` : ""}${reviewsCount > 0 && queriesCount > 0 ? " · " : ""}${queriesCount > 0 ? `${queriesCount} queries` : ""}` : "";
  return `
<div class="section vc-section">
  <div class="vc-header">
    <div class="vc-label">Voice Composition</div>
    <div class="vc-context-right">${s(contextRight)}</div>
  </div>
  <div class="vc-context">Across ${total} classified signals</div>
  <div class="vc-bar">
    <div class="vc-bar-pos" style="width:${posPct}%"></div>
    <div class="vc-bar-mix" style="width:${mixPct}%"></div>
    <div class="vc-bar-neg" style="width:${negPct}%"></div>
  </div>
  <div class="vc-legend">
    <div class="vc-legend-item"><span class="vc-dot vc-dot-pos"></span>${posPct}% Positive</div>
    <div class="vc-legend-item"><span class="vc-dot vc-dot-mix"></span>${mixPct}% Mixed</div>
    <div class="vc-legend-item"><span class="vc-dot vc-dot-neg"></span>${negPct}% Negative</div>
  </div>
</div>`;
})()}

${sl(report.portal_implications).length ? `
<div class="section">
  <div class="section-label">Implications</div>
  <div class="section-title">What This Means</div>
  <div class="means-list">
    ${sl(report.portal_implications).slice(0, 3).map(item => `<div class="means-item">${ns(item)}</div>`).join("")}
  </div>
</div>` : ""}

${sl(report.portal_actions).length ? `
<div class="section">
  <div class="section-label">Priority Actions</div>
  <div class="section-title">What To Do Next</div>
  <div class="actions-list">
    ${sl(report.portal_actions).slice(0, 3).map((a, i) => `
    <div class="action-item">
      <div class="action-num">${i + 1}</div>
      <div>
        <div class="action-hook">${ns(a.headline)}</div>
        <div class="action-insight">${ns(a.insight)}</div>
        <div class="action-mechanism">${ns(a.mechanism)}</div>
        <div class="action-consequence">${ns(a.consequence)}</div>
        ${a.suggestion ? `<div class="action-suggestion"><span class="action-suggestion-label">Suggested Direction</span>${ns(a.suggestion)}</div>` : ""}
        ${sl(a.videos).length ? `<div class="action-evidence"><span class="action-evidence-label">What This Looks Like In The Real World</span><div class="action-evidence-list">${sl(a.videos).map(v => `<div class="action-evidence-item"><div class="action-evidence-context">${ns(v.context)}</div><a class="action-evidence-link" href="${s(v.url)}" target="_blank" rel="noreferrer">Watch →</a></div>`).join("")}</div></div>` : ""}
      </div>
    </div>`).join("")}
  </div>
</div>` : ""}

<div class="footer">
  <div class="footer-text">
    Generated by Semantic Intent Engine · ${s(report.brand_name)} · ${s(generated)}<br>
    Based on ${coverage.total_signals_processed || "—"} real customer signals across reviews, video comments, and search data.
  </div>
</div>

</div>
</body>
</html>`;
}
// ─── SECTION 4a: generateReportHTML ──────────────────────────────────────────

export function generateReportHTML(report, signals, evidence, knownActivity, uuid) {
  const nt = (v) => normalizeText(String(v || ""));
  const s = (v) => String(v || "").replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;").replace(/"/g,"&quot;");
  const ns = (v) => s(nt(v));
  const sl = (v) => Array.isArray(v) ? v : [];
  const generated = report.generated_at ? new Date(report.generated_at).toLocaleString() : new Date().toLocaleString();
  const sb = report.signal_breakdown || {};
  const lang = normaliseCustomerLanguage(report.real_customer_language);
  const ss = report.strategic_summary || {};
  const hook = report.condensed_strategic_hook || {};
  const mis = report.core_strategic_misalignment || {};
  const es = report.executive_summary || {};
  const ct = report.category_thinking || {};
  const cat = report.category_expectations || {};
  const bp = report.brand_positioning || {};
  const recommendedAngles = sl(report.recommended_ad_angles).map(normaliseAdAngle);

  const knownActivityNotes = [];
  if (knownActivity?.ctv) knownActivityNotes.push("Known activity override confirms CTV / TV / streaming is active.");
  if (knownActivity?.longForm) knownActivityNotes.push("Known activity override confirms long-form / DRTV / infomercial activity is active.");
  if (knownActivity?.youtube) knownActivityNotes.push("Known activity override confirms online video ad activity is active.");
  if (knownActivity?.podcasts) knownActivityNotes.push("Known activity override confirms podcast / audio sponsorship is active.");

  const filteredEvidenceNotes = sl(evidence?.notes).filter(n =>
    !ACTIVITY_PHRASES.some(phrase => n.toLowerCase().includes(phrase))
  );

  function renderCluster(cluster) {
    return `
<div class="r-cluster">
  <div class="r-cluster-head">
    <div>
      <div class="r-cluster-stage">${s(cluster.intent_stage)}</div>
      <div class="r-cluster-name">${s(cluster.cluster_name)}</div>
    </div>
    <div class="r-cluster-tag">${s(cluster.intent_stage)}</div>
  </div>
  ${cluster.mindset ? `<div class="r-cluster-thinking">${s(cluster.mindset)}</div>` : ""}
  <div class="r-cluster-cols">
    <div class="r-cluster-col">
      ${sl(cluster.user_queries).length ? `<div class="r-field-label">REPRESENTATIVE USER QUERIES</div>${sl(cluster.user_queries).map(q => `<div class="r-query-pill">${s(q)}</div>`).join("")}` : ""}
    </div>
    <div class="r-cluster-col r-cluster-col--right">
      ${sl(cluster.real_customer_signals).length ? `<div class="r-field-label">REAL CUSTOMER SIGNALS</div>${sl(cluster.real_customer_signals).map(sig => `<div class="r-signal-pill">"${ns(sig)}"</div>`).join("")}` : ""}
    </div>
  </div>
  <div class="r-cluster-footer">
    <div class="r-cluster-footer-cell"><div class="r-cluster-footer-label">STRATEGIC GAP</div><div class="r-cluster-footer-text">${s(cluster.strategic_gap)}</div></div>
    <div class="r-cluster-footer-cell"><div class="r-cluster-footer-label">COMPETITOR MESSAGING</div><div class="r-cluster-footer-text">${s(cluster.competitor_messaging)}</div></div>
    <div class="r-cluster-footer-cell">${sl(cluster.evidence_sources).length ? `<div class="r-cluster-footer-label">EVIDENCE SOURCES</div><div class="r-cluster-footer-text">${sl(cluster.evidence_sources).join(" · ")}</div>` : ""}</div>
  </div>
  ${sl(cluster.ad_angles).length ? `
  <div class="r-cluster-angle">
    <div class="r-cluster-angle-label">RECOMMENDED AD ANGLE</div>
    ${sl(cluster.ad_angles).slice(0,1).map(a => {
      if (typeof a === "string") return `<div class="r-cluster-angle-text">${s(a)}</div>`;
      return `<div class="r-cluster-angle-text">${s(a.angle_name || a.angle)}</div>${a.what_it_does ? `<div class="r-cluster-angle-sub">${s(a.what_it_does)}</div>` : ""}`;
    }).join("")}
    ${cluster.funnel_role ? `<div class="r-cluster-funnel">Funnel role: ${s(cluster.funnel_role)}</div>` : ""}
  </div>` : ""}
</div>`;
  }

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>${s(report.brand_name)} — Semantic Intent Cluster</title>
<link href="https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Mono:wght@300;400;500&family=Inter:wght@300;400;500;600;700&display=swap" rel="stylesheet">
<style>
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
:root {
  --navy-deep: #080f1c; --navy-base: #0d1526; --navy-raised: #111d33;
  --navy-surface: #162040; --navy-edge: #1e2f55; --navy-mid: #243568;
  --blue: #2563eb; --blue-bright: #3b82f6; --blue-light: #60a5fa;
  --blue-dim: rgba(37,99,235,0.1);
  --text-primary: #f0f4ff; --text-secondary: #a8c0e8;
  --text-muted: #7a9cc8; --text-faint: #4a6a99;
  --light-bg: #eef2fa; --light-surface: #f6f8fd;
  --light-border: #d0daf0; --light-text: #0d1526; --light-muted: #4a5f8a;
  --font-disp: 'Bebas Neue', sans-serif;
  --font-body: 'Inter', 'Helvetica Neue', Arial, sans-serif;
  --font-mono: 'DM Mono', monospace;
  --pad: 56px;
}
html, body { background: var(--light-bg); color: var(--light-text); font-family: var(--font-body); font-size: 14px; line-height: 1.6; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.r-wrap { max-width: 960px; margin: 0 auto; padding-bottom: 80px; }
.r-cover { background: var(--navy-deep); color: var(--text-primary); padding: 56px var(--pad) 48px; position: relative; overflow: hidden; }
.r-cover::before { content: ''; position: absolute; inset: 0; background: radial-gradient(ellipse at 80% 50%, rgba(37,99,235,0.12) 0%, transparent 70%); pointer-events: none; }
.r-cover-eyebrow { font-family: var(--font-mono); font-size: 12px; letter-spacing: .18em; text-transform: uppercase; color: var(--text-muted); margin-bottom: 18px; position: relative; }
.r-cover-name { font-family: var(--font-disp); font-size: 64px; letter-spacing: -.01em; line-height: 1; color: var(--text-primary); position: relative; margin-bottom: 6px; }
.r-cover-url { font-family: var(--font-mono); font-size: 12px; color: var(--text-muted); position: relative; margin-bottom: 36px; }
.r-cover-verdict { position: relative; border-top: 1px solid var(--navy-edge); padding-top: 24px; }
.r-cover-verdict-label { font-family: var(--font-mono); font-size: 12px; letter-spacing: .18em; text-transform: uppercase; color: var(--blue-light); margin-bottom: 10px; }
.r-cover-verdict-text { font-size: 20px; line-height: 1.35; color: var(--text-primary); max-width: 600px; font-weight: 400; }
.r-cover-stats { position: relative; margin-top: 36px; display: flex; gap: 32px; flex-wrap: wrap; }
.r-cover-stat { font-family: var(--font-mono); font-size: 12px; color: var(--text-muted); text-transform: uppercase; letter-spacing: .1em; }
.r-cover-stat strong { display: block; color: var(--text-primary); font-family: var(--font-disp); font-size: 28px; font-weight: 700; letter-spacing: 0; text-transform: none; margin-bottom: 2px; }
.r-exec-summary { background: rgba(37,99,235,0.06); border-left: 4px solid var(--blue); padding: 28px var(--pad); border-bottom: 1px solid var(--light-border); }
.r-exec-summary-label { font-family: var(--font-mono); font-size: 12px; letter-spacing: .18em; text-transform: uppercase; color: var(--blue); margin-bottom: 16px; font-weight: 700; }
.r-exec-summary p { margin: 0 0 10px; font-size: 14px; color: var(--light-text); line-height: 1.6; }
.r-exec-summary p:last-child { margin-bottom: 0; }
.r-exec-label { font-family: var(--font-mono); font-size: 10px; letter-spacing: .1em; text-transform: uppercase; font-weight: 700; color: var(--light-text); margin-right: 6px; }
.r-thesis-banner { background: rgba(37,99,235,0.10); border-left: 4px solid var(--blue); padding: 20px var(--pad); border-bottom: 1px solid var(--light-border); }
.r-thesis-banner-label { font-family: var(--font-mono); font-size: 11px; letter-spacing: .22em; text-transform: uppercase; color: var(--blue); margin-bottom: 8px; font-weight: 800; }
.r-thesis-banner p { margin: 0; font-size: 14px; color: var(--light-text); line-height: 1.6; font-style: italic; }
.r-cat-thinking { background: var(--light-surface); border-bottom: 1px solid var(--light-border); }
.r-cat-thinking-label { font-family: var(--font-mono); font-size: 12px; letter-spacing: .18em; text-transform: uppercase; color: var(--text-primary); background: var(--navy-raised); padding: 14px var(--pad); font-weight: 700; }
.r-cat-thinking-setup { padding: 20px var(--pad) 16px; font-size: 14px; line-height: 1.7; color: var(--light-muted); border-bottom: 1px solid var(--light-border); }
.r-cat-thinking-question { font-family: var(--font-mono); font-size: 12px; color: var(--light-text); padding: 10px var(--pad); border-bottom: 1px solid var(--light-border); line-height: 1.5; }
.r-cat-thinking-translation { padding: 16px var(--pad); font-size: 13px; line-height: 1.7; color: var(--light-text); font-weight: 600; font-style: italic; background: var(--light-bg); border-top: 2px solid var(--blue); }
.r-section { padding: 48px var(--pad); border-bottom: 1px solid var(--light-border); background: var(--light-surface); }
.r-section--gray { background: var(--light-bg); }
.r-section--tight { padding-top: 40px; padding-bottom: 40px; }
.r-label { font-family: var(--font-mono); font-size: 12px; letter-spacing: .2em; text-transform: uppercase; color: var(--blue); margin-bottom: 24px; display: flex; align-items: center; gap: 12px; }
.r-label::after { content: ''; flex: 1; height: 1px; background: var(--light-border); }
.r-title { font-family: var(--font-disp); font-size: 38px; letter-spacing: -.01em; line-height: 1.1; color: var(--light-text); margin-bottom: 20px; }
.r-title--lg { font-size: 44px; }
.r-body { font-size: 15px; color: var(--light-muted); line-height: 1.7; max-width: 680px; }
.r-mis-grid { display: grid; grid-template-columns: 1fr 40px 1fr; margin: 32px 0 0; }
.r-mis-col { padding: 24px 20px; border: 1px solid var(--light-border); background: var(--light-surface); }
.r-mis-col:first-child { border-right: none; }
.r-mis-col:last-child { border-left: none; }
.r-mis-divider { background: var(--blue); color: #fff; display: flex; align-items: center; justify-content: center; font-family: var(--font-disp); font-size: 22px; }
.r-mis-col-label { font-family: var(--font-mono); font-size: 10px; letter-spacing: .14em; text-transform: uppercase; color: var(--light-muted); margin-bottom: 8px; }
.r-mis-col-text { font-size: 14px; font-weight: 600; line-height: 1.5; color: var(--light-text); }
.r-mis-why { margin-top: 20px; padding: 16px 20px; background: var(--light-bg); border: 1px solid var(--light-border); }
.r-mis-why-label { font-family: var(--font-mono); font-size: 10px; letter-spacing: .16em; text-transform: uppercase; color: var(--light-muted); margin-bottom: 6px; }
.r-mis-why-text { font-size: 13px; color: var(--light-muted); line-height: 1.7; }
.r-mis-closing { padding: 14px 20px; background: var(--blue); color: #fff; font-size: 13px; font-weight: 700; line-height: 1.5; }
.r-proof { margin-top: 28px; padding: 18px 20px; background: var(--light-bg); border-left: 3px solid var(--blue); }
.r-proof-label { font-family: var(--font-mono); font-size: 12px; letter-spacing: .14em; text-transform: uppercase; color: var(--light-muted); margin-bottom: 10px; }
.r-proof-quote { font-size: 14px; font-style: italic; color: var(--light-muted); padding: 5px 0; border-bottom: 1px solid var(--light-border); }
.r-proof-quote:last-child { border-bottom: none; }
.r-bp-grid { display: grid; grid-template-columns: 1fr 1fr; border: 1px solid var(--light-border); margin-bottom: 20px; background: var(--light-surface); }
.r-bp-col { padding: 20px; }
.r-bp-col:first-child { border-right: 1px solid var(--light-border); }
.r-bp-col-label { font-family: var(--font-mono); font-size: 10px; letter-spacing: .16em; text-transform: uppercase; color: var(--light-muted); margin-bottom: 12px; padding-bottom: 8px; border-bottom: 1px solid var(--light-border); }
.r-bp-item { padding: 8px 0; border-bottom: 1px solid var(--light-border); font-size: 13px; line-height: 1.55; color: var(--light-text); }
.r-bp-item:last-child { border-bottom: none; }
.r-bp-quote { font-family: var(--font-mono); font-size: 12px; font-style: italic; color: var(--light-text); padding: 6px 10px; background: var(--light-bg); border: 1px solid var(--light-border); margin-bottom: 6px; }
.r-bp-quote:last-child { margin-bottom: 0; }
.r-bp-value { padding: 16px 20px; background: var(--light-bg); border: 1px solid var(--light-border); margin-bottom: 20px; font-size: 13px; color: var(--light-muted); line-height: 1.7; }
.r-bp-value-label { font-family: var(--font-mono); font-size: 10px; letter-spacing: .14em; text-transform: uppercase; color: var(--light-muted); margin-bottom: 8px; }
.r-bp-interpret { padding: 16px 20px; background: rgba(37,99,235,0.06); border-top: 2px solid var(--blue); }
.r-bp-interpret-label { font-family: var(--font-mono); font-size: 10px; letter-spacing: .14em; text-transform: uppercase; color: var(--blue); margin-bottom: 10px; }
.r-bp-interpret-tags { display: flex; flex-wrap: wrap; gap: 8px; }
.r-bp-interpret-tag { font-family: var(--font-mono); font-size: 12px; font-style: italic; color: var(--blue); padding: 5px 12px; border: 1px solid rgba(37,99,235,.3); background: var(--light-surface); }
.r-cat-rule { padding: 16px 20px; background: var(--navy-raised); color: var(--text-primary); font-size: 14px; font-weight: 600; line-height: 1.55; font-style: italic; }
.r-cat-rule-label { font-family: var(--font-mono); font-size: 10px; letter-spacing: .16em; text-transform: uppercase; color: var(--text-muted); margin-bottom: 6px; }
.r-cat-grid { display: grid; grid-template-columns: 1fr 1fr; border: 1px solid var(--light-border); margin-top: 20px; background: var(--light-surface); }
.r-cat-col { padding: 20px; }
.r-cat-col:first-child { border-right: 1px solid var(--light-border); }
.r-cat-col-label { font-family: var(--font-mono); font-size: 10px; letter-spacing: .16em; text-transform: uppercase; color: var(--light-muted); margin-bottom: 12px; padding-bottom: 8px; border-bottom: 1px solid var(--light-border); }
.r-cat-item { padding: 8px 0; border-bottom: 1px solid var(--light-border); font-size: 13px; line-height: 1.55; color: var(--light-text); }
.r-cat-item:last-child { border-bottom: none; }
.r-angles { background: var(--navy-deep); padding: 56px var(--pad); }
.r-angles-eyebrow { font-family: var(--font-mono); font-size: 12px; letter-spacing: .2em; text-transform: uppercase; color: var(--blue-light); margin-bottom: 14px; }
.r-angles-title { font-family: var(--font-disp); font-size: 44px; letter-spacing: -.01em; line-height: 1.05; color: var(--text-primary); margin-bottom: 40px; }
.r-angle-item { border-top: 1px solid var(--navy-edge); padding: 24px 0; }
.r-angle-item:last-child { border-bottom: 1px solid var(--navy-edge); }
.r-angle-top { display: grid; grid-template-columns: 52px 1fr; align-items: start; }
.r-angle-num { font-family: var(--font-disp); font-size: 42px; color: var(--blue-light); line-height: 1; margin-top: -4px; }
.r-angle-text { font-size: 20px; font-weight: 600; color: var(--text-primary); line-height: 1.3; padding-top: 5px; }
.r-angle-why { font-family: var(--font-mono); font-size: 12px; color: var(--text-muted); margin-top: 8px; margin-left: 52px; line-height: 1.5; }
.r-angle-stage { display: inline-block; font-family: var(--font-mono); font-size: 10px; letter-spacing: .1em; padding: 3px 8px; border: 1px solid var(--navy-edge); color: var(--text-muted); margin-top: 6px; margin-left: 52px; }
.r-summary-group { margin-bottom: 28px; }
.r-summary-group:last-child { margin-bottom: 0; }
.r-summary-group-label { font-family: var(--font-mono); font-size: 12px; letter-spacing: .16em; text-transform: uppercase; color: var(--light-muted); margin-bottom: 10px; padding-bottom: 8px; border-bottom: 1px solid var(--light-border); }
.r-summary-row { padding: 12px 0; border-bottom: 1px solid var(--light-border); font-size: 14px; color: var(--light-text); line-height: 1.5; }
.r-summary-row:last-child { border-bottom: none; }
.r-stat-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 1px; background: var(--light-border); border: 1px solid var(--light-border); margin-bottom: 32px; }
.r-stat { background: var(--light-surface); padding: 24px 16px; text-align: center; }
.r-stat-num { font-family: var(--font-disp); font-size: 48px; line-height: 1; color: var(--light-text); margin-bottom: 4px; }
.r-stat-label { font-family: var(--font-mono); font-size: 10px; letter-spacing: .12em; text-transform: uppercase; color: var(--light-muted); line-height: 1.4; }
.r-stat-sub { font-family: var(--font-mono); font-size: 9px; letter-spacing: .08em; text-transform: uppercase; color: var(--light-muted); opacity: .7; line-height: 1.4; margin-top: 6px; }
.r-stat--total { background: var(--navy-raised); }
.r-stat--total .r-stat-num { color: var(--text-primary); }
.r-stat--total .r-stat-label { color: var(--text-muted); }
.r-stat--total .r-stat-sub { color: var(--text-muted); }
.r-three-col { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; }
.r-three-col-box { border: 1px solid var(--light-border); background: var(--light-surface); overflow: hidden; }
.r-three-col-label { font-family: var(--font-mono); font-size: 10px; letter-spacing: .16em; text-transform: uppercase; color: var(--light-muted); padding: 8px 12px; background: var(--light-bg); border-bottom: 1px solid var(--light-border); }
.r-three-col-item { padding: 8px 12px; font-size: 13px; color: var(--light-muted); border-bottom: 1px solid var(--light-border); line-height: 1.5; }
.r-three-col-item:last-child { border-bottom: none; }
.r-lang-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
.r-lang-group { border: 1px solid var(--light-border); background: var(--light-surface); overflow: hidden; }
.r-lang-label { font-family: var(--font-mono); font-size: 10px; letter-spacing: .14em; text-transform: uppercase; color: var(--light-muted); padding: 8px 14px; background: var(--light-bg); border-bottom: 1px solid var(--light-border); }
.r-lang-quote { font-size: 13px; font-style: italic; color: var(--light-muted); padding: 8px 14px; border-bottom: 1px solid var(--light-border); line-height: 1.5; }
.r-lang-quote:last-child { border-bottom: none; }
.r-pattern { border: 1px solid var(--light-border); background: var(--light-surface); margin-bottom: 16px; overflow: hidden; }
.r-pattern:last-child { margin-bottom: 0; }
.r-pattern-head { background: var(--navy-raised); padding: 10px 16px; }
.r-pattern-name { font-family: var(--font-disp); font-size: 16px; letter-spacing: .04em; color: var(--text-primary); }
.r-pattern-body { padding: 16px; display: grid; gap: 10px; }
.r-pattern-desc { font-size: 13px; color: var(--light-muted); line-height: 1.65; }
.r-pattern-phrases { display: flex; flex-wrap: wrap; gap: 6px; }
.r-pattern-phrase { font-family: var(--font-mono); font-size: 11px; font-style: italic; background: var(--light-bg); border: 1px solid var(--light-border); padding: 4px 10px; color: var(--light-muted); }
.r-pattern-impact { border-left: 3px solid var(--blue); padding: 8px 12px; background: rgba(37,99,235,0.06); font-size: 12px; color: var(--light-text); font-weight: 600; line-height: 1.5; }
.r-pattern-section-label { font-family: var(--font-mono); font-size: 10px; letter-spacing: .14em; text-transform: uppercase; color: var(--light-muted); margin-bottom: 5px; }
.r-diag { border: 1px solid var(--light-border); background: var(--light-surface); margin-bottom: 16px; overflow: hidden; }
.r-diag:last-child { margin-bottom: 0; }
.r-diag-head { background: var(--navy-raised); padding: 10px 16px; }
.r-diag-type { font-family: var(--font-disp); font-size: 16px; letter-spacing: .04em; color: var(--text-primary); }
.r-diag-body { padding: 16px; display: grid; gap: 10px; }
.r-diag-def { font-size: 13px; color: var(--light-muted); line-height: 1.65; }
.r-diag-sigs { display: grid; gap: 4px; }
.r-diag-sig { font-family: var(--font-mono); font-size: 13px; font-style: italic; color: var(--light-text); padding: 5px 10px; background: var(--light-bg); border: 1px solid var(--light-border); }
.r-diag-impl { border-left: 3px solid var(--blue); padding: 8px 12px; background: rgba(37,99,235,0.06); font-size: 12px; font-weight: 600; color: var(--light-text); line-height: 1.5; }
.r-diag-section-label { font-family: var(--font-mono); font-size: 10px; letter-spacing: .14em; text-transform: uppercase; color: var(--light-muted); margin-bottom: 5px; }
.r-cluster { background: var(--light-surface); border: 1px solid var(--light-border); margin-bottom: 24px; }
.r-cluster:last-child { margin-bottom: 0; }
.r-cluster-head { padding: 24px 24px 0; display: grid; grid-template-columns: 1fr auto; gap: 16px; align-items: start; }
.r-cluster-stage { font-family: var(--font-mono); font-size: 12px; letter-spacing: .18em; text-transform: uppercase; color: var(--blue); margin-bottom: 6px; }
.r-cluster-name { font-family: var(--font-disp); font-size: 28px; letter-spacing: -.01em; line-height: 1.1; color: var(--light-text); }
.r-cluster-tag { font-family: var(--font-mono); font-size: 10px; letter-spacing: .1em; text-transform: uppercase; background: var(--light-bg); color: var(--light-muted); border: 1px solid var(--light-border); padding: 5px 10px; white-space: nowrap; margin-top: 4px; }
.r-cluster-thinking { padding: 12px 24px 0; font-size: 13px; color: var(--light-muted); font-style: italic; line-height: 1.6; }
.r-cluster-cols { display: grid; grid-template-columns: 1fr 1fr; padding: 20px 24px; border-top: 1px solid var(--light-border); margin-top: 20px; }
.r-cluster-col { padding-right: 24px; }
.r-cluster-col--right { padding-right: 0; padding-left: 24px; border-left: 1px solid var(--light-border); }
.r-field-label { font-family: var(--font-mono); font-size: 10px; letter-spacing: .14em; text-transform: uppercase; color: var(--light-muted); margin-bottom: 10px; }
.r-query-pill { border: 1px solid var(--light-border); background: var(--light-bg); padding: 7px 10px; font-size: 12px; color: var(--light-text); margin-bottom: 4px; line-height: 1.4; }
.r-signal-pill { font-size: 13px; font-style: italic; color: var(--light-muted); padding: 6px 10px; border-bottom: 1px solid var(--light-border); line-height: 1.4; }
.r-signal-pill:last-child { border-bottom: none; }
.r-cluster-footer { display: grid; grid-template-columns: 1fr 1fr 1fr; background: var(--light-bg); border-top: 1px solid var(--light-border); }
.r-cluster-footer-cell { padding: 14px 16px; border-right: 1px solid var(--light-border); }
.r-cluster-footer-cell:last-child { border-right: none; }
.r-cluster-footer-label { font-family: var(--font-mono); font-size: 10px; letter-spacing: .14em; text-transform: uppercase; color: var(--light-muted); margin-bottom: 4px; }
.r-cluster-footer-text { font-size: 12px; color: var(--light-text); line-height: 1.4; font-weight: 500; }
.r-cluster-angle { padding: 16px 24px; background: rgba(37,99,235,0.06); border-top: 1px solid rgba(37,99,235,.15); }
.r-cluster-angle-label { font-family: var(--font-mono); font-size: 10px; letter-spacing: .14em; text-transform: uppercase; color: var(--blue); margin-bottom: 5px; }
.r-cluster-angle-text { font-size: 14px; font-weight: 600; color: var(--light-text); line-height: 1.4; }
.r-cluster-angle-sub { font-size: 12px; color: var(--light-muted); margin-top: 4px; line-height: 1.5; }
.r-cluster-funnel { font-size: 11px; color: var(--light-muted); margin-top: 4px; }
.r-evidence { border: 1px solid var(--light-border); background: var(--light-surface); padding: 14px; margin-bottom: 10px; }
.r-evidence:last-child { margin-bottom: 0; }
.r-evidence-type { font-family: var(--font-mono); font-size: 12px; letter-spacing: .12em; text-transform: uppercase; color: var(--light-muted); margin-bottom: 4px; }
.r-evidence-title { font-size: 13px; font-weight: 700; color: var(--light-text); margin-bottom: 4px; line-height: 1.4; }
.r-evidence-snippet { font-size: 12px; color: var(--light-muted); line-height: 1.55; margin-bottom: 4px; }
.r-evidence-url { font-family: var(--font-mono); font-size: 10px; color: var(--blue); word-break: break-all; }
.r-brand-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 1px; background: var(--light-border); border: 1px solid var(--light-border); }
.r-brand-cell { background: var(--light-surface); padding: 14px 16px; }
.r-brand-cell-label { font-family: var(--font-mono); font-size: 10px; letter-spacing: .12em; text-transform: uppercase; color: var(--light-muted); margin-bottom: 4px; }
.r-brand-cell-value { font-size: 13px; color: var(--light-text); font-weight: 500; }
.r-method-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
.r-method { border: 1px solid var(--light-border); background: var(--light-surface); padding: 16px; }
.r-method-label { font-family: var(--font-mono); font-size: 10px; letter-spacing: .16em; text-transform: uppercase; color: var(--light-muted); margin-bottom: 8px; }
.r-method-text { font-size: 12px; color: var(--light-muted); line-height: 1.65; }
.r-market-insight { border: 1px solid var(--light-border); background: var(--light-bg); padding: 20px; }
.r-market-insight-label { font-family: var(--font-mono); font-size: 10px; letter-spacing: .16em; text-transform: uppercase; color: var(--light-muted); margin-bottom: 8px; }
.r-market-insight-text { font-size: 14px; color: var(--light-muted); line-height: 1.75; }
.r-bullets { display: grid; gap: 8px; }
.r-bullet { border-left: 3px solid var(--blue); background: var(--light-bg); padding: 12px 14px; font-size: 13px; line-height: 1.65; color: var(--light-text); }
.r-source-tags { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 12px; }
.r-source-tag { font-family: var(--font-mono); font-size: 12px; letter-spacing: .1em; padding: 5px 10px; border: 1px solid var(--light-border); background: var(--light-bg); color: var(--light-muted); }
/* ─── SIDE NAV LAYOUT (Overview report) ─── */
.r-wrap { display: grid; grid-template-columns: 220px 1fr; gap: 0; max-width: 1200px; margin: 0 auto; }
.r-side-nav { background: var(--navy-deep); border-right: 1px solid var(--navy-edge); padding: 24px 0; position: sticky; top: 48px; align-self: start; max-height: calc(100vh - 48px); overflow-y: auto; }
.r-side-nav::-webkit-scrollbar { width: 4px; }
.r-side-nav::-webkit-scrollbar-track { background: var(--navy-deep); }
.r-side-nav::-webkit-scrollbar-thumb { background: var(--navy-edge); border-radius: 2px; }
.r-side-nav-title { font-family: var(--font-mono); font-size: 10px; letter-spacing: .18em; color: var(--text-faint); padding: 0 20px 14px; border-bottom: 1px solid var(--navy-edge); margin-bottom: 14px; text-transform: uppercase; }
.r-side-nav-group-label { padding: 12px 16px 6px; font-family: var(--font-mono); font-size: 9px; letter-spacing: .16em; color: var(--text-faint); text-transform: uppercase; }
.r-side-nav-link { display: block; padding: 7px 20px; font-family: var(--font-body); font-size: 12px; color: var(--text-muted); text-decoration: none; border-left: 2px solid transparent; transition: color .15s, background .15s, border-color .15s; }
.r-side-nav-link:hover { color: var(--text-primary); background: var(--navy-base); }
.r-side-nav-link.active { color: var(--text-primary); background: var(--blue); border-left-color: var(--blue-light); font-weight: 500; }
.r-main { min-width: 0; }
.r-section-band, .r-section, .r-angles, #exec-summary { scroll-margin-top: 64px; }
/* ─── SECTION DIVIDER BANDS (Overview report) ─── */
.r-section-band { background: var(--navy-mid); padding: 20px var(--pad); border-bottom: 1px solid var(--navy-edge); }
.r-section-band-eyebrow { font-family: var(--font-mono); font-size: 10px; letter-spacing: .2em; color: var(--blue-light); text-transform: uppercase; margin-bottom: 6px; }
.r-section-band-title { font-family: var(--font-disp); font-size: 28px; color: var(--text-primary); letter-spacing: .02em; line-height: 1; }
/* ─── AT A GLANCE STAT STRIP (Overview report) ─── */
.r-glance { background: var(--navy-raised); padding: 20px var(--pad); border-bottom: 1px solid var(--navy-edge); }
.r-glance-label { font-family: var(--font-mono); font-size: 10px; letter-spacing: .2em; color: var(--text-faint); text-transform: uppercase; margin-bottom: 14px; }
.r-glance-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; }
.r-glance-card { background: var(--navy-base); border: 1px solid var(--navy-edge); border-radius: 4px; padding: 14px 16px; text-decoration: none; display: block; transition: border-color .15s; }
.r-glance-card:hover { border-color: var(--blue); }
.r-glance-num { font-family: var(--font-disp); font-size: 32px; color: var(--blue-light); line-height: 1; margin-bottom: 6px; }
.r-glance-label-text { font-family: var(--font-mono); font-size: 9px; color: var(--text-faint); letter-spacing: .14em; text-transform: uppercase; }
.r-back-to-top { position: fixed; bottom: 32px; right: 32px; width: 48px; height: 48px; border-radius: 50%; background: var(--blue); color: var(--text-primary); border: none; cursor: pointer; display: flex; align-items: center; justify-content: center; box-shadow: 0 4px 14px rgba(0,0,0,0.4); opacity: 0; visibility: hidden; transition: opacity .2s, visibility .2s, background .15s; z-index: 99; font-family: var(--font-disp); font-size: 24px; line-height: 1; text-decoration: none; }
.r-back-to-top.visible { opacity: 1; visibility: visible; }
.r-back-to-top:hover { background: var(--blue-bright); }
@media (max-width: 900px) {
  .r-wrap { grid-template-columns: 1fr; }
  .r-side-nav { display: none; }
  .r-glance-grid { grid-template-columns: 1fr 1fr; }
}
@media print {
  @page { margin: 0; size: A4; }
  .r-cluster { page-break-inside: avoid; break-inside: avoid; }
  .r-angles { page-break-inside: avoid; break-inside: avoid; }
  .r-section { page-break-inside: avoid; break-inside: avoid; }
  .r-pattern { page-break-inside: avoid; break-inside: avoid; }
  .r-diag { page-break-inside: avoid; break-inside: avoid; }
  .r-wrap { padding-bottom: 0; }
}
</style>
</head>
<body>
${generateWhiteLabelStyle(report)}
${generateNavBar(report.brand_name, "strategy", uuid, report.white_label)}
<div class="r-wrap">

<aside class="r-side-nav">
  <div class="r-side-nav-title">Overview</div>

  <div class="r-side-nav-group-label">Section 1 — The Setup</div>
  <a class="r-side-nav-link" href="#section-1">Key Findings</a>
  <a class="r-side-nav-link" href="#category-thinking">Category Thinking</a>

  <div class="r-side-nav-group-label">Section 2 — The Diagnosis</div>
  <a class="r-side-nav-link" href="#section-2">Core Strategic Insight</a>
  <a class="r-side-nav-link" href="#brand-positioning">Brand Positioning</a>
  <a class="r-side-nav-link" href="#category-expectations">Category Expectations</a>

  <div class="r-side-nav-group-label">Section 3 — The Output</div>
  <a class="r-side-nav-link" href="#section-3">What To Run Next</a>
  <a class="r-side-nav-link" href="#strategic-summary">Strategic Summary</a>

  <div class="r-side-nav-group-label">Section 4 — The Evidence</div>
  <a class="r-side-nav-link" href="#section-4">Signal Coverage</a>
  <a class="r-side-nav-link" href="#customer-language">Customer Language</a>
  <a class="r-side-nav-link" href="#signal-patterns">Signal Patterns</a>
  <a class="r-side-nav-link" href="#campaign-diagnosis">Campaign Diagnosis</a>
  <a class="r-side-nav-link" href="#market-insight">Market Insight</a>
  <a class="r-side-nav-link" href="#signals-summary">Signals Summary</a>
  <a class="r-side-nav-link" href="#intent-clusters">Intent Clusters</a>
  <a class="r-side-nav-link" href="#public-evidence">Public Evidence</a>
  <a class="r-side-nav-link" href="#brand-signals">Brand Signals</a>
  <a class="r-side-nav-link" href="#methodology">Methodology</a>
</aside>

<main class="r-main">
<div class="r-cover">
  <div class="r-cover-eyebrow">Ntentify · Semantic Intent Cluster / Strategy Report</div>
  <div class="r-cover-name">${s(report.brand_name)}</div>
  <div class="r-cover-url">${s(report.website)} &nbsp;·&nbsp; ${s(generated)}</div>
  <div class="r-cover-verdict">
    <div class="r-cover-verdict-label">Strategic Verdict</div>
    <div class="r-cover-verdict-text">${s((report.strategic_verdict || report.signals_summary || "").replace(/^STRATEGIC VERDICT:\s*/i, ""))}</div>
  </div>
  ${report.signal_coverage ? `
  <div class="r-cover-stats">
    ${report.signal_coverage.total_signals_processed > 0 ? `<div class="r-cover-stat"><strong>${computeTotalSignals(report.signal_coverage)}</strong>Total Signals</div>` : ""}
    ${report.signal_coverage.reviews_analyzed > 0 ? `<div class="r-cover-stat"><strong>${report.signal_coverage.reviews_analyzed}</strong>Reviews Analyzed</div>` : ""}
    ${report.signal_coverage.social_comments_analyzed > 0 ? `<div class="r-cover-stat"><strong>${report.signal_coverage.social_comments_analyzed}</strong>Video Comments</div>` : ""}
    <div class="r-cover-stat"><strong>4</strong>Intent Clusters</div>
  </div>` : ""}
</div>

<div class="r-glance">
  <div class="r-glance-label">At a Glance</div>
  <div class="r-glance-grid">
    <a class="r-glance-card" href="#signal-coverage">
      <div class="r-glance-num">${computeTotalSignals(report.signal_coverage)}</div>
      <div class="r-glance-label-text">Total Signals</div>
    </a>
    <a class="r-glance-card" href="#intent-clusters">
      <div class="r-glance-num">4</div>
      <div class="r-glance-label-text">Funnel Stages</div>
    </a>
    <a class="r-glance-card" href="#what-to-run">
      <div class="r-glance-num">${recommendedAngles.length}</div>
      <div class="r-glance-label-text">Ad Angles</div>
    </a>
    <a class="r-glance-card" href="#signal-coverage">
      <div class="r-glance-num">${(report.signal_coverage?.sources || []).length || 0}</div>
      <div class="r-glance-label-text">Sources</div>
    </a>
  </div>
</div>

<div class="r-section-band" id="section-1">
  <div class="r-section-band-eyebrow">Section 1</div>
  <div class="r-section-band-title">THE SETUP</div>
</div>
${es.the_opportunity ? `
<div class="r-thesis-banner" id="intent-cluster-thesis">
  <div class="r-thesis-banner-label">Intent Cluster Thesis</div>
  <p>${s(es.the_opportunity)}</p>
</div>` : ""}
${(es.category_truth || es.the_gap) ? `
<div class="r-exec-summary" id="exec-summary">
  <div class="r-exec-summary-label">KEY FINDINGS</div>
  ${es.category_truth ? `<p><span class="r-exec-label">Category Truth</span>${s(es.category_truth)}</p>` : ""}
  ${es.customer_expectation ? `<p><span class="r-exec-label">Customer Expectation</span>${s(es.customer_expectation)}</p>` : ""}
  ${es.brand_positioning ? `<p><span class="r-exec-label">Brand Positioning</span>${s(es.brand_positioning)}</p>` : ""}
  ${es.the_gap ? `<p><span class="r-exec-label">The Gap</span>${s(es.the_gap)}</p>` : ""}
</div>` : ""}

${(ct.category_setup || sl(ct.customer_questions).length) ? `
<div class="r-cat-thinking" id="category-thinking">
  <div class="r-cat-thinking-label">HOW CUSTOMERS THINK ABOUT THIS CATEGORY</div>
  ${ct.category_setup ? `<div class="r-cat-thinking-setup">${s(ct.category_setup)}</div>` : ""}
  ${sl(ct.customer_questions).map(q => `<div class="r-cat-thinking-question">"${s(q)}"</div>`).join("")}
  ${ct.intent_translation ? `<div class="r-cat-thinking-translation">${s(ct.intent_translation)}</div>` : ""}
</div>` : ""}

<div class="r-section-band" id="section-2">
  <div class="r-section-band-eyebrow">Section 2</div>
  <div class="r-section-band-title">THE DIAGNOSIS</div>
</div>
${hook.primary_insight ? `
<div class="r-section" id="core-insight">
  <div class="r-label">Core Strategic Insight</div>
  <div class="r-title r-title--lg">${s(hook.primary_insight)}</div>
  ${report.signals_summary ? `<div class="r-body">${s(report.signals_summary)}</div>` : ""}
  ${(hook.what_customers_want || hook.what_marketing_delivers) ? `
  <div class="r-mis-grid">
    <div class="r-mis-col"><div class="r-mis-col-label">What Customers Want</div><div class="r-mis-col-text">${s(hook.what_customers_want)}</div></div>
    <div class="r-mis-divider">≠</div>
    <div class="r-mis-col"><div class="r-mis-col-label">What Marketing Delivers</div><div class="r-mis-col-text">${s(hook.what_marketing_delivers)}</div></div>
  </div>` : ""}
  ${(mis.why_this_matters || mis.why_this_creates_failure) ? `<div class="r-mis-why"><div class="r-mis-why-label">Where This Leaves Performance on the Table</div><div class="r-mis-why-text">${s(mis.why_this_matters || mis.why_this_creates_failure)}</div></div>` : ""}
  ${mis.closing_statement ? `<div class="r-mis-closing">${s(mis.closing_statement)}</div>` : ""}
  ${sl(hook.proof_phrases).length || sl(mis.customer_evidence).length ? `
  <div class="r-proof">
    <div class="r-proof-label">Proof From Customer Signals</div>
    ${[...sl(hook.proof_phrases), ...sl(mis.customer_evidence)].slice(0,6).map(q => `<div class="r-proof-quote">"${ns(q)}"</div>`).join("")}
  </div>` : ""}
</div>` : ""}

${(sl(bp.positioning_summary).length || sl(bp.direct_site_language).length) ? `
<div class="r-section r-section--tight" id="brand-positioning">
  <div class="r-label">Brand Positioning</div>
  <div class="r-title">What Your Brand Is Saying</div>
  <div class="r-bp-grid">
    <div class="r-bp-col">
      <div class="r-bp-col-label">Your Positioning</div>
      ${sl(bp.positioning_summary).map(item => `<div class="r-bp-item">${s(item)}</div>`).join("")}
    </div>
    <div class="r-bp-col">
      <div class="r-bp-col-label">Direct Site Language</div>
      ${sl(bp.direct_site_language).map(q => `<div class="r-bp-quote">"${s(q)}"</div>`).join("")}
    </div>
  </div>
  ${bp.value_presentation ? `<div class="r-bp-value"><div class="r-bp-value-label">How You Present Value</div>${s(bp.value_presentation)}</div>` : ""}
  ${sl(bp.customer_interpretation).length ? `<div class="r-bp-interpret"><div class="r-bp-interpret-label">How Customers Interpret This</div><div class="r-bp-interpret-tags">${sl(bp.customer_interpretation).map(tag => `<div class="r-bp-interpret-tag">"${ns(tag)}"</div>`).join("")}</div></div>` : ""}
</div>` : ""}

${(cat.category_rule || sl(cat.what_customers_expect).length || sl(cat.what_customers_reject).length) ? `
<div class="r-section r-section--gray r-section--tight" id="category-expectations">
  <div class="r-label">Category Expectations</div>
  <div class="r-title">What Customers Expect From This Category</div>
  ${cat.category_rule ? `<div class="r-cat-rule"><div class="r-cat-rule-label">Category Rule</div>${s(cat.category_rule)}</div>` : ""}
  ${(sl(cat.what_customers_expect).length || sl(cat.what_customers_reject).length) ? `
  <div class="r-cat-grid">
    <div class="r-cat-col"><div class="r-cat-col-label">What Customers Expect</div>${sl(cat.what_customers_expect).map(item => `<div class="r-cat-item">${s(item)}</div>`).join("")}</div>
    <div class="r-cat-col"><div class="r-cat-col-label">What Customers Reject</div>${sl(cat.what_customers_reject).map(item => `<div class="r-cat-item">${s(item)}</div>`).join("")}</div>
  </div>` : ""}
</div>` : ""}

<div class="r-section-band" id="section-3">
  <div class="r-section-band-eyebrow">Section 3</div>
  <div class="r-section-band-title">THE OUTPUT</div>
</div>
${recommendedAngles.length ? `
<div class="r-angles" id="what-to-run">
  <div class="r-angles-eyebrow">Primary Output · Highest Priority</div>
  <div class="r-angles-title">What You Should Run Next</div>
  ${recommendedAngles.map((a, i) => `
  <div class="r-angle-item">
    <div class="r-angle-top"><div class="r-angle-num">${i + 1}</div><div class="r-angle-text">${s(a.hook)}</div></div>
    ${(a.what_it_addresses || a.why) ? `<div class="r-angle-why">${s(a.what_it_addresses || a.why)}</div>` : ""}
    ${(a.tied_to_stage || a.tied_to) ? `<div class="r-angle-stage">${s(a.tied_to_stage || a.tied_to)}</div>` : ""}
  </div>`).join("")}
</div>` : ""}

${(sl(ss.top_reasons_marketing_is_failing).length || sl(ss.top_messaging_shifts_needed).length) ? `
<div class="r-section r-section--tight" id="strategic-summary">
  <div class="r-label">Strategic Summary</div>
  <div class="r-title">Where Messaging Can Improve</div>
  ${sl(ss.top_reasons_marketing_is_failing).length ? `<div class="r-summary-group"><div class="r-summary-group-label">Top Reasons Marketing Is Failing</div>${sl(ss.top_reasons_marketing_is_failing).map(r => `<div class="r-summary-row">${s(r)}</div>`).join("")}</div>` : ""}
  ${sl(ss.top_messaging_shifts_needed).length ? `<div class="r-summary-group"><div class="r-summary-group-label">Top Messaging Shifts Needed</div>${sl(ss.top_messaging_shifts_needed).map(r => `<div class="r-summary-row">${s(r)}</div>`).join("")}</div>` : ""}
</div>` : ""}

<div class="r-section-band" id="section-4">
  <div class="r-section-band-eyebrow">Section 4</div>
  <div class="r-section-band-title">THE EVIDENCE</div>
</div>
<div class="r-section r-section--gray r-section--tight" id="signal-coverage">
  <div class="r-label">Signal Coverage</div>
  <div class="r-title">${report.signal_coverage ? computeTotalSignals(report.signal_coverage) : "—"} Total Signals Analyzed</div>
  <div class="r-stat-grid">
    <div class="r-stat"><div class="r-stat-num">${report.signal_coverage?.website_signals_extracted ?? 0}</div><div class="r-stat-label">Website Signals</div><div class="r-stat-sub">brand voice inputs</div></div>
    <div class="r-stat"><div class="r-stat-num">${report.signal_coverage?.reviews_analyzed ?? 0}</div><div class="r-stat-label">Reviews Analyzed</div><div class="r-stat-sub">customer voice pool</div></div>
    <div class="r-stat"><div class="r-stat-num">${report.signal_coverage?.social_comments_analyzed ?? 0}</div><div class="r-stat-label">Video Comments</div><div class="r-stat-sub">included in reviews</div></div>
    <div class="r-stat"><div class="r-stat-num">${report.signal_coverage?.google_queries_analyzed ?? 0}</div><div class="r-stat-label">Pre-Purchase Queries</div><div class="r-stat-sub">search intent signals</div></div>
    <div class="r-stat r-stat--total"><div class="r-stat-num">${report.signal_coverage ? computeTotalSignals(report.signal_coverage) : "—"}</div><div class="r-stat-label">Total Signals</div><div class="r-stat-sub">all inputs analyzed</div></div>
  </div>
  <div class="r-three-col">
    <div class="r-three-col-box"><div class="r-three-col-label">Top Signals</div>${sl(sb.top_signals).map(c => `<div class="r-three-col-item">${ns(c)}</div>`).join("")}</div>
    <div class="r-three-col-box"><div class="r-three-col-label">Dominant Emotions</div>${sl(sb.dominant_emotions).map(e => `<div class="r-three-col-item">${s(e)}</div>`).join("")}</div>
    <div class="r-three-col-box"><div class="r-three-col-label">Key Buying Drivers</div>${sl(sb.key_buying_drivers).map(d => `<div class="r-three-col-item">${s(d)}</div>`).join("")}</div>
  </div>
  ${report.signal_coverage && sl(report.signal_coverage.sources).length ? `<div class="r-source-tags" style="margin-top:20px;">${sl(report.signal_coverage.sources).map(src => `<div class="r-source-tag">${s(src)}</div>`).join("")}</div>` : ""}
</div>

<div class="r-section r-section--tight" id="customer-language">
  <div class="r-label">Real Customer Language</div>
  <div class="r-title">What Customers Actually Say</div>
  <div class="r-lang-grid">
    ${Object.entries(lang).filter(([, quotes]) => quotes.length > 0).length > 0
      ? Object.entries(lang).filter(([, quotes]) => quotes.length > 0).map(([theme, quotes]) => `
        <div class="r-lang-group">
          <div class="r-lang-label">${s(theme.replace(/_/g, " ").toUpperCase())}</div>
          ${quotes.map(q => `<div class="r-lang-quote">"${ns(q)}"</div>`).join("")}
        </div>`).join("")
      : `<div class="r-bullet" style="grid-column:1/-1">Insufficient verbatim quotes found in this run.</div>`
    }
  </div>
</div>

${sl(report.signal_patterns).length ? `
<div class="r-section r-section--tight r-section--gray" id="signal-patterns">
  <div class="r-label">Signal Patterns</div>
  <div class="r-title">Recurring Customer Patterns</div>
  ${sl(report.signal_patterns).map(p => `
  <div class="r-pattern">
    <div class="r-pattern-head"><div class="r-pattern-name">${s(p.pattern_name)}</div></div>
    <div class="r-pattern-body">
      <div class="r-pattern-desc">${s(p.description)}</div>
      ${sl(p.repeated_phrases).length ? `<div><div class="r-pattern-section-label">Repeated Phrases</div><div class="r-pattern-phrases">${sl(p.repeated_phrases).map(ph => `<span class="r-pattern-phrase">"${ns(ph)}"</span>`).join("")}</div></div>` : ""}
      <div><div class="r-pattern-section-label">Impact</div><div class="r-pattern-impact">${s(p.impact)}</div></div>
    </div>
  </div>`).join("")}
</div>` : ""}

${sl(report.campaign_diagnosis).length ? `
<div class="r-section r-section--tight" id="campaign-diagnosis">
  <div class="r-label">Campaign Diagnosis</div>
  <div class="r-title">Where Messaging Can Improve</div>
  ${sl(report.campaign_diagnosis).map(d => `
  <div class="r-diag">
    <div class="r-diag-head"><div class="r-diag-type">${s(d.failure_type)}</div></div>
    <div class="r-diag-body">
      <div class="r-diag-def">${s(d.definition)}</div>
      ${sl(d.signals).length ? `<div><div class="r-diag-section-label">Signals That Triggered This</div><div class="r-diag-sigs">${sl(d.signals).map(sig => `<div class="r-diag-sig">"${ns(sig)}"</div>`).join("")}</div></div>` : ""}
      <div><div class="r-diag-section-label">Implication</div><div class="r-diag-impl">${s(d.implication)}</div></div>
    </div>
  </div>`).join("")}
</div>` : ""}

${report.market_insight ? `
<div class="r-section r-section--tight r-section--gray" id="market-insight">
  <div class="r-label">Market Insight</div>
  <div class="r-market-insight"><div class="r-market-insight-label">Market Context</div><div class="r-market-insight-text">${s(report.market_insight)}</div></div>
</div>` : ""}

${(report.signals_summary || report.evidence_summary) ? `
<div class="r-section r-section--tight" id="signals-summary">
  <div class="r-label">Signals Summary</div>
  <div class="r-bullets">
    ${report.signals_summary ? `<div class="r-bullet">${s(report.signals_summary)}</div>` : ""}
    ${report.evidence_summary ? `<div class="r-bullet">${s(report.evidence_summary)}</div>` : ""}
  </div>
</div>` : ""}

<div class="r-section r-section--gray" id="intent-clusters">
  <div class="r-label">Semantic Intent Clusters</div>
  <div class="r-title">Customer Mindset Across 4 Stages</div>
  ${[...sl(report.intent_clusters)].sort((a, b) => {
    const order = ["Problem Awareness", "Frustration", "Solution Exploration", "Decision"];
    return order.indexOf(a.intent_stage) - order.indexOf(b.intent_stage);
  }).map(cluster => renderCluster(cluster)).join("")}
</div>

<div class="r-section r-section--tight" id="public-evidence">
  <div class="r-label">Public Evidence</div>
  <div class="r-title">Evidence Found</div>
  ${knownActivityNotes.length ? `<div class="r-bullets" style="margin-bottom:16px;">${knownActivityNotes.map(n => `<div class="r-bullet">${s(n)}</div>`).join("")}</div>` : ""}
  ${filteredEvidenceNotes.length ? `<div class="r-bullets" style="margin-bottom:16px;">${filteredEvidenceNotes.map(n => `<div class="r-bullet">${s(n)}</div>`).join("")}</div>` : ""}
  ${sl(evidence?.evidenceItems).slice(0, 6).map(item => `
  <div class="r-evidence">
    ${item.type && item.type !== "general" ? `<div class="r-evidence-type">${s(item.type.toUpperCase())}</div>` : ""}
    <div class="r-evidence-title">${s(item.title)}</div>
    ${item.snippet ? `<div class="r-evidence-snippet">${s(item.snippet)}</div>` : ""}
    ${item.url ? `<div class="r-evidence-url">${s(item.url)}</div>` : ""}
  </div>`).join("")}
</div>

${signals ? `
<div class="r-section r-section--tight r-section--gray" id="brand-signals">
  <div class="r-label">Scraped Brand Signals</div>
  <div class="r-title">Website &amp; Technical Signals</div>
  <div class="r-brand-grid">
    ${[
      ["Domain", signals.domain], ["Forms", signals.formCount],
      ["Video Embeds", signals.videoEmbedCount],
      ["Testimonials Present", signals.testimonialsPresent ? "YES" : "NO"],
      ["Pricing Present", signals.pricingPresent ? "YES" : "NO"],
      ["Schema Present", signals.schemaPresent ? "YES" : "NO"],
      ["Social Platforms", (signals.socialPlatformsDetected || []).join(", ") || "None detected"],
      ["Title", signals.title], ["Primary H1", (signals.h1s || [])[0] || ""],
    ].map(([k, v]) => {
      const decoded = s(String(v || "")).split("&amp;ndash;").join("–").split("&amp;mdash;").join("—").split("&amp;amp;").join("&");
      return `<div class="r-brand-cell"><div class="r-brand-cell-label">${s(String(k))}</div><div class="r-brand-cell-value">${decoded}</div></div>`;
    }).join("")}
  </div>
</div>` : ""}

<div class="r-section r-section--tight" id="methodology">
  <div class="r-label">Methodology</div>
  <div class="r-title">How Signals Were Gathered</div>
  <div class="r-method-grid">
    ${[
      ["Website Signal Extraction", "We scrape the brand's homepage and key landing pages to extract messaging signals — headlines, CTAs, value propositions, product positioning, pricing language, and trust indicators."],
      ["Search Intent Signals", "We pull the questions people search — real queries, the questions they ask and the related searches around them — to map the exact language customers use at each stage of the decision journey."],
      ["Review &amp; Social Signal Analysis", "We analyze retail and review-site reviews, community forums, and video comments to surface real complaint language, emotional patterns, and unmet expectations."],
      ["Public Evidence Gathering", "We scan public sources — news, forums, competitor pages, and industry content — to identify market context, competitive messaging patterns, and strategic gaps the brand can exploit."],
      ["Semantic Intent Clustering", "All signals are structured into intent clusters mapped across four stages: Problem Awareness, Frustration, Solution Exploration, and Decision."],
      ["Output &amp; Application", "Clusters are designed for direct use in ad strategy, creative briefing, landing page development, and campaign sequencing."],
    ].map(([label, text]) => `<div class="r-method"><div class="r-method-label">${label}</div><div class="r-method-text">${text}</div></div>`).join("")}
  </div>
</div>

</main>

</div>
<a href="#" class="r-back-to-top" id="rBackToTop" aria-label="Back to top">↑</a>
<script>
(function() {
  var navLinks = document.querySelectorAll('.r-side-nav-link');
  var sections = [];
  navLinks.forEach(function(link) {
    var id = link.getAttribute('href').substring(1);
    var el = document.getElementById(id);
    if (el) sections.push({ id: id, el: el, link: link });
  });
  var btn = document.getElementById('rBackToTop');
  if (btn) {
    btn.addEventListener('click', function(e) {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }
  function updateActive() {
    var scrollPos = window.scrollY + 120;
    if (btn) { btn.classList.toggle('visible', window.scrollY > 400); }
    if (!sections.length) return;
    var current = sections[0];
    for (var i = 0; i < sections.length; i++) {
      if (sections[i].el.offsetTop <= scrollPos) current = sections[i];
      else break;
    }
    sections.forEach(function(s) { s.link.classList.toggle('active', s === current); });
  }
  updateActive();
  window.addEventListener('scroll', updateActive, { passive: true });
})();
</script>
</body>
</html>`;
}
// ─── SECTION 4b: generateExecutiveSummaryHTML ────────────────────────────────

export function generateExecutiveSummaryHTML(report, signals, uuid) {
  const s = (v) => String(v || "").replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;").replace(/"/g,"&quot;");
  const sl = (v) => Array.isArray(v) ? v : [];
  const generated = report.generated_at ? new Date(report.generated_at).toLocaleString() : new Date().toLocaleString();

  const execVerdict = report.exec_summary_verdict || {};
  const verdictText = (execVerdict.verdict || report.strategic_verdict || report.signals_summary || "Signal analysis complete.").replace(/^STRATEGIC VERDICT:\s*/i, "");
  const signalProfile = execVerdict.signal_profile || "";
  const stageSentiment = execVerdict.stage_sentiment || {
    "Problem Awareness": "negative", "Frustration": "negative",
    "Solution Exploration": "mixed", "Decision": "positive",
  };

  const complaintPattern = sl(execVerdict.complaint_pattern || []);
  const BADGE_COLORS = {
  CHRONIC:    { bg: '#3d0f0f', color: '#f87171', border: '#7f1d1d' },
  EMERGING:   { bg: '#1a1f0a', color: '#a3e635', border: '#365314' },
  HISTORICAL: { bg: '#0f1a2a', color: '#60a5fa', border: '#1e3a5f' },
  };
  
  const stageSeverity = execVerdict.stage_severity || {
    "Problem Awareness": 45, "Frustration": 82, "Solution Exploration": 58, "Decision": 35,
  };
  const stageSignalCountsFromReport = execVerdict.stage_signal_counts || null;

  const STAGE_ORDER = ["Problem Awareness", "Frustration", "Solution Exploration", "Decision"];
  const STAGE_COLORS = {
    "Problem Awareness": "#3b82f6", "Frustration": "#ef4444",
    "Solution Exploration": "#f59e0b", "Decision": "#10b981",
  };
  const STAGE_NUMS = {
    "Problem Awareness": "01", "Frustration": "02",
    "Solution Exploration": "03", "Decision": "04",
  };

  const clusters = sl(report.intent_clusters);
  const stageSignalCounts = {};
  STAGE_ORDER.forEach(st => { stageSignalCounts[st] = 0; });
  if (stageSignalCountsFromReport) {
    STAGE_ORDER.forEach(st => { stageSignalCounts[st] = stageSignalCountsFromReport[st] || 0; });
  } else {
    clusters.forEach(c => {
      const stage = STAGE_ORDER.find(st => (c.intent_stage || "").includes(st.split(" ")[0])) || c.intent_stage;
      if (stage && stageSignalCounts[stage] !== undefined) {
        stageSignalCounts[stage] += sl(c.real_customer_signals).length + sl(c.user_queries).length;
      }
    });
  }
  const maxStageCount = Math.max(...Object.values(stageSignalCounts), 1);
  const stageBarWidths = {};
  STAGE_ORDER.forEach(st => { stageBarWidths[st] = Math.round((stageSignalCounts[st] / maxStageCount) * 100); });

  const cov = report.signal_coverage || {};
  const sb = report.signal_breakdown || {};
  const totalSignals = computeTotalSignals(cov);
  const adIntelAds = sl(report.ad_intel?.ads);
  const googleAdsCount = adIntelAds.filter(a => a.format === "text" || a.format === "image").length;
  const youtubeAdsCount = adIntelAds.filter(a => a.format === "video").length;
  // Kill switch (Session 17): legacy reports without sources_enabled behave as
  // before (all-on). New reports filter out operator-disabled sources entirely
  // — distinct from "ran and got zero" which still renders as zero.
  const enabled = report.sources_enabled || { amazon: true, walmart: true, trustpilot: true, reddit: true, youtube: true, paa: true };
  const reviewSiteCount =
    (enabled.amazon ? (cov.amazon_count || 0) : 0) +
    (enabled.trustpilot ? (cov.trustpilot_count || 0) : 0) +
    (enabled.walmart ? (cov.walmart_count || 0) : 0);
  const sources = [
    ...(cov.website_signals_extracted > 0 ? [{ name: "Brand Site", count: cov.website_signals_extracted, color: "#60a5fa", icon: "B" }] : []),
    // Source types, not platforms: the review sites sum into one card, with
    // the same per-platform gates and counts as before.
    ...(reviewSiteCount > 0 ? [{ name: "Retail and review-site reviews", count: reviewSiteCount, color: "#10b981", icon: "R" }] : []),
    ...(enabled.youtube ? [{ name: "Video comments and viewing trends", count: cov.youtube_count || cov.social_comments_analyzed || sb.social_comments || 0, color: "#ef4444", icon: "V" }] : []),
    ...(enabled.reddit && cov.reddit_count > 0 ? [{ name: "Community forums", count: cov.reddit_count, color: "#a855f7", icon: "F" }] : []),
    ...(enabled.paa ? [{ name: "The questions people search", count: cov.google_queries_analyzed || 0, color: "#3b82f6", icon: "Q" }] : []),
  ];
  const maxSourceCount = Math.max(...sources.map(src => src.count), 1);

  const cbi = report.conversion_barrier_index || {};
  const cbiScore = cbi.score !== null && cbi.score !== undefined ? cbi.score : null;
  const cbiColor = cbiScore === null ? "#60a5fa" : cbiScore >= 60 ? "#f87171" : cbiScore >= 30 ? "#fbbf24" : "#4ade80";
  const cbiDisplay = cbiScore !== null ? `${cbiScore}` : "—";
  const cbiUnit = cbiScore === null ? "insufficient decision-stage signals"
    : cbiScore >= 60 ? "most buyers have unresolved objections at the decision stage"
    : cbiScore >= 30 ? "some buyers are stalling at the decision stage"
    : "buyers at the decision stage are not stuck";

  const momentum = report.signal_momentum || {};
  const momentumTrend = momentum.trend || "insufficient_data";
  const momentumColor = momentumTrend === "deteriorating" ? "#f87171" : momentumTrend === "improving" ? "#4ade80" : momentumTrend === "stable" ? "#fbbf24" : "#60a5fa";
  const momentumDisplay = momentumTrend === "insufficient_data" ? "—" : momentumTrend === "deteriorating" ? "↑" : momentumTrend === "improving" ? "↓" : "→";
  const momentumUnit = momentumTrend === "insufficient_data" ? "not enough dated signals to calculate"
    : momentumTrend === "deteriorating" ? "recent complaints are more severe than the historical average"
    : momentumTrend === "improving" ? "recent complaints are less severe than the historical average"
    : "complaint severity is consistent with the historical average";

  const geoCard = report.geo_signal?.insight ? (() => {
    const regions = report.geo_signal.top_regions || [];
    const geoDataJS = JSON.stringify(regions.reduce((acc, r) => { acc[r.region] = r.score; return acc; }, {}));
    return `
<div class="geo-section">
  <div class="panel-label">Geographic search signal</div>
  <div class="geo-card">
    <div class="geo-keyword">Keyword: ${s(report.geo_signal.keyword)}</div>
    <div id="geo-map-wrap" style="width:100%;position:relative;">
      <div id="geo-tip" style="position:absolute;background:#080f1c;border:1px solid #243568;color:#f0f4ff;font-size:12px;padding:6px 10px;border-radius:6px;pointer-events:none;opacity:0;transition:opacity 0.12s;white-space:nowrap;z-index:10;"></div>
    </div>
    <div style="display:flex;gap:20px;margin-top:12px;flex-wrap:wrap;">
      <div style="display:flex;align-items:center;gap:6px;font-family:'DM Mono',monospace;font-size:11px;color:#7a9cc8;"><div style="width:12px;height:12px;border-radius:3px;background:#22c55e;"></div>Peak</div>
      <div style="display:flex;align-items:center;gap:6px;font-family:'DM Mono',monospace;font-size:11px;color:#7a9cc8;"><div style="width:12px;height:12px;border-radius:3px;background:#eab308;"></div>Strong</div>
      <div style="display:flex;align-items:center;gap:6px;font-family:'DM Mono',monospace;font-size:11px;color:#7a9cc8;"><div style="width:12px;height:12px;border-radius:3px;background:#60a5fa;"></div>Active</div>
      <div style="display:flex;align-items:center;gap:6px;font-family:'DM Mono',monospace;font-size:11px;color:#7a9cc8;"><div style="width:12px;height:12px;border-radius:3px;background:#3b82f6;"></div>Low</div>
      <div style="display:flex;align-items:center;gap:6px;font-family:'DM Mono',monospace;font-size:11px;color:#7a9cc8;"><div style="width:12px;height:12px;border-radius:3px;background:#ef4444;"></div>Minimal</div>
    </div>
    <div style="font-family:'DM Mono',monospace;font-size:10px;color:#4a6a99;letter-spacing:.1em;margin-top:12px;margin-bottom:2px;">NOTE: Tiers are relative to this dataset. Scores reflect relative search interest by state, not absolute search volume.</div>
    <div class="geo-insight">${s(report.geo_signal.insight)}</div>
  </div>
</div>
<script>
(function() {
  var geoData = ${geoDataJS};
  function computeThresholds(data) {
    var scores = Object.values(data).filter(function(s) { return s != null; }).sort(function(a, b) { return b - a; });
    var n = scores.length;
    if (!n) return null;
    var f = Math.floor(n / 5);
    return {
      peak:   scores[Math.min(f - 1, n - 1)],
      strong: scores[Math.min(f * 2 - 1, n - 1)],
      active: scores[Math.min(f * 3 - 1, n - 1)],
      low:    scores[Math.min(f * 4 - 1, n - 1)]
    };
  }
  var thresholds = computeThresholds(geoData);
  function tierColor(score) {
    if (score == null || !thresholds) return '#162040';
    if (score >= thresholds.peak)   return '#22c55e';
    if (score >= thresholds.strong) return '#eab308';
    if (score >= thresholds.active) return '#60a5fa';
    if (score >= thresholds.low)    return '#3b82f6';
    return '#ef4444';
  }
  function tierLabel(score) {
    if (!score || !thresholds) return 'No data';
    if (score >= thresholds.peak)   return 'Peak';
    if (score >= thresholds.strong) return 'Strong';
    if (score >= thresholds.active) return 'Active';
    if (score >= thresholds.low)    return 'Low';
    return 'Minimal';
  }
  function loadScript(src, cb) { var s = document.createElement('script'); s.src = src; s.onload = cb; document.head.appendChild(s); }
  function buildMap() {
    var wrap = document.getElementById('geo-map-wrap');
    var tip = document.getElementById('geo-tip');
    if (!wrap) return;
    var W = 900, H = 560;
    var svg = d3.select('#geo-map-wrap').append('svg').attr('viewBox', '0 0 ' + W + ' ' + H).attr('width', '100%');
    var proj = d3.geoAlbersUsa().scale(1150).translate([W/2, H/2]);
    var path = d3.geoPath(proj);
    d3.json('https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json').then(function(us) {
      svg.selectAll('path').data(topojson.feature(us, us.objects.states).features).join('path')
        .attr('d', path)
        .attr('fill', function(d) { return tierColor(geoData[d.properties.name]); })
        .attr('stroke', '#0d1526').attr('stroke-width', 0.8)
        .on('mousemove', function(event, d) {
          var name = d.properties.name; var score = geoData[name];
          var label = score ? name + ' · ' + score + ' · ' + tierLabel(score) : name + ' · no data';
          var rect = wrap.getBoundingClientRect();
          tip.style.opacity = '1';
          tip.style.left = (event.clientX - rect.left + 12) + 'px';
          tip.style.top = (event.clientY - rect.top - 36) + 'px';
          tip.textContent = label;
        })
        .on('mouseleave', function() { tip.style.opacity = '0'; });
    }).catch(function() {});
  }
  loadScript('https://cdnjs.cloudflare.com/ajax/libs/d3/7.8.5/d3.min.js', function() {
    loadScript('https://cdnjs.cloudflare.com/ajax/libs/topojson/3.0.2/topojson.min.js', buildMap);
  });
})();
</script>`;
  })() : "";

  function severityConfig(score) {
    if (score >= 70) return { bg: "#1a0505", textColor: "#f87171", label: "Critical", borderColor: "#7f1d1d" };
    if (score >= 50) return { bg: "#1a1000", textColor: "#fbbf24", label: "Elevated", borderColor: "#78350f" };
    if (score >= 30) return { bg: "#0a1a10", textColor: "#4ade80", label: "Moderate", borderColor: "#14532d" };
    return { bg: "#080f1c", textColor: "#60a5fa", label: "Low", borderColor: "#1e3a5f" };
  }
  function sentimentConfig(label) {
    const l = (label || "").toLowerCase();
    if (l === "positive") return { tag: "Positive", tagBg: "#0f3d2a", tagColor: "#4ade80", meterColor: "#10b981", meterWidth: 72 };
    if (l === "negative") return { tag: "Negative", tagBg: "#3d0f0f", tagColor: "#f87171", meterColor: "#ef4444", meterWidth: 85 };
    return { tag: "Mixed", tagBg: "#2a1f0a", tagColor: "#fbbf24", meterColor: "#f59e0b", meterWidth: 55 };
  }

  function generateFunnelDiagnosis(severity) {
    const pa = severity["Problem Awareness"] || 0;
    const fr = severity["Frustration"] || 0;
    const se = severity["Solution Exploration"] || 0;
    const de = severity["Decision"] || 0;
    const scores = { "Problem Awareness": pa, "Frustration": fr, "Solution Exploration": se, "Decision": de };
    const sorted = Object.entries(scores).sort((a, b) => b[1] - a[1]);
    const [topStage, topScore] = sorted[0];
    const [secondStage, secondScore] = sorted[1];
    const gap = topScore - secondScore;
    if (fr >= 60 && de >= 60) return `Both Frustration and Decision severity are elevated. Customers are encountering friction mid-funnel and carrying it to the purchase moment — this compound pattern is the highest-cost scenario for paid acquisition.`;
    if (gap < 12) return `Friction is distributed across all four stages — no single chokepoint dominates. Signals suggest compounding drop-off rather than one stage breaking the funnel.`;
    if (topStage === "Frustration") {
      if (de >= 50) return `Frustration is the dominant signal and buyers are still converting despite it — which means the product works, but unresolved friction is raising acquisition cost on every campaign running into this audience.`;
      return `Frustration is the dominant signal. Customers are entering the funnel and encountering a wall — the category promise is not matching the product or messaging experience, and that gap is suppressing downstream conversion.`;
    }
    if (topStage === "Decision") return `Severity peaks at the Decision stage. Buyers are reaching the wire and stalling — objections are not being resolved before the purchase moment, which means spend is generating interest that isn't closing.`;
    if (topStage === "Problem Awareness") return `Severity is highest at Problem Awareness. The category itself carries unresolved friction — customers arrive already skeptical, before the brand has had a chance to speak. Messaging needs to start earlier in the journey.`;
    if (topStage === "Solution Exploration") return `Severity peaks during Solution Exploration. Customers are actively comparing options and the brand is not winning that comparison — competitive positioning and proof points are the highest-leverage fix.`;
    if (fr >= 60 && de >= 60) return `Both Frustration and Decision severity are elevated. Customers are encountering friction mid-funnel and carrying it to the purchase moment — this compound pattern is the highest-cost scenario for paid acquisition.`;
    return `${topStage} carries the highest severity in this dataset at ${topScore}. Signals at this stage represent the primary friction point in the current funnel.`;
  }
  
  const stageDistBars = STAGE_ORDER.filter(stage => stageSignalCounts[stage] > 0).map(stage => `
      <div class="dist-row">
        <div class="dist-name">${s(stage)}</div>
        <div class="dist-track"><div class="dist-fill" style="background:${STAGE_COLORS[stage]};" data-width="${stageBarWidths[stage]}"></div></div>
        <div class="dist-count">${stageSignalCounts[stage]}</div>
      </div>`).join("");

  const sentimentMeters = STAGE_ORDER.map(stage => {
    const cfg = sentimentConfig(stageSentiment[stage]);
    return `
      <div class="sent-row">
        <div class="sent-header">
          <span class="sent-name">${s(stage)}</span>
          <span class="sent-tag" style="background:${cfg.tagBg};color:${cfg.tagColor};">${cfg.tag}</span>
        </div>
        <div class="meter-track"><div class="meter-bar" style="background:${cfg.meterColor};" data-width="${cfg.meterWidth}"></div></div>
      </div>`;
  }).join("");

  // Source Breakdown carries a source-attribution note here and in
  // ntentify-viewer/src/generators/ExecutiveSummary.jsx (kept in sync by hand —
  // same text, same panel). public/generators.js, public/reports.html, and
  // ntentify-reports/index.html are legacy copies that predate this note and
  // are deliberately left without one.
  const sourceCards = sources.map(src => `
      <div class="source-card">
        <div class="source-header">
          <div class="source-icon" style="background:${src.color}22;color:${src.color};">${s(src.icon)}</div>
          <span class="source-name">${s(src.name)}</span>
        </div>
        <div class="source-count" data-target="${src.count}" data-counter>0</div>
        <div class="source-bar-track"><div class="source-bar-fill" style="background:${src.color};" data-width="${Math.round((src.count / maxSourceCount) * 100)}"></div></div>
      </div>`).join("");

  const STAGE_DEFINITIONS = {
    "Problem Awareness": "Pre-purchase pain customers describe when entering the category.",
    "Frustration": "Product failures and let-downs customers experience after buying.",
    "Solution Exploration": "What's working, what's missing, what customers compare against.",
    "Decision": "Doubts, hesitations, and objections at or near the purchase moment."
  };
  const heatCells = STAGE_ORDER.map((stage, i) => {
    const score = stageSeverity[stage] || 20;
    const cfg = severityConfig(score);
    return `
      <div class="heat-cell" style="background:${cfg.bg};border:1px solid ${cfg.borderColor};" data-delay="${i * 120}">
        <div class="heat-stage" style="color:${cfg.textColor}88;">${s(STAGE_NUMS[stage])}</div>
        <div class="heat-stage-name" style="color:${cfg.textColor};">${s(stage.split(" ")[0])}</div>
        <div class="heat-score" style="color:${cfg.textColor};" data-target="${score}" data-counter>0</div>
        <div class="heat-label" style="color:${cfg.textColor}88;">${cfg.label}</div>
        <div class="heat-def" style="color:${cfg.textColor}aa;">${s(STAGE_DEFINITIONS[stage] || "")}</div>
      </div>`;
  }).join("");

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>${s(report.brand_name)} — Executive Summary</title>
<link href="https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Mono:wght@300;400;500&family=Inter:wght@300;400;500;600;700&display=swap" rel="stylesheet">
<style>
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
:root {
  --navy-deep: #080f1c; --navy-base: #0d1526; --navy-raised: #111d33;
  --navy-surface: #162040; --navy-edge: #1e2f55; --navy-mid: #243568;
  --blue: #2563eb; --blue-bright: #3b82f6; --blue-light: #60a5fa;
  --text-primary: #f0f4ff; --text-secondary: #a8c0e8;
  --text-muted: #7a9cc8; --text-faint: #4a6a99;
  --font-disp: 'Bebas Neue', sans-serif;
  --font-body: 'Inter', sans-serif;
  --font-mono: 'DM Mono', monospace;
  --pad: 48px;
}
html, body { background: var(--navy-deep); color: var(--text-primary); font-family: var(--font-body); font-size: 14px; line-height: 1.6; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.wrap { max-width: 960px; margin: 0 auto; padding-bottom: 80px; }
.cover { background: var(--navy-deep); padding: 52px var(--pad) 44px; border-bottom: 1px solid var(--navy-edge); position: relative; overflow: hidden; }
.cover::after { content: ''; position: absolute; top: 0; right: 0; width: 500px; height: 100%; background: radial-gradient(ellipse at 80% 50%, rgba(37,99,235,0.1) 0%, transparent 70%); pointer-events: none; }
.cover-eyebrow { font-family: var(--font-mono); font-size: 10px; letter-spacing: .22em; text-transform: uppercase; color: var(--text-faint); margin-bottom: 14px; position: relative; display: flex; align-items: center; gap: 10px; }
.cover-pulse { width: 7px; height: 7px; border-radius: 50%; background: var(--blue-bright); animation: pulse 2s infinite; flex-shrink: 0; }
@keyframes pulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.4;transform:scale(.85)} }
.cover-title { font-family: var(--font-disp); font-size: 68px; line-height: 1; letter-spacing: -.01em; color: var(--text-primary); position: relative; margin-bottom: 4px; }
.cover-brand { font-family: var(--font-disp); font-size: 34px; letter-spacing: .02em; color: var(--blue-light); position: relative; margin-bottom: 32px; }
.cover-meta { font-family: var(--font-mono); font-size: 10px; color: var(--text-faint); position: relative; border-top: 1px solid var(--navy-edge); padding-top: 20px; display: flex; gap: 36px; flex-wrap: wrap; }
.cover-meta-stat strong { display: block; font-family: var(--font-disp); font-size: 30px; color: var(--text-primary); line-height: 1; margin-bottom: 2px; }
.stat-strip { background: var(--navy-raised); border-bottom: 1px solid var(--navy-edge); padding: 0 var(--pad); display: grid; grid-template-columns: repeat(auto-fit, minmax(100px, 1fr)); }
.stat-block { padding: 28px 16px; border-right: 1px solid var(--navy-edge); }
.stat-block:last-child { border-right: none; }
.stat-label { font-family: var(--font-mono); font-size: 10px; letter-spacing: .16em; text-transform: uppercase; color: var(--text-secondary); margin-bottom: 8px; line-height: 1.3; min-height: 26px; display: flex; align-items: flex-end; }
.stat-num { font-family: var(--font-disp); font-size: 52px; line-height: 1; color: var(--text-primary); letter-spacing: .01em; }
.stat-num.blue { color: var(--blue-bright); }
.stat-num.amber { color: #fbbf24; }
.stat-num.red { color: #f87171; }
.stat-num.green { color: #4ade80; }
.stat-unit { font-family: var(--font-mono); font-size: 10px; color: var(--text-secondary); margin-top: 4px; line-height: 1.3; }
.panel-row { display: grid; grid-template-columns: 1fr 1fr; gap: 0; border-bottom: 1px solid var(--navy-edge); }
.panel { background: var(--navy-base); padding: 32px var(--pad); border-right: 1px solid var(--navy-edge); }
.panel:last-child { border-right: none; }
.panel-label { font-family: var(--font-mono); font-size: 11px; letter-spacing: .2em; text-transform: uppercase; color: var(--text-secondary); margin-bottom: 20px; padding-bottom: 10px; border-bottom: 1px solid var(--navy-edge); }
.dist-row { display: flex; align-items: center; gap: 10px; margin-bottom: 16px; }
.dist-row:last-child { margin-bottom: 0; }
.dist-name { font-size: 13px; color: var(--text-primary); width: 140px; flex-shrink: 0; }
.dist-track { flex: 1; height: 5px; background: var(--navy-edge); border-radius: 3px; overflow: hidden; }
.dist-fill { height: 100%; border-radius: 3px; width: 0%; transition: width 1.2s cubic-bezier(.4,0,.2,1); }
.dist-count { font-family: var(--font-mono); font-size: 12px; color: var(--text-secondary); width: 28px; text-align: right; flex-shrink: 0; }
.sent-row { margin-bottom: 18px; }
.sent-row:last-child { margin-bottom: 0; }
.sent-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px; }
.sent-name { font-size: 13px; color: var(--text-primary); }
.sent-tag { font-family: var(--font-mono); font-size: 11px; letter-spacing: .06em; padding: 3px 8px; border-radius: 2px; }
.meter-track { height: 4px; background: var(--navy-edge); border-radius: 2px; overflow: hidden; }
.meter-bar { height: 100%; border-radius: 2px; width: 0%; transition: width 1.4s cubic-bezier(.4,0,.2,1); }
.source-grid { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 12px; }
.source-card { background: var(--navy-raised); border: 1px solid var(--navy-edge); border-radius: 4px; padding: 16px; }
.source-header { display: flex; align-items: center; gap: 10px; margin-bottom: 12px; }
.source-icon { width: 28px; height: 28px; border-radius: 4px; display: flex; align-items: center; justify-content: center; font-family: var(--font-mono); font-size: 11px; font-weight: 500; flex-shrink: 0; }
.source-name { font-size: 13px; color: var(--text-primary); }
.source-count { font-family: var(--font-disp); font-size: 36px; color: var(--text-primary); line-height: 1; margin-bottom: 10px; }
.source-bar-track { height: 3px; background: var(--navy-edge); border-radius: 2px; overflow: hidden; }
.source-bar-fill { height: 100%; border-radius: 2px; width: 0%; transition: width 1.6s cubic-bezier(.4,0,.2,1); }
.heat-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; }
.heat-cell { border-radius: 4px; padding: 20px 16px; text-align: center; opacity: 0; transform: translateY(10px); transition: opacity .5s ease, transform .5s ease; }
.heat-cell.visible { opacity: 1; transform: translateY(0); }
.heat-def { font-family: var(--font-mono); font-size: 10px; line-height: 1.5; letter-spacing: .02em; margin-top: 12px; padding-top: 10px; border-top: 1px solid rgba(255,255,255,0.06); text-align: left; }
.panel-explainer { font-family: var(--font-body); font-size: 12px; line-height: 1.6; color: var(--text-muted); margin-bottom: 16px; max-width: 540px; }
.heat-scale { font-family: var(--font-mono); font-size: 10px; letter-spacing: .14em; text-transform: uppercase; color: var(--text-muted); margin-top: 14px; padding-top: 10px; border-top: 1px solid var(--navy-edge); text-align: center; }
.heat-stage { font-family: var(--font-mono); font-size: 18px; font-weight: 300; line-height: 1; margin-bottom: 2px; }
.heat-stage-name { font-family: var(--font-mono); font-size: 11px; letter-spacing: .1em; text-transform: uppercase; margin-bottom: 10px; }
.heat-score { font-family: var(--font-disp); font-size: 42px; line-height: 1; margin-bottom: 4px; }
.heat-label { font-family: var(--font-mono); font-size: 11px; letter-spacing: .08em; text-transform: uppercase; }
.geo-section { padding: 32px var(--pad); background: var(--navy-base); border-bottom: 1px solid var(--navy-edge); }
.vc-exec-section { padding: 28px var(--pad); background: var(--navy-base); border-bottom: 1px solid var(--navy-edge); }
.vc-exec-header { display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 4px; gap: 16px; flex-wrap: wrap; padding-bottom: 10px; border-bottom: 1px solid var(--navy-edge); }
.vc-exec-label { font-family: var(--font-mono); font-size: 11px; letter-spacing: .2em; text-transform: uppercase; color: var(--text-secondary); }
.vc-exec-context-right { font-family: var(--font-mono); font-size: 11px; color: var(--text-muted); letter-spacing: .04em; }
.vc-exec-context { font-family: var(--font-mono); font-size: 12px; color: var(--text-muted); margin: 16px 0 14px; }
.vc-exec-bar { display: flex; height: 14px; border-radius: 3px; overflow: hidden; background: var(--navy-edge); margin-bottom: 14px; }
.vc-exec-bar-pos { background: #5DCAA5; transition: width 1.2s cubic-bezier(.4,0,.2,1); }
.vc-exec-bar-mix { background: #94a3b8; transition: width 1.2s cubic-bezier(.4,0,.2,1); }
.vc-exec-bar-neg { background: #F09595; transition: width 1.2s cubic-bezier(.4,0,.2,1); }
.vc-exec-legend { display: flex; gap: 24px; flex-wrap: wrap; font-family: var(--font-mono); font-size: 11px; color: var(--text-primary); letter-spacing: .04em; }
.vc-exec-legend-item { display: flex; align-items: center; gap: 8px; }
.vc-exec-dot { width: 10px; height: 10px; border-radius: 50%; display: inline-block; }
.vc-exec-dot-pos { background: #5DCAA5; }
.vc-exec-dot-mix { background: #94a3b8; }
.vc-exec-dot-neg { background: #F09595; }
.geo-card { background: var(--navy-raised); border: 1px solid var(--navy-edge); padding: 20px 24px; }
.geo-keyword { font-family: var(--font-mono); font-size: 10px; letter-spacing: .16em; text-transform: uppercase; color: var(--text-muted); margin-bottom: 16px; }
.geo-insight { font-size: 14px; line-height: 1.75; color: var(--text-secondary); border-top: 1px solid var(--navy-edge); padding-top: 14px; }
.signal-data-section { padding: 32px var(--pad); background: var(--navy-base); border-top: 1px solid var(--navy-edge); }
.signal-data-label { font-family: var(--font-mono); font-size: 13px; letter-spacing: .22em; text-transform: uppercase; color: var(--text-secondary); margin-bottom: 20px; padding-bottom: 10px; border-bottom: 1px solid var(--navy-edge); }
.signal-data-list { display: grid; gap: 10px; }
.signal-data-item { display: grid; grid-template-columns: 24px 1fr; gap: 12px; align-items: start; padding: 12px 16px; background: var(--navy-raised); border: 1px solid var(--navy-edge); }
.signal-data-num { font-family: var(--font-disp); font-size: 20px; color: var(--blue-light); line-height: 1; margin-top: 2px; opacity: 0.5; }
.signal-data-text { font-size: 14px; color: var(--text-secondary); line-height: 1.6; }
.competitor-grid { display: flex; flex-wrap: wrap; gap: 8px; }
.competitor-tag { font-family: var(--font-mono); font-size: 12px; color: var(--blue-light); padding: 6px 14px; background: var(--navy-raised); border: 1px solid var(--navy-edge); letter-spacing: .06em; }
.verdict-section { padding: 40px var(--pad); background: var(--navy-base); border-top: 1px solid var(--navy-edge); display: grid; grid-template-columns: 1fr; gap: 16px; }
.profile-card { background: var(--navy-raised); border: 1px solid var(--navy-edge); border-left: 3px solid #10b981; padding: 28px 32px; opacity: 0; transition: opacity .8s ease 2.1s; }
.profile-card.visible { opacity: 1; }
.profile-label { font-family: var(--font-mono); font-size: 13px; letter-spacing: .22em; text-transform: uppercase; color: #4ade80; margin-bottom: 14px; }
.profile-text { font-size: 15px; line-height: 1.8; color: var(--text-secondary); }
.verdict-card { background: var(--navy-raised); border: 1px solid var(--navy-edge); border-left: 3px solid var(--blue); padding: 28px 32px; opacity: 0; transition: opacity .8s ease 1.8s; }
.verdict-card.visible { opacity: 1; }
.verdict-label { font-family: var(--font-mono); font-size: 13px; letter-spacing: .22em; text-transform: uppercase; color: var(--blue-light); margin-bottom: 14px; }
.verdict-text { font-size: 15px; line-height: 1.8; color: var(--text-secondary); }
.footer { padding: 24px var(--pad); background: var(--navy-deep); border-top: 1px solid var(--navy-edge); }
.footer-text { font-family: var(--font-mono); font-size: 10px; color: var(--text-faint); letter-spacing: .1em; line-height: 1.6; }
@media (max-width: 680px) {
  :root { --pad: 24px; }
  .stat-strip { grid-template-columns: 1fr 1fr; }
  .panel-row { grid-template-columns: 1fr; }
  .panel { border-right: none; border-bottom: 1px solid var(--navy-edge); }
  .heat-grid { grid-template-columns: 1fr 1fr; }
}
@media print {
  @page { margin: 0; size: A4; }
  .heat-cell { opacity: 1 !important; transform: none !important; }
  .verdict-card { opacity: 1 !important; }
  .wrap { padding-bottom: 0; }
}
</style>
</head>
<body>
${generateWhiteLabelStyle(report)}
${generateNavBar(report.brand_name, "exec", uuid, report.white_label)}
<div class="wrap">

<div class="cover">
  <div class="cover-eyebrow"><div class="cover-pulse"></div>Ntentify · Semantic Intent Engine · Executive Summary</div>
  <div class="cover-title">SIGNAL DIAGNOSTIC</div>
  <div class="cover-brand">${s(report.brand_name)}</div>
  <div class="cover-meta">
    <div class="cover-meta-stat"><strong>${totalSignals}</strong>Total Signals</div>
    <div class="cover-meta-stat"><strong>4</strong>Funnel Stages</div>
    <div class="cover-meta-stat"><strong>${s(report.website)}</strong>Website</div>
    <div class="cover-meta-stat"><strong>${s(generated)}</strong>Generated</div>
  </div>
</div>

<div class="stat-strip">
  <div class="stat-block"><div class="stat-label">Total signals</div><div class="stat-num" data-target="${totalSignals}" data-counter>0</div><div class="stat-unit">all inputs analyzed</div></div>
  <div class="stat-block"><div class="stat-label">Intent clusters</div><div class="stat-num blue" data-target="4" data-counter>0</div><div class="stat-unit">funnel stages mapped</div></div>
  <div class="stat-block"><div class="stat-label">Reviews analyzed</div><div class="stat-num amber" data-target="${sb.reviews_analyzed || cov.reviews_analyzed || 0}" data-counter>0</div><div class="stat-unit">signals extracted</div></div>
  <div class="stat-block"><div class="stat-label">Video comments</div><div class="stat-num red" data-target="${cov.social_comments_analyzed || sb.social_comments || 0}" data-counter>0</div><div class="stat-unit">video comment signals</div></div>
  <div class="stat-block"><div class="stat-label">Conversion barrier</div><div class="stat-num" style="color:${cbiColor};" ${cbiScore !== null ? `data-target="${cbiScore}" data-counter` : ""}>${cbiScore !== null ? "0" : "—"}</div><div class="stat-unit">${s(cbiUnit)}</div></div>
  <div class="stat-block"><div class="stat-label">Signal momentum</div><div class="stat-num" style="color:${momentumColor};font-size:52px;">${s(momentumDisplay)}</div><div class="stat-unit">${s(momentumUnit)}</div></div>
  ${googleAdsCount > 0 ? `<div class="stat-block"><div class="stat-label">Search ads analyzed</div><div class="stat-num blue" data-target="${googleAdsCount}" data-counter>0</div><div class="stat-unit">search + display creatives</div></div>` : ""}
  ${youtubeAdsCount > 0 ? `<div class="stat-block"><div class="stat-label">Video ads analyzed</div><div class="stat-num red" data-target="${youtubeAdsCount}" data-counter>0</div><div class="stat-unit">video ad creatives</div></div>` : ""}
</div>

<div class="panel-row">
  <div class="panel"><div class="panel-label">Signal distribution — by funnel stage</div>${stageDistBars}</div>
  <div class="panel"><div class="panel-label">Sentiment — per stage</div>${sentimentMeters}</div>
</div>

<div class="panel-row">
  <div class="panel"><div class="panel-label">Source breakdown</div><div class="panel-explainer">Signals are extracted and classified from publicly available customer reviews, discussions, video, and search behavior. Each count is how many classified signals that source contributed. Direct quotations appear throughout as supporting evidence.</div><div class="source-grid">${sourceCards}</div></div>
  <div class="panel"><div class="panel-label">Signal intensity by stage</div><div class="panel-explainer">Scored 0–100 across the four stages of the buying journey. The higher the number, the louder and more intense the customer signal at that point.</div><div class="heat-grid">${heatCells}</div><div class="heat-scale">0–39 Low · 40–69 Elevated · 70–100 Critical</div></div>
</div>

${(() => {
  const pb = report.signal_polarity_breakdown;
  if (!pb || typeof pb !== "object") return "";
  const pos = pb.positive || 0;
  const mix = pb.mixed || 0;
  const neg = pb.negative || 0;
  const total = pos + mix + neg;
  if (total === 0) return "";
  const posPct = Math.round((pos / total) * 100);
  const negPct = Math.round((neg / total) * 100);
  const mixPct = 100 - posPct - negPct;
  const cov = report.signal_coverage || {};
  const reviewsCount = cov.reviews_analyzed || 0;
  const queriesCount = cov.google_queries_analyzed || 0;
  const contextRight = (reviewsCount > 0 || queriesCount > 0) ? `${reviewsCount > 0 ? `${reviewsCount} reviews` : ""}${reviewsCount > 0 && queriesCount > 0 ? " · " : ""}${queriesCount > 0 ? `${queriesCount} queries` : ""}` : "";
  return `
<div class="vc-exec-section">
  <div style="font-family:var(--font-mono);font-size:10px;letter-spacing:.22em;text-transform:uppercase;color:var(--blue-bright);margin-bottom:20px;padding-bottom:10px;border-bottom:1px solid var(--navy-edge);">Signal Analysis</div>
  <div class="vc-exec-header">
    <div>
      <div class="vc-exec-label">Voice Composition</div>
      <<div style="font-family:var(--font-mono);font-size:10px;color:var(--text-muted);letter-spacing:.06em;margin-top:3px;">Signal content polarity — what customers said, not how many stars they left</div>    </div>
    <div class="vc-exec-context-right">${s(contextRight)}</div>
  </div>
  <div class="vc-exec-context">Across ${total} classified signals</div>
  <div class="vc-exec-bar">
    <div class="vc-exec-bar-pos" style="width:${posPct}%"></div>
    <div class="vc-exec-bar-mix" style="width:${mixPct}%"></div>
    <div class="vc-exec-bar-neg" style="width:${negPct}%"></div>
  </div>
  <div class="vc-exec-legend">
    <div class="vc-exec-legend-item"><span class="vc-exec-dot vc-exec-dot-pos"></span>${posPct}% Positive</div>
    <div class="vc-exec-legend-item"><span class="vc-exec-dot vc-exec-dot-mix"></span>${mixPct}% Mixed</div>
    <div class="vc-exec-legend-item"><span class="vc-exec-dot vc-exec-dot-neg"></span>${negPct}% Negative</div>
  </div>
</div>`;
})()}

<div style="background:var(--navy-base);border-bottom:1px solid var(--navy-edge);padding:24px var(--pad);display:flex;align-items:flex-start;gap:20px;">
  <div style="font-family:var(--font-mono);font-size:10px;letter-spacing:.18em;text-transform:uppercase;color:var(--text-secondary);white-space:nowrap;padding-top:3px;flex-shrink:0;">Funnel Diagnosis</div>
  <div style="width:1px;background:var(--navy-edge);align-self:stretch;flex-shrink:0;"></div>
  <div style="font-size:14px;line-height:1.75;color:var(--text-secondary);max-width:680px;">${s(generateFunnelDiagnosis(stageSeverity))}</div>
</div>

${generateReviewTrendHTML(report)}
${complaintPattern.length ? `
<div style="background:var(--navy-base);border-bottom:1px solid var(--navy-edge);padding:24px var(--pad);">
  <div style="font-family:var(--font-mono);font-size:10px;letter-spacing:.18em;text-transform:uppercase;color:var(--text-secondary);margin-bottom:4px;padding-bottom:0;">Signal pattern</div>
  <div style="font-family:var(--font-mono);font-size:10px;color:var(--text-muted);letter-spacing:.06em;margin-bottom:16px;padding-bottom:10px;border-bottom:1px solid var(--navy-edge);">Patterns that persist across time periods, regardless of polarity</div>
  <div style="display:grid;gap:10px;">
    ${complaintPattern.map(p => {
      const cfg = BADGE_COLORS[p.badge] || BADGE_COLORS.HISTORICAL;
      return `<div style="display:grid;grid-template-columns:auto 1fr;gap:12px;align-items:start;padding:12px 16px;background:var(--navy-raised);border:1px solid var(--navy-edge);"><div style="font-family:var(--font-mono);font-size:10px;font-weight:600;letter-spacing:.14em;padding:4px 10px;background:${cfg.bg};color:${cfg.color};border:1px solid ${cfg.border};white-space:nowrap;">${s(p.badge)}</div><div><div style="font-size:13px;color:var(--text-primary);font-weight:600;margin-bottom:4px;">${s(p.complaint)}</div><div style="font-size:12px;color:var(--text-secondary);line-height:1.6;">${s(p.evidence)}</div></div></div>`;
    }).join('')}
  </div>
</div>` : ""}
${(execVerdict.verdict || report.strategic_verdict) ? `
<div style="background:var(--navy-raised);border-left:4px solid var(--blue);padding:28px var(--pad);border-bottom:1px solid var(--navy-edge);">
  <div style="font-family:var(--font-mono);font-size:10px;letter-spacing:.22em;text-transform:uppercase;color:var(--blue-light);margin-bottom:12px;">Highest Signal Opportunity</div>
  <div style="font-size:15px;line-height:1.75;color:var(--text-secondary);max-width:720px;">${s((execVerdict.verdict || report.strategic_verdict || "").replace(/^STRATEGIC VERDICT:\s*/i, ""))}</div>
</div>` : ""}

${report.market_context_exec ? (() => {
  const mce = report.market_context_exec;
  const mc = report.market_context || {};
  const STATE_CONFIG = {
    category_leader:        { color: "#4ade80", border: "rgba(74,222,128,0.3)",  bg: "rgba(74,222,128,0.08)",  label: "LEADER" },
    competing_in_category:  { color: "#60a5fa", border: "rgba(96,165,250,0.3)",  bg: "rgba(96,165,250,0.08)",  label: "COMPETING" },
    category_outsider:      { color: "#fbbf24", border: "rgba(251,191,36,0.3)",  bg: "rgba(251,191,36,0.08)",  label: "OUTSIDER" },
    category_invisible:     { color: "#f87171", border: "rgba(248,113,113,0.3)", bg: "rgba(248,113,113,0.08)", label: "INVISIBLE" },
    fractured_category:     { color: "#7a9cc8", border: "rgba(122,156,200,0.3)", bg: "rgba(122,156,200,0.08)", label: "FRAGMENTED" },
  };
  const cfg = STATE_CONFIG[mce.positioning_state] || STATE_CONFIG.category_invisible;
  const catVol = mc.category_total_volume ? mc.category_total_volume.toLocaleString() : "—";
  const brandedShare = (mc.branded_share_pct !== undefined && mc.branded_share_pct !== null) ? `${mc.branded_share_pct.toFixed(1)}%` : "—";
  const rank = mc.target_brand_rank ? `#${mc.target_brand_rank}` : "Not ranked";
  const kwCount = mc.total_keywords_analyzed ? mc.total_keywords_analyzed.toLocaleString() : "—";
  return `
<div style="background:var(--navy-base);border-bottom:1px solid var(--navy-edge);padding:32px var(--pad);">
  <div style="font-family:var(--font-mono);font-size:11px;letter-spacing:.2em;text-transform:uppercase;color:var(--text-secondary);margin-bottom:20px;padding-bottom:10px;border-bottom:1px solid var(--navy-edge);">Market Context — Category Positioning</div>
  <div style="display:grid;grid-template-columns:repeat(4,1fr);gap:0;background:var(--navy-raised);border:1px solid var(--navy-edge);border-radius:4px;margin-bottom:24px;">
    <div style="padding:18px 16px;border-right:1px solid var(--navy-edge);">
      <div style="font-family:var(--font-disp);font-size:32px;line-height:1;color:var(--blue-light);margin-bottom:6px;">${s(catVol)}</div>
      <div style="font-family:var(--font-mono);font-size:9px;color:var(--text-faint);letter-spacing:.16em;text-transform:uppercase;">Category Volume / Mo</div>
    </div>
    <div style="padding:18px 16px;border-right:1px solid var(--navy-edge);">
      <div style="font-family:var(--font-disp);font-size:32px;line-height:1;color:var(--blue-light);margin-bottom:6px;">${s(brandedShare)}</div>
      <div style="font-family:var(--font-mono);font-size:9px;color:var(--text-faint);letter-spacing:.16em;text-transform:uppercase;">Branded Share</div>
    </div>
    <div style="padding:18px 16px;border-right:1px solid var(--navy-edge);">
      <div style="font-family:var(--font-disp);font-size:32px;line-height:1;color:${cfg.color};margin-bottom:6px;">${s(rank)}</div>
      <div style="font-family:var(--font-mono);font-size:9px;color:var(--text-faint);letter-spacing:.16em;text-transform:uppercase;">Category Rank</div>
    </div>
    <div style="padding:18px 16px;">
      <div style="font-family:var(--font-disp);font-size:32px;line-height:1;color:var(--blue-light);margin-bottom:6px;">${s(kwCount)}</div>
      <div style="font-family:var(--font-mono);font-size:9px;color:var(--text-faint);letter-spacing:.16em;text-transform:uppercase;">Keywords Analyzed</div>
    </div>
  </div>
  <div style="font-size:14px;line-height:1.8;color:var(--text-secondary);max-width:780px;margin-bottom:24px;">${s(mce.positioning_paragraph)}</div>
  <div style="background:${cfg.bg};border:1px solid ${cfg.border};border-left:3px solid ${cfg.color};padding:20px 24px;">
    <div style="display:flex;align-items:center;gap:14px;margin-bottom:10px;flex-wrap:wrap;">
      <div style="font-family:var(--font-mono);font-size:10px;letter-spacing:.22em;text-transform:uppercase;color:${cfg.color};">Positioning Verdict</div>
      <div style="font-family:var(--font-mono);font-size:10px;letter-spacing:.16em;padding:3px 10px;background:${cfg.color}22;color:${cfg.color};border:1px solid ${cfg.border};border-radius:2px;">${s(cfg.label)}</div>
    </div>
    <div style="font-size:15px;font-weight:600;line-height:1.7;color:var(--text-primary);max-width:720px;">${s(mce.positioning_verdict)}</div>
  </div>
</div>`;
})() : ""}

${(() => {
  const sc = signals?.scripts || {};
  const infra = [
    { key: "metaPixel",  label: "META PIXEL",   sub: "PAID SOCIAL" },
    { key: "ga4",        label: "GA4",           sub: "ANALYTICS" },
    { key: "googleAds",  label: "GOOGLE ADS",    sub: "PAID SEARCH" },
    { key: "klaviyo",    label: "KLAVIYO",       sub: "EMAIL RETENTION" },
    { key: "tiktok",     label: "TIKTOK",        sub: "SHORT FORM" },
    { key: "hotjar",     label: "HOTJAR",        sub: "CRO / SESSION" },
  ];
  const active = infra.filter(i => sc[i.key]);
  const inactive = infra.filter(i => !sc[i.key]);

  let interpretation = "";
  if (active.length === 0) {
    interpretation = "No ad tracking pixels detected on the website. The brand may be running campaigns without conversion tracking, or tracking may be loaded dynamically.";
  } else {
    const parts = [];
    if (sc.metaPixel && sc.googleAds) parts.push("Meta Pixel and Google Ads are both firing, indicating paid social and search acquisition are running in parallel.");
    else if (sc.metaPixel) parts.push("Meta Pixel is active, indicating paid social is the primary acquisition channel.");
    else if (sc.googleAds) parts.push("Google Ads is active, indicating paid search is the primary acquisition channel.");
    if (sc.klaviyo) parts.push("Klaviyo confirms an email retention layer is active — ad traffic is being captured into a list.");
    if (sc.tiktok) parts.push("TikTok pixel detected, indicating short-form social creative is in play.");
    if (sc.hotjar) parts.push("Hotjar is active, meaning session recording and CRO testing are underway.");
    if (!sc.tiktok && !sc.hotjar) parts.push("No TikTok pixel or session recording detected — short-form creative and active CRO testing are not currently in play.");
    interpretation = parts.join(" ");
  }

  const cards = infra.map(item => {
    const on = sc[item.key];
    return on
      ? `<div style="background:#0d1f0d;border:1px solid #16a34a44;border-radius:4px;padding:16px 18px;">
          <div style="font-family:'DM Mono',monospace;font-size:10px;font-weight:600;color:#4ade80;margin:0 0 8px;letter-spacing:.12em;">${item.label}</div>
          <div style="font-family:'Bebas Neue',sans-serif;font-size:22px;color:#86efac;margin:0 0 6px;letter-spacing:.04em;">ACTIVE</div>
          <div style="font-family:'DM Mono',monospace;font-size:10px;color:#a8c0e8;margin:0;letter-spacing:.08em;">${item.sub}</div>
        </div>`
      : `<div style="background:#111d33;border:1px solid #1e2f55;border-radius:4px;padding:16px 18px;">
          <div style="font-family:'DM Mono',monospace;font-size:10px;font-weight:600;color:#7a9cc8;margin:0 0 8px;letter-spacing:.12em;">${item.label}</div>
          <div style="font-family:'Bebas Neue',sans-serif;font-size:22px;color:#4a6a99;margin:0 0 6px;letter-spacing:.04em;">NONE</div>
          <div style="font-family:'DM Mono',monospace;font-size:10px;color:#7a9cc8;margin:0;letter-spacing:.08em;">${item.sub}</div>
        </div>`;
  }).join("");

  return `
<div style="padding:32px var(--pad);background:var(--navy-base);border-bottom:1px solid var(--navy-edge);">
  <div style="font-family:'DM Mono',monospace;font-size:11px;letter-spacing:.2em;text-transform:uppercase;color:var(--text-secondary);margin-bottom:20px;padding-bottom:10px;border-bottom:1px solid var(--navy-edge);">Ad infrastructure detected</div>
  <div style="display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:10px;margin-bottom:16px;">${cards}</div>
  <div style="background:var(--navy-raised);border-left:3px solid #16a34a;border-radius:2px;padding:14px 16px;">
    <div style="font-size:13px;color:#f0f4ff;line-height:1.7;">${s(interpretation)}</div>
  </div>
</div>`;
})()}

${geoCard}

${sl(report.top_abandonment_reasons).length ? `
<div class="signal-data-section">
  <div class="signal-data-label">Why buyers stall</div>
  <div class="signal-data-list">
    ${sl(report.top_abandonment_reasons).slice(0, 6).map((item, i) => `
    <div class="signal-data-item"><div class="signal-data-num">${i + 1}</div><div class="signal-data-text">${s(item)}</div></div>`).join("")}
  </div>
</div>` : ""}

${sl(report.top_resolution_hints).length ? `
<div class="signal-data-section">
  <div class="signal-data-label">What would fix it</div>
  <div class="signal-data-list">
    ${sl(report.top_resolution_hints).slice(0, 6).map((item, i) => `
    <div class="signal-data-item"><div class="signal-data-num">${i + 1}</div><div class="signal-data-text">${s(item)}</div></div>`).join("")}
  </div>
</div>` : ""}

${sl(report.competitor_mentions).filter(item => {
  const name = typeof item === "string" ? item : (item.brand || item.name || "");
  return name && name !== "null" && name !== "undefined";
}).length ? `
<div class="signal-data-section">
  <div class="signal-data-label">Who they're comparing</div>
  <div class="competitor-grid">
    ${sl(report.competitor_mentions).filter(item => {
      const name = typeof item === "string" ? item : (item.brand || item.name || "");
      return name && name !== "null" && name !== "undefined";
    }).slice(0, 8).map(item => {
      const name = typeof item === "string" ? item : (item.brand || item.name || item);
      const count = typeof item === "object" && item.count ? ` · ${item.count}` : "";
      return `<div class="competitor-tag">${s(name)}${s(count)}</div>`;
    }).join("")}
  </div>
</div>` : ""}

<div class="verdict-section">
  ${signalProfile ? `<div class="profile-card" id="profileCard"><div class="profile-label">Who the signals point to</div><div class="profile-text">${s(signalProfile)}</div></div>` : ""}
  <div class="verdict-card" id="verdictCard"><div class="verdict-label">System verdict</div><div class="verdict-text">${s(verdictText)}</div></div>
</div>

<div class="footer">
  <div class="footer-text">
    Generated by Semantic Intent Engine · ${s(report.brand_name)} · ${s(generated)}<br>
    Signal analysis based on ${totalSignals} real customer signals across reviews, video comments, and search data.
  </div>
</div>

</div>

<script>
  function easeOut(t) { return 1 - Math.pow(1 - t, 3); }
  function countUp(el) {
    const target = parseInt(el.getAttribute('data-target'), 10);
    if (!target) { el.textContent = '0'; return; }
    const duration = 1200; const start = performance.now();
    function step(now) {
      const p = Math.min((now - start) / duration, 1);
      el.textContent = Math.round(easeOut(p) * target);
      if (p < 1) requestAnimationFrame(step); else el.textContent = target;
    }
    requestAnimationFrame(step);
  }
  function animateBars() {
    document.querySelectorAll('.dist-fill, .meter-bar, .source-bar-fill').forEach(el => {
      const w = el.getAttribute('data-width');
      if (w) setTimeout(() => { el.style.width = w + '%'; }, 200);
    });
  }
  function animateHeat() {
    document.querySelectorAll('.heat-cell').forEach(el => {
      const delay = parseInt(el.getAttribute('data-delay') || '0', 10);
      setTimeout(() => el.classList.add('visible'), 500 + delay);
    });
  }
  document.querySelectorAll('[data-counter]').forEach(countUp);
  animateBars();
  animateHeat();
  setTimeout(() => {
    const v = document.getElementById('verdictCard'); if (v) v.classList.add('visible');
    const p = document.getElementById('profileCard'); if (p) p.classList.add('visible');
  }, 100);
</script>
</body>
</html>`;
}

// A SIGNAL WITH NO STAR RATING BELONGS IN NO BAND.
//
// This computed `parseFloat(sig.rating) || 0` and then `else if (r <= 2)`.
// parseFloat(null) is NaN, NaN || 0 is 0, and 0 <= 2 — so every ratingless
// signal was plotted as a NEGATIVE review. Reddit sets rating: null explicitly
// (intentcluster-worker reviewSignals/redditCorpus.js:117) and YouTube never
// sets it at all; Trustpilot joined them for eight weeks between
// intentcluster-worker 2a2b818 and 92834f3.
//
// WHY THIS COPY MATTERED MORE THAN THE VIEWER'S. ntentify-viewer has
// reviewTrendIsReadable(), which already excluded ratingless signals when
// deciding whether to render — so it under-rendered rather than over-rendered.
// This file has no such gate. Its only gate is `totalBars < 30`, and
// ratingless signals INFLATE totalBars, so a Reddit-heavy brand could clear
// the gate on ratingless volume alone and draw a chart that was almost
// entirely artifact. And this one is a file you send: App.jsx imports
// generateExecutiveSummaryHTML, which calls this, and that is what the
// Download HTML / Download PDF buttons produce.
//
// The >= 1 floor matches ntentify-reports/index.html:1276, which has always
// had it. Written as an explicit guard rather than that file's
// `else if (r === 3)` ordering: identical for the integer ratings every bank
// stores, but it does not misfile a fractional 3.5 as negative.
//
// STILL UNFIXED, DELIBERATELY — two hand-synced copies of this function that
// nothing links to any more:
//   public/generators.js:3496   (loaded only by public/reports.html)
//   public/reports.html:1134    (hub links reports-v2.html, which points at
//                                viewer.ntentify.com; this page is reachable
//                                only by typing its URL)
// They now differ from this file by more than the `export` keywords they
// already differed by. ntentify-reports/index.html has never had the bug.
export function generateReviewTrendHTML(report) {
  const signals = report.review_signals || [];
  if (!signals.length) return '';

  // The one predicate the whole function turns on. A rating must be a real
  // number of at least 1 — parseFloat(null), parseFloat(undefined) and
  // parseFloat('') are all NaN, and no bank stores a 0-star review.
  const hasRating = (s) => { const r = parseFloat(s && s.rating); return Number.isFinite(r) && r >= 1; };

  const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

  function parseReviewDate(d) {
    if (!d) return null;
    const m = d.match(/on\s+(\w+)\s+(\d+),?\s+(\d{4})/i);
    if (m) { const p = new Date(`${m[1]} ${m[2]}, ${m[3]}`); if (!isNaN(p)) return p; }
    const p = new Date(d); return isNaN(p) ? null : p;
  }

  const now = new Date();
  const sixMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 6, 1);
  // The window is chosen from the population that will actually be DRAWN.
  // Counting ratingless signals here is the same defect as banding them: a
  // Reddit-heavy brand with old comments would flip the chart to a 12-month
  // window on the strength of signals that never appear in it.
  const olderThan6mo = signals.filter(s => {
    if (!s.date || !hasRating(s)) return false;
    const d = parseReviewDate(s.date);
    return d && d < sixMonthsAgo;
  }).length;
  const windowMonths = olderThan6mo >= 10 ? 12 : 3;
  const months = [];
  for (let i = windowMonths - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push({ key: `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`, label: MONTH_NAMES[d.getMonth()], pos: 0, neu: 0, neg: 0 });
  }
  const keyMap = {};
  months.forEach(m => { keyMap[m.key] = m; });

  signals.forEach(sig => {
    const d = parseReviewDate(sig.date);
    if (!d) return;
    const key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
    if (!keyMap[key]) return;
    if (!hasRating(sig)) return;              // ratingless — no band. See the header.
    const r = parseFloat(sig.rating);
    if (r >= 4) keyMap[key].pos++;
    else if (r <= 2) keyMap[key].neg++;
    else keyMap[key].neu++;
  });

  const totalBars = months.reduce((acc, m) => acc + m.pos + m.neu + m.neg, 0);
  if (totalBars < 30) return '';

  const posData = JSON.stringify(months.map(m => m.pos));
  const neuData = JSON.stringify(months.map(m => m.neu));
  const negData = JSON.stringify(months.map(m => m.neg));
  const labels = JSON.stringify(months.map(m => m.label));
  // The caption counts what is PLOTTED. It said "N of M signals date-stamped"
  // above a chart that draws only rated signals — a denominator for a
  // different population than the one on screen.
  //
  // The note underneath used to report the DATE share, which is now the wrong
  // constraint to warn about: bank rows almost always carry a date, and Reddit
  // comments in particular carry one while carrying no rating at all. So the
  // old note stayed silent in exactly the case that needed it. It now reports
  // the rating share, which is what actually decides how much of the corpus
  // reaches this chart.
  const ratedCount = signals.filter(hasRating).length;
  const ratedPct = signals.length > 0 ? Math.round((ratedCount / signals.length) * 100) : 0;
  const showNote = ratedCount < signals.length;

  return `
<div style="background:#111d33;border:1px solid #1e2f55;border-radius:8px;padding:24px;margin:0 var(--pad) 0;">
  <div style="font-family:var(--font-mono);font-size:11px;font-weight:600;letter-spacing:.08em;color:#a8c0e8;text-transform:uppercase;margin:0 0 4px;">Review signal trend</div>
  <div style="font-family:var(--font-mono);font-size:10px;color:#7a9cc8;letter-spacing:.06em;margin:0 0 8px;">Star rating volume over time — 4–5★ positive, 3★ neutral, 1–2★ negative</div>
  <div style="font-family:var(--font-mono);font-size:12px;color:#a8c0e8;margin:0 0 14px;">Monthly volume by sentiment — ${windowMonths === 12 ? 'last 12 months' : 'last 90 days'} (${totalBars} plotted &middot; ${ratedCount} of ${signals.length} signals carry a star rating)</div>
  <div style="display:flex;gap:20px;margin-bottom:12px;flex-wrap:wrap;">
    <span style="display:flex;align-items:center;gap:5px;font-family:var(--font-mono);font-size:11px;color:#a8c0e8;"><span style="width:8px;height:8px;border-radius:2px;background:#3b82f6;display:inline-block;"></span>Positive (4–5★)</span>
    <span style="display:flex;align-items:center;gap:5px;font-family:var(--font-mono);font-size:11px;color:#a8c0e8;"><span style="width:8px;height:8px;border-radius:2px;border:1px solid #4a6a99;display:inline-block;"></span>Neutral (3★)</span>
    <span style="display:flex;align-items:center;gap:5px;font-family:var(--font-mono);font-size:11px;color:#a8c0e8;"><span style="width:8px;height:8px;border-radius:2px;background:#c84b3a;display:inline-block;"></span>Negative (1–2★)</span>
  </div>
  <div style="position:relative;height:160px;">
    <canvas id="reviewTrendChart" role="img" aria-label="Line chart of monthly review volume by sentiment">Monthly review signal trend.</canvas>
  </div>
  ${showNote ? `<div style="font-family:var(--font-mono);font-size:11px;color:#4a6a99;text-align:center;margin-top:10px;font-style:italic;">${ratedPct}% of signals carry a star rating — community forums and video comments have none and are not charted</div>` : ''}
</div>
<script src="https://cdnjs.cloudflare.com/ajax/libs/Chart.js/4.4.1/chart.umd.js"><\/script>
<script>
(function() {
  if (typeof Chart === 'undefined') return;
  new Chart(document.getElementById('reviewTrendChart'), {
    type: 'line',
    data: {
      labels: ${labels},
      datasets: [
        { label: 'Positive', data: ${posData}, borderColor: '#3b82f6', backgroundColor: 'rgba(59,130,246,0.06)', borderWidth: 2, pointRadius: 3, pointBackgroundColor: '#3b82f6', tension: 0.35, fill: false },
        { label: 'Neutral',  data: ${neuData}, borderColor: '#a8c0e8', backgroundColor: 'rgba(168,192,232,0.04)', borderWidth: 1.5, pointRadius: 2, pointBackgroundColor: '#a8c0e8', tension: 0.35, fill: false, borderDash: [4,3] },
        { label: 'Negative', data: ${negData}, borderColor: '#c84b3a', backgroundColor: 'rgba(200,75,58,0.04)', borderWidth: 2, pointRadius: 3, pointBackgroundColor: '#c84b3a', tension: 0.35, fill: false }
      ]
    },
    options: {
      responsive: true, maintainAspectRatio: false,
      scales: {
        x: { grid: { color: 'rgba(30,47,85,0.8)' }, ticks: { color: '#a8c0e8', font: { size: 11 }, maxRotation: 0, autoSkip: false }, border: { color: '#1e2f55' } },
        y: { grid: { color: 'rgba(30,47,85,0.8)' }, ticks: { color: '#a8c0e8', font: { size: 11 } }, border: { color: '#1e2f55' } }
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: '#0d1526', borderColor: '#1e2f55', borderWidth: 1,
          titleColor: '#f0f4ff', bodyColor: '#a8c0e8',
          callbacks: { label: (item) => ' ' + item.dataset.label + ': ' + item.raw }
        }
      }
    }
  });
})();
<\/script>`;
}

export function generateMarketSignalTrendsHTML(report, uuid) {
  const s = (v) => String(v || "").replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;").replace(/"/g,"&quot;");
  const ns = (v) => s(normalizeText(String(v || "")));
  const sl = (v) => Array.isArray(v) ? v : [];
  const generated = report.generated_at ? new Date(report.generated_at).toLocaleString() : new Date().toLocaleString();
  const mst = report.market_signal_trends || null;

  if (!mst) {
    return `${generateWhiteLabelStyle(report)}${generateNavBar(report.brand_name, "trends", uuid, report.white_label)}
<div style="max-width:960px;margin:0 auto;padding:80px 48px;text-align:center;">
  <div style="font-family:'Bebas Neue',sans-serif;font-size:48px;letter-spacing:.04em;color:#4a6a99;margin-bottom:16px;">MARKET SIGNAL TRENDS</div>
  <div style="font-family:'DM Mono',monospace;font-size:13px;color:#4a6a99;line-height:1.7;">This report was not generated for this run.<br>Re-run the pipeline to generate Market Signal Trends data.</div>
</div>`;
  }

  const STATUS_COLORS = { accelerating: "#4ade80", stable: "#fbbf24", declining: "#f87171" };
  const statusColor = STATUS_COLORS[mst.brand_health_status] || "#60a5fa";
  const TREND_COLORS = { deteriorating: "#f87171", stable: "#fbbf24", improving: "#4ade80", insufficient_data: "#60a5fa" };
  const trendColor = TREND_COLORS[mst.signal_velocity?.trend] || "#60a5fa";

  const defectionRows = sl(mst.defection_map).map((d, i) => `
    <div style="background:var(--navy-raised);border:1px solid var(--navy-edge);padding:20px 24px;margin-bottom:12px;">
      <div style="display:grid;grid-template-columns:24px 1fr;gap:14px;align-items:start;">
        <div style="font-family:var(--font-disp);font-size:22px;color:var(--blue-light);line-height:1;opacity:.5;">${i+1}</div>
        <div>
          <div style="font-family:var(--font-mono);font-size:10px;letter-spacing:.16em;text-transform:uppercase;color:var(--blue-light);margin-bottom:6px;">TRIGGER</div>
          <div style="font-size:15px;font-weight:600;color:var(--text-primary);margin-bottom:12px;line-height:1.4;">${ns(d.trigger)}</div>
          <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:12px;">
            <div><div style="font-family:var(--font-mono);font-size:9px;letter-spacing:.14em;text-transform:uppercase;color:var(--text-faint);margin-bottom:4px;">BEHAVIOR</div><div style="font-size:13px;color:var(--text-secondary);line-height:1.5;">${ns(d.customer_behavior)}</div></div>
            <div><div style="font-family:var(--font-mono);font-size:9px;letter-spacing:.14em;text-transform:uppercase;color:var(--text-faint);margin-bottom:4px;">DESTINATION</div><div style="font-size:13px;color:#f87171;line-height:1.5;font-weight:600;">${ns(d.destination)}</div></div>
            <div><div style="font-family:var(--font-mono);font-size:9px;letter-spacing:.14em;text-transform:uppercase;color:var(--text-faint);margin-bottom:4px;">CAPTURING MESSAGE</div><div style="font-size:13px;color:var(--text-secondary);line-height:1.5;">${ns(d.capturing_message)}</div></div>
          </div>
        </div>
      </div>
    </div>`).join("");

  const competitorRows = sl(mst.competitor_capture).map(c => `
    <div style="background:var(--navy-raised);border:1px solid var(--navy-edge);padding:18px 22px;margin-bottom:10px;display:grid;grid-template-columns:auto 1fr auto;gap:16px;align-items:start;">
      <div style="font-family:var(--font-disp);font-size:18px;color:var(--blue-light);white-space:nowrap;">${s(c.brand)}</div>
      <div>
        <div style="font-size:14px;color:var(--text-primary);margin-bottom:6px;line-height:1.4;">${ns(c.message)}</div>
        <div style="font-size:12px;color:var(--text-muted);line-height:1.5;">${ns(c.why_winning)}</div>
      </div>
      ${c.longevity_days ? `<div style="font-family:var(--font-mono);font-size:11px;color:var(--text-faint);white-space:nowrap;text-align:right;">${c.longevity_days}d running</div>` : ""}
    </div>`).join("");

  const warningItems = sl(mst.early_warning_indicators).map((w, i) => `
    <div style="display:grid;grid-template-columns:28px 1fr;gap:12px;align-items:start;padding:14px 18px;background:rgba(251,191,36,0.06);border:1px solid rgba(251,191,36,0.2);margin-bottom:8px;">
      <div style="font-family:var(--font-disp);font-size:22px;color:#fbbf24;line-height:1;opacity:.7;">${i+1}</div>
      <div style="font-size:13px;color:var(--text-secondary);line-height:1.65;">${ns(w)}</div>
    </div>`).join("");

  const categoryStatus = mst.category_health?.status || "";
  const STATUS_LABELS = { brand_defection: "Brand Defection", category_abandonment: "Category Abandonment", mixed: "Mixed", healthy: "Healthy" };
  const STATUS_BG = { brand_defection: "rgba(248,113,113,0.1)", category_abandonment: "rgba(251,191,36,0.1)", mixed: "rgba(96,165,250,0.1)", healthy: "rgba(74,222,128,0.1)" };
  const STATUS_BORDER = { brand_defection: "rgba(248,113,113,0.3)", category_abandonment: "rgba(251,191,36,0.3)", mixed: "rgba(96,165,250,0.3)", healthy: "rgba(74,222,128,0.3)" };
  const STATUS_TEXT = { brand_defection: "#f87171", category_abandonment: "#fbbf24", mixed: "#60a5fa", healthy: "#4ade80" };

  return `${generateWhiteLabelStyle(report)}${generateNavBar(report.brand_name, "trends", report.white_label, uuid)}
<style>
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
:root{--navy-deep:#080f1c;--navy-base:#0d1526;--navy-raised:#111d33;--navy-surface:#162040;--navy-edge:#1e2f55;--navy-mid:#243568;--blue:#2563eb;--blue-bright:#3b82f6;--blue-light:#60a5fa;--text-primary:#f0f4ff;--text-secondary:#a8c0e8;--text-muted:#7a9cc8;--text-faint:#4a6a99;--font-disp:'Bebas Neue',sans-serif;--font-body:'Inter',sans-serif;--font-mono:'DM Mono',monospace;--pad:48px}
.wrap{max-width:960px;margin:0 auto;padding-bottom:80px;font-family:'Inter','Helvetica Neue',Arial,sans-serif;font-size:14px;line-height:1.6;}
.cover{background:var(--navy-deep);padding:52px var(--pad) 44px;border-bottom:1px solid var(--navy-edge);position:relative;overflow:hidden}
.cover::after{content:'';position:absolute;top:0;right:0;width:500px;height:100%;background:radial-gradient(ellipse at 80% 50%,rgba(37,99,235,0.1) 0%,transparent 70%);pointer-events:none}
.section{padding:40px var(--pad);border-bottom:1px solid var(--navy-edge);background:var(--navy-base)}
.section-label{font-family:var(--font-mono);font-size:11px;letter-spacing:.2em;text-transform:uppercase;color:var(--text-secondary);margin-bottom:20px;padding-bottom:10px;border-bottom:1px solid var(--navy-edge)}
.section-title{font-family:var(--font-disp);font-size:36px;letter-spacing:.02em;color:var(--text-primary);margin-bottom:20px;line-height:1}
.footer{padding:24px var(--pad);background:var(--navy-deep);border-top:1px solid var(--navy-edge)}
.footer-text{font-family:var(--font-mono);font-size:10px;color:var(--text-faint);letter-spacing:.1em;line-height:1.6}
</style>
<div class="wrap">
<div class="cover">
  <div style="font-family:var(--font-mono);font-size:10px;letter-spacing:.22em;text-transform:uppercase;color:var(--text-faint);margin-bottom:14px;position:relative;">Ntentify · Semantic Intent Engine · Market Signal Trends</div>
  <div style="font-family:var(--font-disp);font-size:68px;line-height:1;color:var(--text-primary);position:relative;margin-bottom:4px;">MARKET SIGNAL TRENDS</div>
  <div style="font-family:var(--font-disp);font-size:34px;letter-spacing:.02em;color:var(--blue-light);position:relative;margin-bottom:32px;">${s(report.brand_name)}</div>
  <div style="border-top:1px solid var(--navy-edge);padding-top:24px;display:flex;gap:32px;flex-wrap:nowrap;align-items:flex-start;">
    <div><div style="font-family:var(--font-mono);font-size:10px;letter-spacing:.14em;text-transform:uppercase;color:var(--text-faint);margin-bottom:6px;">Brand Health</div><div style="font-family:var(--font-disp);font-size:36px;letter-spacing:.04em;color:${statusColor};">${s((mst.brand_health_status||"").toUpperCase())}</div></div>
    <div><div style="font-family:var(--font-mono);font-size:10px;letter-spacing:.14em;text-transform:uppercase;color:var(--text-faint);margin-bottom:6px;">Signal Velocity</div><div style="font-family:var(--font-disp);font-size:36px;letter-spacing:.04em;color:${trendColor};">${s((mst.signal_velocity?.trend||"").toUpperCase())}</div></div>
    <div><div style="font-family:var(--font-mono);font-size:10px;letter-spacing:.14em;text-transform:uppercase;color:var(--text-faint);margin-bottom:6px;">Defection Triggers</div><div style="font-family:var(--font-disp);font-size:36px;color:var(--text-primary);">${sl(mst.defection_map).length}</div></div>
    <div><div style="font-family:var(--font-mono);font-size:10px;letter-spacing:.14em;text-transform:uppercase;color:var(--text-faint);margin-bottom:6px;">Competitors Capturing</div><div style="font-family:var(--font-disp);font-size:36px;color:var(--text-primary);">${sl(mst.competitor_capture).length}</div></div>
    <div><div style="font-family:var(--font-mono);font-size:10px;letter-spacing:.14em;text-transform:uppercase;color:var(--text-faint);margin-bottom:6px;">Early Warnings</div><div style="font-family:var(--font-disp);font-size:36px;color:#fbbf24;">${sl(mst.early_warning_indicators).length}</div></div>
  </div>
</div>

${mst.headline_statement ? `<div style="background:var(--blue);padding:28px var(--pad);"><div style="font-family:var(--font-mono);font-size:10px;letter-spacing:.2em;text-transform:uppercase;color:rgba(255,255,255,.7);margin-bottom:10px;">Primary Diagnosis</div><div style="font-size:20px;font-weight:600;color:#fff;line-height:1.45;max-width:780px;">${ns(mst.headline_statement)}</div></div>` : ""}

${mst.signal_velocity?.interpretation ? `<div class="section"><div class="section-label">Signal Velocity</div><div style="font-size:15px;color:var(--text-secondary);line-height:1.75;max-width:720px;">${ns(mst.signal_velocity.interpretation)}</div></div>` : ""}

${defectionRows ? `<div class="section"><div class="section-label">Defection Map</div><div class="section-title">Where Customers Go When They Leave</div>${defectionRows}</div>` : ""}

${competitorRows ? `<div class="section"><div class="section-label">Competitor Capture</div><div class="section-title">Who Is Winning Defecting Customers</div>${competitorRows}</div>` : ""}

${mst.category_health?.interpretation ? `<div class="section"><div class="section-label">Category Health</div><div style="display:inline-block;font-family:var(--font-mono);font-size:11px;letter-spacing:.12em;padding:5px 14px;background:${STATUS_BG[categoryStatus]||"rgba(96,165,250,0.1)"};color:${STATUS_TEXT[categoryStatus]||"#60a5fa"};border:1px solid ${STATUS_BORDER[categoryStatus]||"rgba(96,165,250,0.3)"};margin-bottom:16px;">${s(STATUS_LABELS[categoryStatus]||categoryStatus)}</div><div style="font-size:15px;color:var(--text-secondary);line-height:1.75;max-width:720px;">${ns(mst.category_health.interpretation)}</div>${sl(mst.category_health.abandonment_signals).length ? `<div style="margin-top:16px;display:flex;flex-wrap:wrap;gap:8px;">${sl(mst.category_health.abandonment_signals).map(sig=>`<div style="font-family:var(--font-mono);font-size:12px;color:var(--text-muted);padding:5px 12px;border:1px solid var(--navy-edge);background:var(--navy-raised);">"${ns(sig)}"</div>`).join("")}</div>` : ""}</div>` : ""}

${warningItems ? `<div class="section"><div class="section-label">Early Warning Indicators</div><div class="section-title">What to Watch</div>${warningItems}</div>` : ""}

${mst.verdict ? `<div style="padding:36px var(--pad);background:var(--navy-raised);border-top:2px solid var(--blue);border-bottom:1px solid var(--navy-edge);"><div style="font-family:var(--font-mono);font-size:10px;letter-spacing:.22em;text-transform:uppercase;color:var(--blue-light);margin-bottom:14px;">System Verdict</div><div style="font-size:15px;line-height:1.8;color:var(--text-secondary);max-width:760px;">${ns(mst.verdict)}</div></div>` : ""}

<div class="footer"><div class="footer-text">Generated by Semantic Intent Engine · ${s(report.brand_name)} · ${s(generated)}</div></div>
</div>`;
}